import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import "maplibre-gl/dist/maplibre-gl.css";
import { layers, namedFlavor } from "@protomaps/basemaps";
import { WeatherCogPool, type DecodedFrame } from "./weatherCogPool";
import { WeatherRasterLayer, type WeatherFrame } from "./weatherRasterLayer";
import { WEATHER_LAYERS, type LayerConfig } from "./weatherLayerConfig";
// Vite worker import (see Vite note in README)
// @ts-ignore
import WeatherCogWorker from "./weatherCogWorker.ts?worker";
import { WeatherWindStreamlines } from "./weatherWindStreamlines";
import { StormChanceUI, BrandCube, BrandWordmark } from "./StormChanceUI";
import { addWeatherCoastline, removeWeatherCoastline, COASTLINE_LAYER_ID, applyFeatureVisibility, bakeFeatureVisibility, type FeatureToggles } from "./weatherStyle";
import { applyWeatherLabelStyle, applyLabelScaleLive } from "./weatherLabels";
import { AttributionPill, useVisibleAlertSources } from "./AttributionPill";
import { useWakeEpoch } from "./useWakeEpoch";
import {
  usePersistedState, isBool, isString, isStringOrNull, isNumberInRange, isBoolRecord,
} from "./usePersistedState";
import {
  attachMetarPills, detachMetarPills, setMetarPillsEnabled,
  setMetarPillsMode, setMetarPillsVisible,
} from "./weatherMetarPills";
import {
  fetchTropicalData, upsertTropicalOverlay, removeTropicalOverlay,
  buildTropicalPopupHTML, TROPICAL_LYR, type TropicalData,
  fetchSpcOutlook, upsertSpcOverlay, removeSpcOverlay, type SpcProduct,
} from "./stormOverlays";

// Shared "is this a small/mobile viewport" hook. Matches the breakpoint used
// inside StormChanceUI (< 400px in either dimension) so the legend bar and the
// timeline pill agree on when to switch to their compact mobile sizing.
function useIsSmall() {
  const [isSmall, setIsSmall] = useState(
    () => typeof window !== "undefined" && (window.innerWidth < 400 || window.innerHeight < 400)
  );
  useEffect(() => {
    const check = () => setIsSmall(window.innerWidth < 400 || window.innerHeight < 400);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  return isSmall;
}

const PMTILES_URL = "https://pmtile.stormchance.org/20241104.pmtiles";
const COG_API = "https://api.stormchance.io";
const FLASK_API = "https://api.stormchance.io";

// ─── COG access token ────────────────────────────────────────────────────────
// The COG Worker requires a short-lived signed token (?t=...) on /manifest,
// /cog/*, and /lightning. We mint it from our own server-side Pages function
// (/api/cog-token), which holds the signing secret — it never reaches this
// bundle. Tokens are cached until ~30s before expiry, so we mint roughly once
// per few minutes rather than per request. A single in-flight mint is shared
// across concurrent callers.
let cogToken: { token: string; exp: number } | null = null;
let cogTokenInflight: Promise<string> | null = null;

async function getCogToken(): Promise<string> {
  const nowSec = Math.floor(Date.now() / 1000);
  if (cogToken && cogToken.exp - nowSec > 30) return cogToken.token;
  if (cogTokenInflight) return cogTokenInflight;

  cogTokenInflight = fetch("/api/cog-token", { cache: "no-store" })
    .then((r) => r.json())
    .then((d: { token: string; exp: number }) => {
      cogToken = d;
      return d.token;
    })
    .finally(() => {
      cogTokenInflight = null;
    });
  return cogTokenInflight;
}
// iOS / iPadOS detection for memory budgeting. iPadOS 13+ masquerades as
// macOS in the UA, but real Macs report maxTouchPoints 0 — the touch check
// catches iPads without also catching desktop Safari. WebKit on these devices
// hard-kills the web content process at ~1.5 GB resident (observed JetsamEvent
// at exactly 1536 MB), which is why their frame-cache budget must stay small.
const IS_IOS_FAMILY =
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const NWS_ALERTS_API = "https://api.weather.gov/alerts/active?status=actual";
// "All Weather Alerts" overlay source.
//
// HISTORY: This used to pull the bare CAP/JSON active-alerts feed
// (api.weather.gov/alerts/active). That feed has two fatal problems for a
// whole-country map: (1) it's paginated at 500 features/page and we only ever
// read the first page, so on busy days >500 alerts silently vanished; and
// (2) the majority of advisories/watches come back with geometry:null and only
// a UGC zone reference, so they can't be drawn without a separate zone→polygon
// join (the old NWS_ZONES_GEOJSON backfill).
//
// NOW: Esri Living Atlas "NWS_Watches_Warnings_v1" feature service. This is the
// SAME NOAA/NWS data as the old mapservices.weather.noaa.gov feed, on the same
// 5-min cadence (Esri parses the NWS RSS-CAP feeds every 5 minutes), but hosted
// on Esri's faster services9.arcgis.com infrastructure AND carrying a richer,
// flatter schema — crucially a `Summary` field with the actual alert narrative
// text, which the old ArcGIS feed lacked (US popups had to fall back to a bare
// "view full alert" link). Data is public domain (US federal work); attribution
// "NOAA / NWS / Esri" is courtesy. The service is multi-layer (alerts pre-split
// by severity into L8–L12); we query L6 = "Events_Ordered_by_Size_and_Severity",
// the single layer containing EVERY active event, so the fetch stays one request.
//
// L6 schema (per feature): Event, Severity, Summary, Link, Urgency, Certainty,
// Category, Updated, Start, End_, Uid. Dates are Esri Date (epoch ms). Same two
// payload optimizations as before: outFields scoped to what normalizeArcgisAlert
// reads (not *), and maxAllowableOffset (~2km) generalizes geometry server-side —
// invisible on translucent fills at the zooms the full set is viewed, and the US
// set is large (one polygon per zone per alert), so this meaningfully cuts
// transfer + tiling. NOTE: if L6's fields ever differ, only the outFields list
// and normalizeArcgisAlert need updating.
//
// NOT requested: `Affected` (alias "Areas Affected", up to 1024 chars). L6 emits
// one polygon PER ZONE per alert, so a single multi-county warning repeats that
// ~1KB area string across every one of its polygons — pure download/parse bloat
// for the bulk fetch, since the text is only read when a single popup opens.
// Leaving it out keeps the layer fetch lean; the popup's Areas Affected section
// just stays empty for US alerts (the "View full alert" link covers the detail).
const NWS_ARCGIS_ALERTS_API =
  "https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/NWS_Watches_Warnings_v1/FeatureServer/6/query"
  + "?where=1%3D1&outFields=Event,Severity,Summary,Link,Urgency,Certainty,Category,Updated,Start,End_"
  + "&returnGeometry=true&outSR=4326&maxAllowableOffset=0.02&f=geojson";
// Canada alerts via the Esri Canada / MSC ArcGIS feature service (Living Atlas).
// Same query shape as the US NWS ArcGIS source above: SR 4326, geometry on,
// GeoJSON out. Two payload optimizations vs the naive query:
//   • outFields scoped to the 9 fields normalizeEcccFeature reads, NOT * — the
//     service exposes 35 columns including Alrt_Des_F (a full DUPLICATE French
//     narrative) and several Shape_*/duplicate _<epoch> columns we never use.
//     Dropping them, especially the second long description, cuts the response
//     size substantially.
//   • maxAllowableOffset generalizes geometry server-side to ~0.01° (~1km),
//     which is finer than these fill polygons ever render on screen but strips
//     the dense vertices that bloat the payload and slow tiling. (Visually
//     identical for translucent zone fills; remove if you ever need crisp edges.)
// resultRecordCount is raised because hosted ArcGIS services cap records per
// request (commonly 1000–2000) and active alerts can span many forecast zones.
// NOTE: service name ends in "___Test" — the currently-published Living Atlas
// service. If a non-Test production sibling appears, swap the name here.
const ECCC_ALERTS_API =
  "https://services.arcgis.com/wjcPoefzjpzCgffS/arcgis/rest/services/Environment_Canada_Weather_Alerts___Test/FeatureServer/0/query"
  + "?where=1%3D1"
  + "&outFields=Alrt_Nam_E,Alert_Type,Urgency,Alrt_Des_E,Effective_Date,Expire_Date,Website_URL,NAME,PROVINCE_C"
  + "&returnGeometry=true&outSR=4326&maxAllowableOffset=0.01&f=geojson&resultRecordCount=4000";
const GLYPHS_URL = "https://pmtile.stormchance.org/fonts/{fontstack}/{range}.pbf";

// ─── Fixed-resolution full-extent decode ─────────────────────────────────
// DESIGN: We decode the ENTIRE radar extent ONCE at a single fixed resolution
// (each layer's configured `downsample`) and reuse those exact frames at every
// zoom. Zooming just scales the already-decoded raster on the GPU, which is
// free — so zoom never triggers a re-decode and the animation loop only ever
// loads once. Panning never matters either (the whole extent is present).
//
// We keep a trivial single "tier" so the cache-key plumbing below stays intact
// (frameCacheKey/tierToken), but it's constant: sameTier() is always true, so
// the cache key never changes with zoom and frames decoded on open stay cached
// for the life of the layer. The resolution is whatever the layer config picks
// (radar_mrms uses downsample:3 — see weatherLayerConfig.ts for the memory
// rationale; this comment previously claimed downsample:1, which was stale
// and misdirected an iOS memory-crash investigation).

/** A resolution descriptor. Collapsed to a single constant value — `tier` and
 * `mult` are fixed so zoom changes never alter the cache key or the decode
 * resolution. (Kept as an object so the existing cache-key helpers and refs
 * don't need to change shape.) */
export interface ResolutionTier {
  tier: number; // always 0 — single fixed resolution
  mult: number; // always 1 — decode at the layer's base downsample, unscaled
}

/** The one and only tier. Independent of zoom: we want a single fixed-res
 * decode reused everywhere. Returned for any map/zoom. */
const FIXED_TIER: ResolutionTier = { tier: 0, mult: 1 };
function computeResolutionTier(_map: any): ResolutionTier {
  return FIXED_TIER;
}

/** Per-layer full-extent downsample. With mult fixed at 1 this is just the
 * layer's configured base downsample (never below 1). */
function tierDownsample(tier: ResolutionTier | null, baseDownsample: number): number {
  const mult = tier ? tier.mult : 1;
  const ds = Math.max(1, Math.round(baseDownsample * mult));
  // iOS resident-memory relief at the source. Frame bytes scale with 1/ds^2,
  // so one extra step (3 -> 4) cuts each decoded frame to ~56% — which means
  // more frames fit both the per-layer and the global budget, less eviction,
  // and a lower peak during preload. Frames are drawn into a smoothed,
  // palette-mapped raster whose on-screen detail is dominated by the shader,
  // so the visual cost is small next to a process kill. Desktop is unchanged.
  return IS_IOS_FAMILY ? ds + 1 : ds;
}

/** Always true now — there is a single fixed tier, so a zoom never counts as
 * a tier change and never triggers a re-decode. */
function sameTier(a: ResolutionTier | null, b: ResolutionTier | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.tier === b.tier && a.mult === b.mult;
}

// Cache-key helpers. The token is constant (single tier), so a frame decoded
// on open is found again at every zoom — the key never changes.

/** Short, stable string identifying the resolution a frame was decoded at.
 * Constant now (single fixed tier), so it's a stable cache-key suffix. */
function tierToken(t: ResolutionTier | null): string {
  if (!t) return "full";
  return `t${t.tier}m${t.mult}`;
}

/** Compose the cache key for a frame: its keyframe key plus the tier it was
 * decoded at. Keeping the tier in the key lets each tier's full-extent decode
 * stay cached, so switching back to a previously-visited tier is instant and
 * we never have to clear the cache on a zoom. */
function frameCacheKey(kfKey: string, tier: ResolutionTier | null): string {
  return `${kfKey}::${tierToken(tier)}`;
}

/**
 * Smallest zoom at which the Web Mercator world (512 × 2^z px) is at least as
 * wide as the given container. Below this zoom MapLibre starts repeating the
 * world horizontally — which is fine for a basemap but breaks our weather
 * raster/streamlines (their data only covers one [-180, +180] copy).
 *
 * Math: world width at zoom z = 512 * 2^z (CSS px). Solve for the z that makes
 * that >= container width: z >= log2(width / 512). Floor for integer-ish, then
 * add a hair to avoid sub-pixel slivers.
 */
function computeMinZoom(container: HTMLElement): number {
  const w = container.clientWidth || window.innerWidth || 1024;
  // Tile size MapLibre uses internally is 512 px. Add a small epsilon (0.05)
  // because at exactly the threshold zoom you can still see a 1-2 px seam on
  // some DPRs. Clamp to [0, 6] — going below 0 has no meaning, and a floor of
  // 6 would be silly on phones where you genuinely need to zoom out.
  const z = Math.log2(w / 512) + 0.05;
  return Math.max(0, Math.min(6, z));
}

const WARNING_COLORS: Record<string, string> = {
  "Tornado Warning": "#ff0000",
  "Severe Thunderstorm Warning": "#ff6a00",
  "Flash Flood Warning": "#31c345",
};

// Color ramp for the "All Weather Alerts" overlay, keyed on the NWS
// `severity` property (Extreme | Severe | Moderate | Minor | Unknown). Because
// that overlay shows EVERY active alert — hundreds of event types, far more
// than WARNING_COLORS enumerates — coloring by the five severity buckets is
// the only scalable scheme. Order roughly red→green by descending severity.
const ALERT_SEVERITY_COLORS: Record<string, string> = {
  Extreme: "#d50000",
  Severe: "#ff6d00",
  Moderate: "#ffd600",
  Minor: "#00c853",
  Unknown: "#90a4ae",
};

// Custom hazard colors, keyed on the alert `event` name. Sourced from the
// 2025_Alerts color spec (the home-page warnings-box palette), so the map
// matches the site's own color system rather than weather.gov's. This table
// is the curated set of alerts the "All Weather Alerts" overlay shows: it
// drives BOTH the per-event color AND the layer's visibility filter (the
// filter is derived from these keys — see the alerts render effect), so any
// event NOT listed here is omitted from the map entirely rather than painted
// via a fallback. Four NWS-renamed products (Cold Weather Advisory, Extreme
// Heat Warning/Watch, Air Stagnation Advisory) are aliased to their closest
// equivalent's color so the live feed's current product names still resolve.
// ALERT_SEVERITY_COLORS below remains only as a popup-accent fallback.
const NWS_PRODTYPE_COLORS: Record<string, string> = {
  "Tornado Warning": "#ff0000",
  "Hurricane Force Wind Warning": "#dc143c",
  "Tsunami Warning": "#dc143c",
  "Extreme Wind Warning": "#dc143c",
  "Blizzard Warning": "#dc143c",
  "Ice Storm Warning": "#ff00ff",
  "Snow Squall Warning": "#ff00ff",
  "Volcano Warning": "#dc143c",
  "Severe Thunderstorm Warning": "#ff6a00",
  "High Wind Warning": "#ff6a00",
  "Flash Flood Warning": "#31c345",
  "Storm Surge Warning": "#dc143c",
  "Winter Storm Warning": "#ff00ff",
  "Lake Effect Snow Warning": "#ff00ff",
  "Heavy Freezing Spray Warning": "#ff00ff",
  "Wind Chill Warning": "#9f6bfd",
  "Extreme Cold Warning": "#9f6bfd",
  "Avalanche Warning": "#ff00ff",
  "Earthquake Warning": "#dc143c",
  "Ashfall Warning": "#dc143c",
  "Tornado Watch": "#dc143c",
  "Hurricane Warning": "#dc143c",
  "Typhoon Warning": "#dc143c",
  "Tsunami Watch": "#ff6a00",
  "Excessive Heat Warning": "#ff6a00",
  "Coastal Flood Warning": "#228b22",
  "Lakeshore Flood Warning": "#228b22",
  "Flood Warning": "#228b22",
  "Blizzard Watch": "#ff00ff",
  "Winter Weather Advisory": "#9f6bfd",
  "Wind Chill Advisory": "#9f6bfd",
  "Gale Warning": "#ffa500",
  "Dust Storm Warning": "#ffa500",
  "Severe Thunderstorm Watch": "#ffa500",
  "Special Marine Warning": "#ffa500",
  "Tropical Storm Warning": "#ff6a00",
  "Flash Flood Watch": "#2e8b57",
  "Flood Watch": "#2e8b57",
  "Winter Storm Watch": "#db7ff7",
  "Lake Effect Snow Watch": "#db7ff7",
  "Hard Freeze Watch": "#db7ff7",
  "Extreme Cold Watch": "#9f6bfd",
  "Freezing Fog Advisory": "#708090",
  "Hard Freeze Warning": "#9f6bfd",
  "Red Flag Warning": "#dc143c",
  "Storm Surge Watch": "#ff6a00",
  "Hurricane Watch": "#ff6a00",
  "Hurricane Force Wind Watch": "#ff6a00",
  "Typhoon Watch": "#ff6a00",
  "High Surf Warning": "#dc143c",
  "Blowing Dust Warning": "#ff6a00",
  "Hazardous Seas Warning": "#ff6a00",
  "Storm Warning": "#dc143c",
  "Excessive Heat Watch": "#ff6a00",
  "Avalanche Watch": "#ff6a00",
  "Heat Advisory": "#ff6a00",
  "Flood Advisory": "#2e8b57",
  "Coastal Flood Watch": "#2e8b57",
  "Lakeshore Flood Watch": "#2e8b57",
  "Lakeshore Flood Advisory": "#2e8b57",
  "Coastal Flood Advisory": "#2e8b57",
  "Freeze Watch": "#db7ff7",
  "Wind Chill Watch": "#db7ff7",
  "Freezing Spray Advisory": "#db7ff7",
  "Freeze Warning": "#9f6bfd",
  "Tropical Storm Watch": "#ff6a00",
  "Storm Watch": "#ff6a00",
  "High Surf Advisory": "#ffa500",
  "Dense Fog Advisory": "#708090",
  "Dense Smoke Advisory": "#708090",
  "Blowing Dust Advisory": "#ffa500",
  "Wind Advisory": "#ffa500",
  "Hazardous Seas Watch": "#ff6a00",
  "High Wind Watch": "#ff6a00",
  "Gale Watch": "#ff6a00",
  "Fire Weather Watch": "#ff6a00",
  "Air Quality Alert": "#708090",
  "Ashfall Advisory": "#708090",
  "Severe Weather Statement": "#ffa500",
  "Tsunami Advisory": "#ffa500",
  "Flash Flood Statement": "#2e8b57",
  "Flood Statement": "#2e8b57",
  "Hydrologic Advisory": "#2e8b57",
  "Coastal Flood Statement": "#2e8b57",
  "Lakeshore Flood Statement": "#2e8b57",
  "Frost Advisory": "#ffa500",
  "Small Craft Advisory": "#ffa500",
  "Dust Advisory": "#ffa500",
  "Lake Wind Advisory": "#ffa500",
  "Rip Current Statement": "#ffa500",
  "Beach Hazards Statement": "#ffa500",
  "Marine Weather Statement": "#ffa500",
  "Cold Weather Advisory": "#9f6bfd",
  "Extreme Heat Warning": "#ff6a00",
  "Extreme Heat Watch": "#ff6a00",
  "Air Stagnation Advisory": "#708090",
};

// ─── NWS Esri Living Atlas alert normalization ───────────────────────────
// The NWS_Watches_Warnings_v1 service (L6) returns GeoJSON with geometry
// attached and a flat CAP-style schema, so normalization is mostly a rename
// onto the property shape the popup/renderer expect (source-agnostic, exactly
// like normalizeEcccFeature for Canada). Big wins vs the old watch_warn_adv
// feed: `Severity`/`Urgency` arrive as direct CAP strings (no VTEC-code
// synthesis), and `Summary` carries the actual alert narrative text — so the
// popup's body now populates for US alerts instead of only offering a link.
//
// L6 fields: Event, Severity, Summary, Link, Urgency, Certainty, Category,
//   Updated, Start, End_ (dates are Esri Date = epoch ms; reuse epochMsToIso).
function normalizeArcgisAlert(f: any): any {
  const p = f.properties ?? {};
  // Severity arrives capitalized (Extreme/Severe/Moderate/Minor) and already
  // matches ALERT_SEVERITY_COLORS keys; title-case defensively in case a value
  // ever comes lowercased, so the color match + badge still resolve.
  const sevRaw = (p.Severity ?? "").toString();
  const severity = sevRaw ? sevRaw.charAt(0).toUpperCase() + sevRaw.slice(1).toLowerCase() : "Unknown";
  const startIso = epochMsToIso(p.Start);
  const endIso = epochMsToIso(p.End_);
  const updatedIso = epochMsToIso(p.Updated);
  return {
    ...f,
    // SANITIZE but do NOT simplify here. A single degenerate vertex (Infinity /
    // NaN / a 1e30 sentinel) in any one feature poisons its MapLibre tile and
    // discards every alert sharing it — sanitizeGeometry drops only genuinely-
    // invalid vertices/rings and protects the tiler. (Geometry is already
    // server-generalized via maxAllowableOffset in the query, so no client-side
    // simplify is needed for size.)
    geometry: f.geometry ? sanitizeGeometry(f.geometry) : null,
    properties: {
      // Preserve raw attributes for debugging / future use.
      ...p,
      // NWS-compatible fields the renderer + popup read:
      event: p.Event || "Weather Alert",
      severity,
      urgency: p.Urgency || "Unknown",
      certainty: p.Certainty || "Unknown",
      sent: updatedIso || startIso,
      effective: startIso || updatedIso,
      expires: endIso,
      ends: endIso,
      // Actual narrative text now available — populates the popup body. Note
      // the Summary field is capped at 512 chars by the service, so long alerts
      // are truncated here; the popup keeps a "View full alert" link to the
      // complete CAP product for anything cut off. `instruction` stays null —
      // the preparedness/protective-action text is parsed out of Summary in the
      // popup builder when present.
      description: p.Summary || null,
      instruction: null,
      // areaDesc intentionally null: the L6 `Affected` field is omitted from the
      // bulk query to avoid per-zone payload bloat (see NWS_ARCGIS_ALERTS_API).
      // The popup's Areas Affected section is hidden when this is empty.
      areaDesc: null,
      // Link to the official CAP product for the "View full alert" affordance.
      url: p.Link || null,
      _source: "nws",
    },
  };
}


// ─── Canada (Esri/MSC ArcGIS) alert normalization ───────────────────────
// The Esri Canada feature service returns GeoJSON with geometry included and
// uses its own field names (Alrt_Nam_E, Alert_Type, Urgency, Alrt_Des_E,
// Effective_Date/Expire_Date as epoch-ms, NAME/PROVINCE_C for the zone). This
// maps them onto the NWS property shape so the severity colour ramp and popup
// builder work unchanged — riding on the same proven path as the US ArcGIS
// source rather than the old OGC pipeline.

const ECCC_COLOUR_TO_SEVERITY: Record<string, string> = {
  red: "Extreme",
  orange: "Severe",
  yellow: "Moderate",
  grey: "Minor",
  gray: "Minor",
};

// Fallback only — the service provides a real CAP `Urgency` field we prefer.
const ECCC_TYPE_TO_URGENCY: Record<string, string> = {
  warning: "Immediate",
  advisory: "Expected",
  watch: "Future",
  statement: "Past",
};

// The risk colour isn't its own field — it's embedded at the top of the
// description as "| Alert Risk Colour Code: Yellow |". Pull it out so it can
// drive the severity colour ramp.
function ecccRiskColour(desc: string | null | undefined): string | null {
  if (!desc) return null;
  const m = desc.match(/Alert Risk Colour Code:\s*([A-Za-z]+)/i);
  return m ? m[1].toLowerCase() : null;
}

// Both the US (Esri Living Atlas) and Canada (Esri/MSC) feeds return dates as
// epoch milliseconds. fmtTime in the popup only reshapes strict ISO strings, so
// convert; anything invalid → null (omitted).
function epochMsToIso(ms: any): string | null {
  if (typeof ms !== "number" || !Number.isFinite(ms)) return null;
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function normalizeEcccFeature(f: any): any {
  const p = f.properties ?? {};
  const colour = ecccRiskColour(p.Alrt_Des_E);
  const effIso = epochMsToIso(p.Effective_Date);
  const expIso = epochMsToIso(p.Expire_Date);
  return {
    ...f,
    // ArcGIS GeoJSON is standard [lng,lat] like the US source. sanitizeGeometry
    // still runs (drops any degenerate vertex that would poison a MapLibre tile);
    // simplifyGeometry only decimates rings above MAX_RING_POINTS and leaves
    // everything else byte-for-byte, so normal zones pass through untouched.
    geometry: f.geometry ? simplifyGeometry(sanitizeGeometry(f.geometry)) : null,
    properties: {
      // Preserve originals for debugging
      ...p,
      // NWS-compatible fields used by the renderer + popup
      event: p.Alrt_Nam_E ?? "Weather Alert",
      severity: (colour && ECCC_COLOUR_TO_SEVERITY[colour]) ?? "Unknown",
      // Prefer the CAP Urgency field; fall back to mapping Alert_Type.
      urgency: p.Urgency ?? ECCC_TYPE_TO_URGENCY[p.Alert_Type?.toLowerCase()] ?? "Unknown",
      certainty: "Unknown", // not provided by this service
      sent: effIso,
      effective: effIso,
      expires: expIso,
      ends: expIso,
      description: p.Alrt_Des_E ?? null,
      instruction: null,  // instructions are embedded in Alrt_Des_E
      areaDesc: [p.NAME, p.PROVINCE_C].filter(Boolean).join(", "),
      url: p.Website_URL || null,
      // Flag so the popup can distinguish source if needed
      _source: "eccc",
    },
  };
}

// Strip coordinates MapLibre can't tile. NOAA alert polygons occasionally
// carry a degenerate vertex — a sentinel like 1e30, an Infinity, or a NaN —
// and when MapLibre's vector tiler hits one it throws "Given varint doesn't
// fit into 10 bytes" and DISCARDS THE ENTIRE TILE, silently erasing a whole
// geographic swath of otherwise-valid alerts. (ArcGIS's own renderer tolerates
// these; MapLibre does not.) This is the root cause of regions vanishing in
// chunks. We defensively drop any vertex that isn't a finite lng/lat in range,
// drop rings that fall below the 4 points a closed ring needs, and return null
// if nothing valid remains so the feature simply doesn't paint instead of
// poisoning its tile.
function sanitizeGeometry(geom: any): any | null {
  if (!geom) return null;
  const validPt = (p: any): boolean =>
    Array.isArray(p) && p.length >= 2 &&
    Number.isFinite(p[0]) && Number.isFinite(p[1]) &&
    p[0] >= -180 && p[0] <= 180 && p[1] >= -90 && p[1] <= 90;
  const cleanRing = (ring: any): number[][] | null => {
    if (!Array.isArray(ring)) return null;
    const out = ring.filter(validPt).map((p: number[]) => [p[0], p[1]]);
    if (out.length < 3) return null; // not even a triangle's worth of points
    // Guarantee closure. Filtering a bad vertex (or an upstream simplify pass
    // that deduped an endpoint) can leave first != last; an unclosed ring makes
    // MapLibre reject the whole feature/tile. Re-close explicitly so this is the
    // final word on validity regardless of what ran before us.
    const first = out[0], last = out[out.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) out.push([first[0], first[1]]);
    // A closed polygon ring needs ≥4 positions (first == last). Below that it's
    // collapsed; drop it entirely.
    return out.length >= 4 ? out : null;
  };
  const cleanPolygon = (poly: any): number[][][] | null => {
    if (!Array.isArray(poly)) return null;
    const rings = poly.map(cleanRing).filter(Boolean) as number[][][];
    // Need at least an outer ring.
    return rings.length > 0 ? rings : null;
  };
  if (geom.type === "Polygon") {
    const rings = cleanPolygon(geom.coordinates);
    return rings ? { type: "Polygon", coordinates: rings } : null;
  }
  if (geom.type === "MultiPolygon") {
    const polys = (geom.coordinates ?? [])
      .map(cleanPolygon)
      .filter(Boolean) as number[][][][];
    return polys.length > 0 ? { type: "MultiPolygon", coordinates: polys } : null;
  }
  // Points/lines aren't used by the alert layers; pass through only if their
  // coordinates are finite, else drop.
  return geom;
}

// Decimate ONLY rings that would exceed MapLibre's WebGL index limit; leave
// everything else byte-for-byte untouched. The previous implementation rounded
// ALL coordinates to 3 decimals, which collapsed small alert zones to a single
// point and got them dropped — erasing whole regions. This version:
//   • does nothing to a ring already under the threshold (so small zones are
//     never harmed — the actual fix for the vanishing-Canada bug), and
//   • when a ring genuinely is too large, decimates by keeping every Nth point
//     (uniform stride) rather than rounding coordinates, preserving the polygon
//     shape and never collapsing it. First/last points are always kept so the
//     ring stays closed.
// MapLibre's hard limit is 65535 indices per segment; we target well under that
// to leave headroom for triangulation.
const MAX_RING_POINTS = 8000;
function simplifyGeometry(geom: any): any {
  if (!geom) return geom;
  const decimateRing = (ring: number[][]): number[][] => {
    if (!Array.isArray(ring) || ring.length <= MAX_RING_POINTS) return ring; // untouched
    const stride = Math.ceil(ring.length / MAX_RING_POINTS);
    const out: number[][] = [];
    for (let i = 0; i < ring.length; i += stride) out.push(ring[i]);
    // Guarantee closure: keep the original last point and close to first.
    const last = ring[ring.length - 1];
    if (out[out.length - 1] !== last) out.push(last);
    const f = out[0], l = out[out.length - 1];
    if (f[0] !== l[0] || f[1] !== l[1]) out.push([f[0], f[1]]);
    return out;
  };
  if (geom.type === "Polygon") {
    return { type: "Polygon", coordinates: geom.coordinates.map(decimateRing) };
  }
  if (geom.type === "MultiPolygon") {
    return {
      type: "MultiPolygon",
      coordinates: geom.coordinates.map((poly: number[][][]) => poly.map(decimateRing)),
    };
  }
  return geom; // Point / other — pass through
}


// Inject CSS once at module load to theme the MapLibre popup chrome for our
// NWS alert popups. Two color variants — `.nws-dark` and `.nws-light` —
// match the StormChance theme tokens so the popup follows the basemap's
// day/night appearance. Idempotent: HMR-safe re-injection guard.
// Load Roboto for all UI chrome text (panels, timeline, legend, settings).
// Idempotent: guard against duplicate injection on HMR. NOTE: this styles the
// React UI only — the MAP labels (city/place names) are rendered by MapLibre
// from the glyph server, which serves Noto Sans; changing those would require
// Roboto glyph PBFs on the tile server.
if (typeof document !== "undefined" && !document.getElementById("stormchance-roboto-font")) {
  const preconnect1 = document.createElement("link");
  preconnect1.rel = "preconnect";
  preconnect1.href = "https://fonts.googleapis.com";
  const preconnect2 = document.createElement("link");
  preconnect2.rel = "preconnect";
  preconnect2.href = "https://fonts.gstatic.com";
  preconnect2.crossOrigin = "anonymous";
  const link = document.createElement("link");
  link.id = "stormchance-roboto-font";
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Anton&family=Roboto:wght@400;500;700&display=swap";
  document.head.appendChild(preconnect1);
  document.head.appendChild(preconnect2);
  document.head.appendChild(link);
}

if (typeof document !== "undefined" && !document.getElementById("nws-alert-popup-css")) {
  const style = document.createElement("style");
  style.id = "nws-alert-popup-css";
  style.textContent = `
    .maplibregl-popup.nws-alert-popup {
      /* Hard cap for the container. min() keeps a gutter on narrow phones so
         the card can never touch (or run off) the screen edge. */
      max-width: min(360px, calc(100vw - 20px)) !important;
    }
    .maplibregl-popup.nws-alert-popup .maplibregl-popup-content {
      padding: 0;
      border-radius: 12px;
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      overflow: hidden;
      /* Cap the popup to the viewport so tall alerts (long descriptions +
         Impact + What to Do) never run off-screen. The inner wrapper
         (.nws-alert-body) handles the actual scrolling. */
      max-height: 85vh;
      display: flex;
      flex-direction: column;
    }
    /* Inner scroll container: the popup chrome stays fixed, this scrolls. */
    .nws-alert-body {
      overflow-y: auto;
      overscroll-behavior: contain;
      scrollbar-width: thin;
    }
    .nws-alert-popup.nws-dark .nws-alert-body { scrollbar-color: rgba(255,255,255,0.18) transparent; }
    .nws-alert-popup.nws-light .nws-alert-body { scrollbar-color: rgba(0,0,0,0.2) transparent; }
    .nws-alert-body::-webkit-scrollbar { width: 6px; }
    .nws-alert-body::-webkit-scrollbar-track { background: transparent; }
    .nws-alert-popup.nws-dark .nws-alert-body::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.18); border-radius: 3px; }
    .nws-alert-popup.nws-light .nws-alert-body::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.2); border-radius: 3px; }
    /* Dark theme */
    .maplibregl-popup.nws-alert-popup.nws-dark .maplibregl-popup-content {
      background: rgba(10, 14, 22, 0.92);
      border: 1px solid rgba(94,181,223,0.15);
      box-shadow: 0 12px 40px rgba(0,0,0,0.6), 0 2px 12px rgba(0,0,0,0.4);
      color: #e8ecf2;
    }
    .maplibregl-popup.nws-alert-popup.nws-dark .maplibregl-popup-tip {
      border-top-color: rgba(10,14,22,0.92) !important;
      border-bottom-color: rgba(10,14,22,0.92) !important;
      border-left-color: rgba(10,14,22,0.92) !important;
      border-right-color: rgba(10,14,22,0.92) !important;
    }
    .maplibregl-popup.nws-alert-popup.nws-dark .maplibregl-popup-close-button { color: #7d8a9a; }
    .maplibregl-popup.nws-alert-popup.nws-dark .maplibregl-popup-close-button:hover {
      color: #e8ecf2; background: rgba(255,255,255,0.06);
    }
    /* Light theme */
    .maplibregl-popup.nws-alert-popup.nws-light .maplibregl-popup-content {
      background: rgba(255,255,255,0.96);
      border: 1px solid rgba(46,139,190,0.2);
      box-shadow: 0 12px 40px rgba(0,30,60,0.12), 0 2px 12px rgba(0,30,60,0.08);
      color: #1a2533;
    }
    .maplibregl-popup.nws-alert-popup.nws-light .maplibregl-popup-tip {
      border-top-color: rgba(255,255,255,0.96) !important;
      border-bottom-color: rgba(255,255,255,0.96) !important;
      border-left-color: rgba(255,255,255,0.96) !important;
      border-right-color: rgba(255,255,255,0.96) !important;
    }
    .maplibregl-popup.nws-alert-popup.nws-light .maplibregl-popup-close-button { color: #506070; }
    .maplibregl-popup.nws-alert-popup.nws-light .maplibregl-popup-close-button:hover {
      color: #1a2533; background: rgba(0,0,0,0.06);
    }
    /* Close button shared chrome */
    .maplibregl-popup.nws-alert-popup .maplibregl-popup-close-button {
      font-size: 22px; width: 28px; height: 28px; padding: 0;
      right: 8px; top: 8px; background: transparent; border: none;
      border-radius: 6px; line-height: 1; transition: all 0.15s ease;
    }
    /* Scrollable body region — fixed max-height with custom scrollbar so the
       popup itself doesn't grow indefinitely on long alert narratives. */
    .nws-alert-scroll {
      max-height: 160px;
      overflow-y: auto;
      margin-top: 6px;
      padding-right: 6px;
      scrollbar-width: thin;
    }
    .nws-alert-popup.nws-dark .nws-alert-scroll {
      scrollbar-color: rgba(255,255,255,0.18) transparent;
    }
    .nws-alert-popup.nws-light .nws-alert-scroll {
      scrollbar-color: rgba(0,0,0,0.2) transparent;
    }
    .nws-alert-scroll::-webkit-scrollbar { width: 6px; }
    .nws-alert-scroll::-webkit-scrollbar-track { background: transparent; }
    .nws-alert-popup.nws-dark .nws-alert-scroll::-webkit-scrollbar-thumb {
      background: rgba(255,255,255,0.18); border-radius: 3px;
    }
    .nws-alert-popup.nws-light .nws-alert-scroll::-webkit-scrollbar-thumb {
      background: rgba(0,0,0,0.2); border-radius: 3px;
    }

    /* ── Viewport scaling ────────────────────────────────────────
       The card's HTML bakes every dimension in as inline px (360px body, 18px
       padding, 24px Anton headline, badge pills), so no stylesheet rule can
       reach them individually. The whole card is scaled uniformly instead.

       --nws-popup-scale is set per-popup in JS (see nwsPopupScale()), NOT by a
       media query: a fixed breakpoint like "under 400px" misses the common
       Android phone that reports a 412px viewport, which is exactly the case
       that looked oversized. The JS version is continuous — the narrower the
       map, the smaller the card — with no cliff edge.

       'zoom' rather than 'transform: scale()' because zoom participates in
       LAYOUT: the content box genuinely gets smaller, so MapLibre's anchor and
       tip positioning still line up, and the container shrink-wraps it. A
       transform would shrink the pixels while leaving a full-size layout box
       behind, floating the card off its tip. */
    .maplibregl-popup.nws-alert-popup .maplibregl-popup-content {
      zoom: var(--nws-popup-scale, 1);
      /* Height cap. --nws-popup-max-h is set in JS from the MAP container's
         height, not the viewport: the map sits below the app header, so a vh
         value let a tall card run up under the header and get clipped. The
         85vh fallback only applies if JS hasn't set the var yet. Divided by
         the scale because lengths inside a zoomed box are multiplied by it. */
      max-height: calc(var(--nws-popup-max-h, 85vh) / var(--nws-popup-scale, 1));
    }
    /* Fallback for engines without 'zoom'. Layout size stays full there, so
       pin transform-origin to the corner MapLibre anchored the popup by —
       otherwise the shrunken card drifts away from its tip. Only applied when
       JS actually scaled the popup (it adds .nws-popup-scaled), so a 1:1
       desktop popup never gets a needless transform. */
    @supports not (zoom: 1) {
      .maplibregl-popup.nws-alert-popup.nws-popup-scaled .maplibregl-popup-content {
        transform: scale(var(--nws-popup-scale, 1));
        transform-origin: center center;
      }
      .nws-alert-popup.nws-popup-scaled.maplibregl-popup-anchor-top .maplibregl-popup-content { transform-origin: top center; }
      .nws-alert-popup.nws-popup-scaled.maplibregl-popup-anchor-bottom .maplibregl-popup-content { transform-origin: bottom center; }
      .nws-alert-popup.nws-popup-scaled.maplibregl-popup-anchor-left .maplibregl-popup-content { transform-origin: left center; }
      .nws-alert-popup.nws-popup-scaled.maplibregl-popup-anchor-right .maplibregl-popup-content { transform-origin: right center; }
      .nws-alert-popup.nws-popup-scaled.maplibregl-popup-anchor-top-left .maplibregl-popup-content { transform-origin: top left; }
      .nws-alert-popup.nws-popup-scaled.maplibregl-popup-anchor-top-right .maplibregl-popup-content { transform-origin: top right; }
      .nws-alert-popup.nws-popup-scaled.maplibregl-popup-anchor-bottom-left .maplibregl-popup-content { transform-origin: bottom left; }
      .nws-alert-popup.nws-popup-scaled.maplibregl-popup-anchor-bottom-right .maplibregl-popup-content { transform-origin: bottom right; }
    }
  `;
  document.head.appendChild(style);
}

// ─── Alert-popup sizing ─────────────────────────────────────────────
// The alert card is authored at NWS_POPUP_DESIGN_WIDTH and scaled down to fit
// narrower maps (the scale feeds the CSS 'zoom' rule above). Two knobs:
//
//   NWS_POPUP_FULL_WIDTH_AT — map width at which the card renders 1:1. Below
//     this it shrinks proportionally, so a 412px phone lands around 0.66 and a
//     360px phone around 0.58. LOWER this number to make the card BIGGER on
//     phones; raise it to shrink further. Careful past this point: at 0.66 the
//     card's 13px body text renders at ~8.6px and its 10px section labels at
//     ~6.6px, which is about as small as safety text should get.
//   NWS_POPUP_MIN_SCALE — floor, so the text can never shrink to nothing on a
//     very small or split-screen window. Raise it if small phones read too fine.
const NWS_POPUP_DESIGN_WIDTH = 360;
const NWS_POPUP_FULL_WIDTH_AT = 620;
const NWS_POPUP_MIN_SCALE = 0.55;
// Tallest the card may render, as a fraction of the MAP container's height.
// Anything past this scrolls inside the card's existing scroll boxes rather
// than pushing the card off the top or bottom of the map.
//
// Target: the card covers under a third of the map. Footprint is width ×
// height, so BOTH knobs move it — but height is the cheap one. Trimming height
// only costs a bit more scrolling, while trimming width shrinks the type, and
// the type is already near its readable floor (see NWS_POPUP_FULL_WIDTH_AT).
// Hence most of the reduction lives here: on a 412px phone the card ends up
// ~58% of the width × 48% of the height ≈ 28% of the map.
const NWS_POPUP_MAX_HEIGHT_FRAC = 0.48;

/** Scale factor for an alert popup on a map of the given CSS-pixel width. */
function nwsPopupScale(mapWidth: number): number {
  if (!mapWidth || !isFinite(mapWidth)) return 1;
  const raw = mapWidth / NWS_POPUP_FULL_WIDTH_AT;
  const clamped = Math.max(NWS_POPUP_MIN_SCALE, Math.min(1, raw));
  // Round to 2dp — keeps the inline style tidy and avoids sub-pixel jitter.
  return Math.round(clamped * 100) / 100;
}

/**
 * Pick the popup anchor from the click position AND the card's on-screen width.
 *
 * MapLibre's anchor names the popup corner that touches the point, so a "*-left"
 * anchor draws the card to the RIGHT of the click. The old version split purely
 * on the horizontal midpoint, which clipped a full-width card whenever the click
 * landed just left of center. This checks whether the card actually fits on the
 * chosen side and flips when it doesn't, falling back to the roomier side.
 */
function pickPopupAnchor(w: number, h: number, x: number, y: number, cardWidth: number): string {
  const GUTTER = 12; // matches the popup's offset
  const fitsRightOfClick = x + cardWidth + GUTTER <= w;
  const fitsLeftOfClick = x - cardWidth - GUTTER >= 0;
  const horiz = fitsRightOfClick ? "left"
    : fitsLeftOfClick ? "right"
    : x < w / 2 ? "left" : "right";
  // Vertical band is unchanged: open downward from the top third, upward from
  // the bottom third, side-anchored through the middle.
  if (y < h * 0.33) return `top-${horiz}`;
  if (y > h * 0.66) return `bottom-${horiz}`;
  return horiz;
}



// ─── Style builder ──────────────────────────────────────────────────────────
// Approach: leave water polygons completely alone (oceans, lakes, ponds all
// render normally — that's the Protomaps default and it doesn't break anything).
// Only hide LAKE LABELS until the user zooms in close enough that lake names
// are useful (z >= 10). The unlabeled lake polygons remain as quiet gray
// fills that don't draw the eye, which solves the original "too noisy"
// complaint without trying to filter polygons.

function buildDarkStyle(flavor: string, labelScale: number = 1, featureToggles?: FeatureToggles) {
  if (!flavor) flavor = "dark";
  // Pull the default Protomaps layer list, then post-process two things:
  //  1) Bump minzoom on the lake-label layer so small lake names don't
  //     spam the map at low zoom.
  //  2) Override text-color/halo on every text-bearing symbol layer for
  //     readability over the weather raster (light-gray text + dark halo
  //     against bg = labels recede instead of fighting bright colors).
  const baseLayers = layers("protomaps", namedFlavor(flavor), { lang: "en" }) as any[];

  const styledLayers = baseLayers.map((lyr) => {
    // Hide lake name labels until zoomed in (street/town level).
    // Tune the 10 to taste — bump higher to delay them more, lower to show sooner.
    if (lyr.id === "water_label_lakes") {
      return { ...lyr, minzoom: 10 };
    }
    if (lyr.type !== "symbol") return lyr;
    // Place labels handled by applyWeatherLabelStyle below — skip here.
    if (lyr.id.startsWith("places_")) return lyr;
    // Skip icon-only symbol layers (no text-field) — halo on a no-text layer
    // can render oddly on some MapLibre versions.
    const hasText = lyr.layout && lyr.layout["text-field"] !== undefined;
    if (!hasText) return lyr;
    return {
      ...lyr,
      paint: {
        ...(lyr.paint ?? {}),
        // Muted light gray reads on dark + recedes on bright weather colors.
        // Dark halo (matching app bg #0a0c10) "punches out" the text against
        // the raster without drawing focus the way a white halo would.
        "text-color": "#b8bcc4",
        "text-halo-color": "#0a0c10",
        "text-halo-width": 1.2,
        "text-halo-blur": 0.4,
      },
    };
  });

  // TV-style place labels (uppercase/bold/white+halo for cities, etc.),
  // sized by the user's label-size setting.
  const labeledLayers = applyWeatherLabelStyle(styledLayers, labelScale);

  // Bake the user's road/highway/landuse/building show-hide choices so a
  // basemap swap returns with them already applied (no flicker of hidden
  // layers). When no toggles are passed (initial build), everything shows.
  const finalLayers = featureToggles
    ? bakeFeatureVisibility(labeledLayers, featureToggles)
    : labeledLayers;

  return {
    version: 8,
    glyphs: GLYPHS_URL,
    sprite: "https://pmtile.stormchance.org/sprites/v4/" + flavor,
    sources: {
      protomaps: { type: "vector", url: "pmtiles://" + PMTILES_URL,
        attribution: '© <a href="https://openstreetmap.org">OpenStreetMap</a> · <a href="https://maplibre.org">MapLibre</a>' },
    },
    layers: finalLayers,
  } as any;
}

/**
 * Pick a basemap layer ID to use as the `beforeId` argument for weather
 * `addLayer` calls. Weather should slot in *above* water/landuse but *below*
 * boundaries, roads, and labels — so country borders and city names stay
 * visible against the colored speed field.
 *
 * Strategy: walk the actual layers in the live style and return the ID of the
 * first one that's either a `symbol` layer (labels) or has "boundaries" in
 * its ID (admin lines). MapLibre styles paint bottom-up, so inserting before
 * the first such layer keeps boundaries + labels on top of weather.
 *
 * Falls back to specific Protomaps layer IDs in priority order if the
 * heuristic finds nothing (e.g. unusual style with no symbol layers loaded
 * yet). Final fallback is `undefined`, which puts weather on top of
 * everything — degraded but not broken.
 */
function pickWeatherBeforeId(map: any): string | undefined {
  const style = map.getStyle?.();
  const layers = style?.layers ?? [];
  // Prefer the first boundary/label layer found.
  for (const lyr of layers) {
    const id: string = lyr.id ?? "";
    if (lyr.type === "symbol") return id;       // any text/icon labels
    if (id.includes("boundaries")) return id;   // admin borders
    if (id.includes("places")) return id;       // place labels (variant)
  }
  // Heuristic missed — try named fallbacks in order. Prefer ones that keep
  // weather above water (so wind data shows over oceans).
  for (const id of ["roads_pier", "roads_minor", "roads", "water"]) {
    if (map.getLayer && map.getLayer(id)) return id;
  }
  return undefined;
}

// COASTLINE_LAYER_ID and the coastline layer now come from ./weatherStyle
// (addWeatherCoastline / removeWeatherCoastline) � see imports at top.

// ─── Feature flags ──────────────────────────────────────────────────────────
// Whether to include nowcast (forecast) radar frames past "now" in the
// scrubber. Nowcast is gated as a premium feature — when entitlements are
// wired up, replace this with the entitlement check (e.g. `isPremiumUser`).
// Until then, hardcoded `true` so the feature is exercised during dev.
const NOWCAST_ENABLED = true;

// Short-lived in-flight just-fetched manifest cache. The manifest response
// lists every layer's files, so a composite radar fetches it once and all four
// sub-layers (MRMS+EUMETNET realtime+nowcast) filter that one response client-
// side. We dedupe the open-burst into a SINGLE fetch: the first caller's promise
// is memoized and reused for a brief window, then cleared so a later layer
// switch re-fetches fresh data. TTL is short (5s).
//
// SECURITY NOTE: the Worker now requires a valid token on manifest, but it
// still serves the full (unprefixed) listing to an authenticated caller. That's
// an intentional trade: one round-trip for every layer (fast) in exchange for
// an authenticated client being able to see the full key list. Anonymous and
// other-origin callers are blocked entirely by the origin+token gate, so this
// is not a public bucket index — only our own app, holding a fresh token, can
// read it.

// ─── Auto-refresh cadence ────────────────────────────────────────────────────
// How often to re-read /manifest looking for newly-ingested frames while the
// map sits open. Keyed off the data product's own update rate, because polling
// faster than the source publishes is pure waste:
//
//   radar/obs/*     MRMS + EUMETNET publish every ~2 min          → poll 60s
//   satellite/*     GMGSI mosaics land roughly hourly, but the
//                   ingest lag varies, so check more often        → poll 5 min
//   nwp/*           GFS runs every 6h; frames appear in bursts    → poll 15 min
//
// A poll is ONE request no matter how many sub-layers are active (the Worker
// serves the full listing and fetchManifest dedupes within MANIFEST_TTL_MS),
// and a poll that finds nothing new does not touch React state at all — so the
// steady-state cost of leaving the map open is one cheap request per interval.
const REFRESH_DEFAULT_MS = 5 * 60_000;

function refreshIntervalForSubLayers(subKeys: string[]): number {
  let ms = REFRESH_DEFAULT_MS;
  for (const k of subKeys) {
    const prefix = WEATHER_LAYERS[k]?.prefix ?? "";
    let cand = REFRESH_DEFAULT_MS;
    if (prefix.startsWith("radar/obs/")) cand = 60_000;
    else if (prefix.startsWith("satellite/")) cand = 5 * 60_000;
    else if (prefix.startsWith("nwp/")) cand = 15 * 60_000;
    // Composite layers take the tightest cadence of their members.
    ms = Math.min(ms, cand);
  }
  return ms;
}

const MANIFEST_TTL_MS = 5000;
// One cache entry PER PREFIX. Each layer asks the Worker for only its own
// folder (?prefix=...), so the Worker never lists the whole bucket. Listing
// the whole bucket was slow enough to 503 (surfacing as CORS errors) and was
// the cause of the multi-minute loads.
const manifestCache = new Map<string, { at: number; promise: Promise<any> }>();

function fetchManifest(prefix: string): Promise<any> {
  const now = Date.now();
  const hit = manifestCache.get(prefix);
  if (hit && now - hit.at < MANIFEST_TTL_MS) return hit.promise;

  const tMan = performance.now();
  console.log(`[Manifest] start ${prefix}`);
  const promise = getCogToken()
    .then((token) =>
      fetch(
        `${COG_API}/manifest?prefix=${encodeURIComponent(prefix)}` +
          `&_=${now}&t=${encodeURIComponent(token)}`,
        { cache: "no-store" },
      ),
    )
    .then((res) => {
      if (!res.ok) throw new Error(`manifest ${prefix} HTTP ${res.status}`);
      return res.json();
    })
    .then((data) => {
      console.log(
        `[Manifest] done ${prefix} files=${data?.files?.length ?? "?"} ` +
          `in ${(performance.now() - tMan).toFixed(0)}ms`,
      );
      return data;
    })
    .catch((err) => {
      if (manifestCache.get(prefix)?.promise === promise) manifestCache.delete(prefix);
      throw err;
    });
  manifestCache.set(prefix, { at: now, promise });
  return promise;
}

// Fetch keyframes for one prefix. Radar realtime is capped to the most recent
// 20 frames to keep animation tight and memory bounded.
async function fetchKeyframesForPrefix(prefix: string, options?: { cap?: number }) {
  try {
    const data = await fetchManifest(prefix);
    const all = (data?.files ?? [])
      .filter((f: any) => f.key.startsWith(prefix + "/") && f.key.endsWith(".tif"))
      .map((f: any) => ({
        key: f.key, filename: f.key.split("/").pop().replace(".tif", ""), size: f.size,
      }))
      .sort((a: any, b: any) => a.filename.localeCompare(b.filename));
    return options?.cap ? all.slice(-options.cap) : all;
  } catch (err) {
    console.error(`Failed to fetch keyframes for ${prefix}:`, err);
    return [];
  }
}

// Fetch realtime + optional nowcast frames for a sub-layer, returning one
// merged list sorted oldest → newest (realtime first, then forecast frames
// with strictly later timestamps). Each keyframe is tagged with isForecast
// so downstream UI can style/label them distinctly.
//
// Two tagging strategies, picked per-layer:
//   1. Two-prefix layers (radar): the realtime vs. nowcast *prefix* is the
//      authoritative source of truth. Realtime → past, nowcast → future.
//   2. Single-prefix layers (wind, temperature, dewpoint, humidity, etc.):
//      the model run writes past, present, AND future frames into ONE
//      directory, so the prefix tells us nothing about which side of "now"
//      a frame is on. We instead tag by comparing each frame's timestamp to
//      the current wall clock: frames at/before now are past, frames after
//      now are forecast. This makes the scrubber park at the present rather
//      than at the end of the forecast horizon, and lets the PAST/FUTURE
//      labels split at the correct point.
async function fetchKeyframes(cfg: LayerConfig) {
  // Cap BOTH radar realtime lists (MRMS and EUMETNET) to the most recent 20
  // frames; do NOT cap nowcast (it's small). The two radars feed ONE shared
  // scrubber index in the composite, so their realtime lists must be the same
  // length and cadence — otherwise the parked "now" index lands correctly for
  // one radar and out-of-range for the other, which is what made the EUMETNET
  // half stutter / animate from the wrong spot for EU viewers. Capping both to
  // 20 keeps the realtime portions aligned. (Nowcast tails can still differ in
  // length; applyFrameForIdx clamps the longer one at its tail.)
  const realtimeCap = cfg.prefix.startsWith("radar/obs/") ? 20 : undefined;
  const realtime = await fetchKeyframesForPrefix(cfg.prefix, { cap: realtimeCap });

  // ── Strategy 2: single-prefix layers tag by wall clock ──────────────────
  // (No separate nowcast prefix → the one directory spans past→future.)
  if (!cfg.nowcastPrefix) {
    const nowSec = Math.floor(Date.now() / 1000);
    return tagByWallClock(realtime, nowSec);
  }

  // ── Strategy 1: two-prefix layers (radar) — prefix is authoritative ─────
  const realtimeTagged = realtime.map((f: any) => ({ ...f, isForecast: false }));

  if (!NOWCAST_ENABLED) return realtimeTagged;

  const nowcast = await fetchKeyframesForPrefix(cfg.nowcastPrefix);
  // Defensive: if nowcast files somehow overlap or predate the realtime
  // tail, drop any that aren't strictly in the future relative to the last
  // realtime filename. Comparing by filename works because filenames start
  // with a sortable YYYYMMDD_HHMMSS timestamp.
  const lastRealtime = realtime.length > 0 ? realtime[realtime.length - 1].filename : "";
  const nowcastTagged = nowcast
    .filter((f: any) => f.filename > lastRealtime)
    .map((f: any) => ({ ...f, isForecast: true }));

  return [...realtimeTagged, ...nowcastTagged];
}

// Tag a single, oldest→newest keyframe list as past/forecast by comparing
// each frame's UTC timestamp to `nowSec` (unix seconds). The boundary is the
// first frame whose timestamp is strictly after now; everything from there on
// is forecast. If a frame's filename can't be parsed, it inherits the last
// known side (defaulting to past at the head of the list) so a single
// unparseable name can't flip the whole sequence into the future.
function tagByWallClock(kfs: any[], nowSec: number) {
  let seenForecast = false;
  return kfs.map((f: any) => {
    const ts = keyframeUnixTime(f.filename);
    if (ts != null) seenForecast = ts > nowSec;
    return { ...f, isForecast: seenForecast };
  });
}

// Given a single oldest→newest keyframe list, return the index the scrubber
// should park at: the LAST realtime (non-forecast) frame — i.e. "now". If the
// whole sequence is forecast (model run already ahead of the wall clock),
// there is no past frame, so park at the EARLIEST frame (index 0), the one
// closest to the present. Empty list → 0. Shared by the early-park (fast first
// paint) and the final post-preload park so the two agree exactly.
function computeParkIdx(kfs: any[]): number {
  for (let i = kfs.length - 1; i >= 0; i--) {
    if (!kfs[i].isForecast) return i;
  }
  return 0;
}

// Shade palette for lightning strike icons — older strikes get duller
// olive/brown shades, recent strikes get bright yellow. Used by both the
// initial render effect and the scrubber-sync effect that updates each
// timestamp layer's paint properties as the map clock advances.
const LIGHTNING_SHADES = [
  { color: "#8B7320", opacity: 0.35 }, { color: "#9B8225", opacity: 0.40 },
  { color: "#AA912A", opacity: 0.45 }, { color: "#B9A030", opacity: 0.50 },
  { color: "#C8AF35", opacity: 0.55 }, { color: "#D4BC3A", opacity: 0.60 },
  { color: "#E0C940", opacity: 0.65 }, { color: "#EAD545", opacity: 0.72 },
  { color: "#F2E04A", opacity: 0.78 }, { color: "#F8EA50", opacity: 0.85 },
  { color: "#FFEF5A", opacity: 0.92 }, { color: "#FFF176", opacity: 1.0 },
];

const LIGHTNING_LAYER_PREFIX = "lightning-ts-";
const LIGHTNING_ICON_ID = "lightning-bolt";

// Resolve a strike timestamp's shade given the current radar playback time.
// Returns null when the strike is in the future relative to the playback
// position (caller should hide that layer). When `mapTime` is null (no
// radar/animation layer active) the strike is shaded by its index across
// the full timestamp list — same behaviour as before this sync was wired
// up, so toggling lightning without a radar layer still looks correct.
function pickLightningShade(
  ts: number,
  tsIndex: number,
  totalCount: number,
  oldestTs: number,
  newestTs: number,
  mapTime: number | null,
): { color: string; opacity: number } | null {
  if (mapTime != null) {
    // Future strikes hidden until playback reaches them.
    if (ts > mapTime) return null;
    // Visible strikes: closer to the map clock = brighter. With only one
    // visible timestamp the gradient is meaningless, so just return the
    // brightest shade.
    const visibleSpan = Math.max(0, mapTime - ts);
    const oldestVisibleTs = oldestTs;
    const fullSpan = mapTime - oldestVisibleTs;
    if (fullSpan <= 0) return LIGHTNING_SHADES[LIGHTNING_SHADES.length - 1];
    const pos = 1 - Math.max(0, Math.min(1, visibleSpan / fullSpan));
    const idx = Math.round(pos * (LIGHTNING_SHADES.length - 1));
    return LIGHTNING_SHADES[idx];
  }
  // Fallback: no radar playback context. Use index-based shading
  // (identical to the original implementation).
  const idx = Math.round((tsIndex / Math.max(1, totalCount - 1)) * (LIGHTNING_SHADES.length - 1));
  // newestTs param kept for clarity / future use; intentionally unused here.
  void newestTs;
  return LIGHTNING_SHADES[idx];
}

// Expand a selected layer key into the list of sub-layer keys that should be
// rendered. For regular (non-composite) layers this is just [key]. For
// composite layers it is the array of referenced sub-layer keys. Returns
// [] when key is null.
function resolveSubLayers(key: string | null): string[] {
  if (!key) return [];
  const cfg = WEATHER_LAYERS[key];
  if (!cfg) return [];
  if (cfg.composite && cfg.composite.length > 0) return cfg.composite;
  return [key];
}

// Parse the YYYYMMDD_HHMMSS prefix of a keyframe filename into a unix
// timestamp (seconds). Used by the lightning overlay to sync strike
// visibility against the radar scrubber's current playback position.
// Returns null if the filename doesn't match any known format.
//
// IMPORTANT: filename formats vary by data source. Radar/MRMS files use the
// contiguous `YYYYMMDD_HHMMSS` form (e.g. 20260529_183000), but the GFS-derived
// layers (visibility, temperature, apparent_temperature, dewpoint,
// relative_humidity, cloud_cover, wind) use an UNDERSCORE-separated time:
// `YYYYMMDD_HH_MM_SS` (e.g. 20250527_18_30_00). This function previously only
// matched the contiguous form, so it returned null for every GFS-layer frame —
// which meant tagByWallClock never flagged any frame as forecast, the
// past/future boundary was never found, and the scrubber's PAST/FUTURE tick
// never appeared for those layers (even though they have future data). The
// fallback chain below mirrors the label parser in StormChanceUI so tagging and
// the displayed time agree on the boundary.
function keyframeUnixTime(filename: string | undefined | null): number | null {
  if (!filename) return null;
  // Contiguous time: YYYYMMDD_HHMMSS  (radar / MRMS)
  let m = filename.match(/(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})/);
  if (m) {
    const ms = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
    return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
  }
  // Underscore-separated time: YYYYMMDD_HH_MM_SS  (GFS-derived layers)
  m = filename.match(/(\d{4})(\d{2})(\d{2})_(\d{2})_(\d{2})_(\d{2})/);
  if (m) {
    const ms = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
    return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
  }
  // Hour+minute only: YYYYMMDD_HH_MM
  m = filename.match(/(\d{4})(\d{2})(\d{2})_(\d{2})_(\d{2})/);
  if (m) {
    const ms = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], 0);
    return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
  }
  // Hour only: YYYYMMDD_HH
  m = filename.match(/(\d{4})(\d{2})(\d{2})_(\d{2})/);
  if (m) {
    const ms = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], 0, 0);
    return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
  }
  // Last resort: 8-digit date followed by any time-like digit groups.
  m = filename.match(/(\d{4})(\d{2})(\d{2})\D*(\d{2})\D*(\d{2})?/);
  if (m) {
    const ms = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +(m[5] || 0), 0);
    return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
  }
  return null;
}

// ═══════════════════════════════════════════════════════════════════════════
// UI components (legend, scrubber, sidebar) — mostly unchanged
// ═══════════════════════════════════════════════════════════════════════════

function SingleLegend({ layer, bottomOffset, labelOverride }: {
  layer: string;
  bottomOffset?: number;
  labelOverride?: string;
}) {
  const config = WEATHER_LAYERS[layer];
  if (!config) return null;
  const displayLabel = labelOverride ?? config.label;
  const isSmall = useIsSmall();
  // On small/mobile viewports the fixed minWidth:340 nearly spans the screen.
  // Switch to a viewport-relative width with side margins, shrunk ~10% to match
  // the timeline pill. On desktop the base style is used unchanged.
  const responsiveStyle: React.CSSProperties = isSmall
    ? { minWidth: 0, width: "calc(90% - 30px)", maxWidth: "calc(100vw - 30px)" }
    : {};
  const containerStyle = bottomOffset !== undefined
    ? { ...legendContainerStyle, ...responsiveStyle, bottom: bottomOffset }
    : { ...legendContainerStyle, ...responsiveStyle };

  if (config.precipTypePalettes) {
    const makeGradient = (stops: any[]) =>
      stops.map((s: any) => {
        const pct = s[0] * 100;
        const [r, g, b] = s[1];
        return `rgb(${r},${g},${b}) ${pct}%`;
      }).join(", ");

    return (
      <div style={containerStyle}>
        <div style={legendHeaderStyle}>
          <span style={{ fontSize: 11, fontWeight: 600, color: "#e2e5ed" }}>{displayLabel}</span>
          <span style={{ fontSize: 10, color: "#4a5063" }}>Precip Type</span>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {[
            { label: "RAIN", color: "#4ecdc4", stops: config.precipTypePalettes.rain },
            { label: "SLEET", color: "#e879a8", stops: config.precipTypePalettes.sleet },
            { label: "SNOW", color: "#a0a0ff", stops: config.precipTypePalettes.snow },
          ].map((c) => (
            <div key={c.label} style={{ flex: 1 }}>
              <div style={{ fontSize: 9, color: c.color, fontFamily: "monospace", marginBottom: 2, fontWeight: 600 }}>
                {c.label}
              </div>
              <div style={{ height: 8, borderRadius: 3,
                background: `linear-gradient(to right, ${makeGradient(c.stops)})` }} />
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                <span style={{ fontSize: 8, color: "#4a5063", fontFamily: "monospace" }}>Light</span>
                <span style={{ fontSize: 8, color: "#4a5063", fontFamily: "monospace" }}>Heavy</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const stops = config.palette.filter((s) => s[1][3] > 0);
  if (stops.length === 0) return null;
  const gradient = stops.map((s) => `rgb(${s[1][0]},${s[1][1]},${s[1][2]}) ${s[0] * 100}%`).join(", ");

  return (
    <div style={{ ...containerStyle, minWidth: isSmall ? 0 : 260 }}>
      <div style={legendHeaderStyle}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "#e2e5ed" }}>{displayLabel}</span>
        <span style={{ fontSize: 10, color: "#4a5063" }}>{config.unit}</span>
      </div>
      <div style={{ height: 8, borderRadius: 3, background: `linear-gradient(to right, ${gradient})` }} />
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
        {config.legendLabels.map((l: string, i: number) => (
          <span key={i} style={{ fontSize: 9, color: "#4a5063", fontFamily: "monospace" }}>{l}</span>
        ))}
      </div>
    </div>
  );
}

// For composite layers, render one legend per sub-layer stacked vertically —
// UNLESS all sub-layers share the same palette shape (e.g. all are precip-
// type radar), in which case a single shared legend with the composite's own
// label is enough. Saves vertical space and avoids showing an identical
// Rain/Sleet/Snow bar twice for the MRMS + EU radar composite.
function LegendBar({ layer }: { layer: string }) {
  const config = WEATHER_LAYERS[layer];
  if (!config) return null;

  if (config.composite && config.composite.length > 0) {
    const subs = config.composite.map((k) => WEATHER_LAYERS[k]).filter(Boolean);
    const allPrecipType = subs.length > 0 && subs.every((s) => !!s.precipTypePalettes);

    // Collapse to a single shared legend when every sub-layer has the same
    // legend shape (precip-type palettes). We render it under the composite's
    // own label ("Radar") rather than any one sub-layer's label.
    if (allPrecipType) {
      return <SingleLegend layer={config.composite[0]} labelOverride={config.label} />;
    }

    return (
      <>
        {config.composite.map((subKey, i) => (
          <SingleLegend key={subKey} layer={subKey} bottomOffset={90 + i * 72} />
        ))}
      </>
    );
  }

  return <SingleLegend layer={layer} />;
}


const legendContainerStyle: React.CSSProperties = {
  position: "absolute", bottom: 90, left: "50%", transform: "translateX(-50%)",
  background: "rgba(8,10,16,0.9)", backdropFilter: "blur(12px)",
  border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8,
  padding: "8px 14px 5px", zIndex: 40, minWidth: 340,
};
const legendHeaderStyle: React.CSSProperties = {
  display: "flex", justifyContent: "space-between", marginBottom: 6,
};

function TimeScrubber(props: any) {
  // `length` is the scrubber range (min across active sub-layers). `total` is
  // the total frame count across all active sub-layers, used only for the
  // preload progress bar. `keyframes` is a representative list used for the
  // timestamp label (caller picks the shortest one).
  const length = props.length ?? props.keyframes.length;
  const total = props.total ?? props.keyframes.length;
  if (length === 0) return null;
  const currentFloor = Math.min(Math.floor(props.currentIdx), props.keyframes.length - 1);
  const current = props.keyframes[currentFloor];
  let timeLabel = current ? current.filename : "";
  const parts = timeLabel.match(/(\d{8})_(\d{2})_?(\d{2})?_?(\d{2})?/);
  if (parts && parts[3] !== undefined) {
    timeLabel = `${parts[1].slice(0,4)}-${parts[1].slice(4,6)}-${parts[1].slice(6,8)} ${parts[2]}:${parts[3]}Z`;
  }
  // Nowcast frames carry an `isForecast` tag. When the current frame is a
  // forecast, the timestamp switches to a warm amber and a "FORECAST" pill
  // appears. The indication is limited to the scrubber — map rendering
  // stays identical so there's no visual flicker crossing the boundary.
  const isForecast = !!current?.isForecast;
  const labelColor = isForecast ? "#f5a623" : "#4ecdc4";

  const progressPct = total > 0 ? Math.round((props.loadedCount / total) * 100) : 0;
  const fullyLoaded = props.loadedCount >= total;

  return (
    <div style={{
      position: "absolute", bottom: 32, left: "50%", transform: "translateX(-50%)",
      background: "rgba(8,10,16,0.9)", backdropFilter: "blur(12px)",
      border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8,
      padding: "8px 14px", zIndex: 40, display: "flex", flexDirection: "column", gap: 6,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={() => props.setPlaying(!props.playing)} style={{
          background: props.playing ? "rgba(78,205,196,0.2)" : "rgba(255,255,255,0.06)",
          border: "1px solid " + (props.playing ? "rgba(78,205,196,0.4)" : "rgba(255,255,255,0.1)"),
          borderRadius: 6, width: 30, height: 30, cursor: "pointer",
          color: props.playing ? "#4ecdc4" : "#8891a5", fontSize: 13,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>{props.playing ? "||" : ">"}</button>
        <div style={{ width: 200, position: "relative", display: "flex", alignItems: "center" }}>
          {/* Loaded-range underlay: shades the portion of the timeline that's
              actually decoded and therefore playable. Grows leftward as older
              history backfills, matching the preload progress. */}
          <div style={{
            position: "absolute", left: 0, right: 0, height: 3, top: "50%",
            transform: "translateY(-50%)", background: "rgba(255,255,255,0.05)",
            borderRadius: 2, pointerEvents: "none",
          }}>
            <div style={{
              position: "absolute", top: 0, bottom: 0, borderRadius: 2,
              left: `${length > 1 ? ((props.oldestLoaded ?? 0) / (length - 1)) * 100 : 0}%`,
              right: 0, background: "rgba(78,205,196,0.25)",
              transition: "left 0.3s ease",
            }} />
          </div>
          <input type="range" min={0} max={length - 1} value={props.currentIdx}
            step={0.01}
            onChange={(e) => props.onScrub(parseFloat(e.target.value))}
            style={{ width: "100%", accentColor: "#4ecdc4", position: "relative", zIndex: 1 }} />
        </div>
        {isForecast && (
          <span style={{
            fontFamily: "monospace", fontSize: 8, fontWeight: 700,
            color: "#f5a623", background: "rgba(245,166,35,0.14)",
            border: "1px solid rgba(245,166,35,0.35)", borderRadius: 3,
            padding: "2px 5px", letterSpacing: 1,
          }}>FORECAST</span>
        )}
        <span style={{
          fontFamily: "monospace", fontSize: 11, color: labelColor,
          minWidth: 140, textAlign: "right", whiteSpace: "nowrap",
        }}>{timeLabel}</span>
        <span style={{ fontSize: 9, color: "#4a5063", minWidth: 30, textAlign: "right" }}>
          {Math.floor(props.currentIdx) + 1}/{length}
        </span>
      </div>
      {!fullyLoaded && (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ flex: 1, height: 2, background: "rgba(255,255,255,0.06)", borderRadius: 1, overflow: "hidden" }}>
            <div style={{
              height: "100%", width: `${progressPct}%`, background: "#4ecdc4",
              transition: "width 0.2s ease", boxShadow: "0 0 4px rgba(78,205,196,0.5)",
            }} />
          </div>
          <span style={{ fontSize: 9, color: "#4a5063", fontFamily: "monospace", minWidth: 55, textAlign: "right" }}>
            preload {props.loadedCount}/{total}
          </span>
        </div>
      )}
    </div>
  );
}

function LayerSidebar(props: any) {
  return (
    <div style={{
      position: "absolute", top: 0, right: 0, height: "100%",
      width: props.collapsed ? 44 : 240,
      transition: "width 0.25s cubic-bezier(0.4,0,0.2,1)",
      background: "rgba(8,10,16,0.92)", backdropFilter: "blur(16px)",
      borderLeft: "1px solid rgba(255,255,255,0.06)", zIndex: 50,
      display: "flex", flexDirection: "column", overflow: "hidden",
    }}>
      <button onClick={() => props.setCollapsed(!props.collapsed)} style={{
        background: "none", border: "none", color: "#6b7280",
        padding: "14px", cursor: "pointer", fontSize: 16, textAlign: "left",
      }}>{props.collapsed ? "<" : ">"}</button>

      {!props.collapsed && (
        <div style={{ padding: "0 14px 14px", overflowY: "auto", flex: 1 }}>
          <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
            <BrandCube size={22} />
            <BrandWordmark size={13} />
          </div>

          <div style={sectionHeaderStyle}>DATA LAYER</div>
          {Object.entries(WEATHER_LAYERS)
            .filter(([, cfg]: any) => !cfg.hidden)
            .map(([key, cfg]: any) => (
            <button key={key} onClick={() => props.setActiveLayer(key === props.activeLayer ? null : key)}
              style={layerButtonStyle(props.activeLayer === key)}>
              {cfg.label}
              {props.activeLayer === key && props.loading && (
                <span style={{ float: "right", fontSize: 9, color: "#4ecdc4" }}>loading...</span>
              )}
            </button>
          ))}

          {props.activeLayer && (
            <div style={{ marginTop: 16 }}>
              <div style={sectionHeaderStyle}>OPACITY</div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: "#6b7280" }}>Layer opacity</span>
                <span style={{ fontSize: 10, color: "#4a5063", fontFamily: "monospace" }}>{props.opacity}%</span>
              </div>
              <input type="range" min={0} max={100} value={props.opacity}
                onChange={(e) => props.setOpacity(parseInt(e.target.value))}
                style={{ width: "100%", accentColor: "#4ecdc4" }} />
            </div>
          )}

          {props.activeLayer && (
            <div style={{ marginTop: 16 }}>
              <div style={sectionHeaderStyle}>PLAYBACK SPEED</div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: "#6b7280" }}>Animation speed</span>
                <span style={{ fontSize: 10, color: "#4a5063", fontFamily: "monospace" }}>
                  {(0.5 + (props.speedMultiplier - 2) * 0.5).toFixed(1)}×
                </span>
              </div>
              <input type="range" min={2} max={7} step={0.5}
                value={props.speedMultiplier}
                onChange={(e) => props.setSpeedMultiplier(parseFloat(e.target.value))}
                style={{ width: "100%", accentColor: "#4ecdc4" }} />
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                <span style={{ fontSize: 9, color: "#4a5063", fontFamily: "monospace" }}>Slow</span>
                <span style={{ fontSize: 9, color: "#4a5063", fontFamily: "monospace" }}>Fast</span>
              </div>
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <div style={sectionHeaderStyle}>BASEMAP</div>
            {["dark","light","white","black","grayscale"].map((f) => (
              <button key={f} onClick={() => props.setMapFlavor(f)} style={{
                display: "inline-block", padding: "4px 8px", marginRight: 4, marginBottom: 4,
                background: props.mapFlavor === f ? "rgba(78,205,196,0.12)" : "transparent",
                border: "1px solid " + (props.mapFlavor === f ? "rgba(78,205,196,0.3)" : "rgba(255,255,255,0.06)"),
                borderRadius: 4, cursor: "pointer", fontSize: 10, textTransform: "capitalize",
                color: props.mapFlavor === f ? "#4ecdc4" : "#6b7280", fontFamily: "monospace",
              }}>{f}</button>
            ))}
          </div>

          <div style={{ marginTop: 16 }}>
            <div style={sectionHeaderStyle}>OVERLAYS</div>
            <button onClick={() => props.setLightningEnabled(!props.lightningEnabled)}
              style={overlayButtonStyle(props.lightningEnabled, "#ffeb3b")}>Lightning</button>
            <button onClick={() => props.setWarningsEnabled(!props.warningsEnabled)}
              style={overlayButtonStyle(props.warningsEnabled, "#ff4444")}>Warnings (NWS)</button>
          </div>

          {props.activeLayer === "wind" && (
            <div style={{ marginTop: 16 }}>
              <div style={sectionHeaderStyle}>STREAMLINES</div>

              <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8,
                fontSize: 11, color: "#6b7280", cursor: "pointer" }}>
                <input type="checkbox" checked={props.showStreamlines}
                  onChange={(e: any) => props.setShowStreamlines(e.target.checked)}
                  style={{ accentColor: "#4ecdc4" }} />
                Show streamlines
              </label>

              {/* Streamline tuning sliders (Trail fade / Speed factor / Drop rate /
                  Drop rate bump / Point size) are intentionally hidden — the values
                  were tuned interactively and baked as defaults in App's useState
                  initializers. Restore the JSX from git history if you ever need
                  them back; the setSl* setters and props are all still wired. */}
            </div>
          )}

          {/* Debug section for motion-warp was here. Disabled along with
              motionWarp on MRMS. Re-enable this block if you flip motionWarp
              back on in weatherLayerConfig.ts. */}

          <div style={{ ...sectionHeaderStyle, marginTop: 20 }}>INFO</div>
          <div style={{ fontSize: 10, color: "#4a5063", lineHeight: 1.5 }}>
            GPU shader interpolates raw data values between frames at 60fps —
            colors are applied after interpolation for continuous motion.
          </div>
        </div>
      )}
    </div>
  );
}

const sectionHeaderStyle: React.CSSProperties = {
  fontSize: 9, letterSpacing: 2, color: "#3a3f4f",
  fontWeight: 700, marginBottom: 6, fontFamily: "monospace",
};

const layerButtonStyle = (active: boolean): React.CSSProperties => ({
  display: "block", width: "100%", textAlign: "left",
  background: active ? "rgba(78,205,196,0.12)" : "transparent",
  border: "1px solid " + (active ? "rgba(78,205,196,0.3)" : "transparent"),
  borderRadius: 5, padding: "7px 9px", marginBottom: 3, cursor: "pointer",
  color: active ? "#4ecdc4" : "#6b7280", fontSize: 12,
  transition: "all 0.15s",
});

const overlayButtonStyle = (active: boolean, accentColor: string): React.CSSProperties => ({
  display: "block", width: "100%", textAlign: "left",
  background: active ? `${accentColor}1f` : "transparent",
  border: "1px solid " + (active ? `${accentColor}4d` : "transparent"),
  borderRadius: 5, padding: "7px 9px", marginBottom: 3, cursor: "pointer",
  color: active ? accentColor : "#6b7280", fontSize: 12,
  transition: "all 0.15s",
});

// ═══════════════════════════════════════════════════════════════════════════
// TEMPORARY — Streamline tuning panel
// ───────────────────────────────────────────────────────────────────────────
// A self-contained floating panel of sliders for live-tuning the wind
// streamlines. Reads/writes the sl* state in StormChanceMap via props; those
// values flow into WeatherWindStreamlines through the already-wired live-update
// effects. This whole block (component + the pill/panel render in
// StormChanceMap + the showStreamlineTuner state) can be deleted in one pass
// once the look is dialed in. Point-size changes trigger a layer rebuild (see
// the slPointSize effect), so that slider commits on release rather than on
// every drag tick to avoid thrashing the rebuild.
// ═══════════════════════════════════════════════════════════════════════════
function StreamlineTuner(props: {
  fadeOpacity: number; setFadeOpacity: (n: number) => void;
  speedFactor: number; setSpeedFactor: (n: number) => void;
  dropRate: number; setDropRate: (n: number) => void;
  dropRateBump: number; setDropRateBump: (n: number) => void;
  pointSize: number; setPointSize: (n: number) => void;
  particleStateSize: number; setParticleStateSize: (n: number) => void;
  onClose: () => void;
}) {
  // Local mirror for point size so dragging is smooth; we only push the value
  // up (which rebuilds the layer) on pointer release / change-commit.
  const [pointSizeDraft, setPointSizeDraft] = useState(props.pointSize);
  useEffect(() => { setPointSizeDraft(props.pointSize); }, [props.pointSize]);
  // Particle count also rebuilds the layer, so mirror + commit on release too.
  const [particleDraft, setParticleDraft] = useState(props.particleStateSize);
  useEffect(() => { setParticleDraft(props.particleStateSize); }, [props.particleStateSize]);

  const rowStyle: React.CSSProperties = { marginBottom: 12 };
  const labelRowStyle: React.CSSProperties = {
    display: "flex", justifyContent: "space-between", marginBottom: 4,
  };
  const nameStyle: React.CSSProperties = { fontSize: 11, color: "#9aa3b2" };
  const valStyle: React.CSSProperties = {
    fontSize: 10, color: "#4ecdc4", fontFamily: "monospace",
  };
  const rangeStyle: React.CSSProperties = { width: "100%", accentColor: "#4ecdc4" };
  const hintStyle: React.CSSProperties = {
    fontSize: 9, color: "#4a5063", marginTop: 2, lineHeight: 1.35,
  };

  return (
    <div style={{
      position: "absolute", top: 16, left: 16, zIndex: 60, width: 248,
      background: "rgba(8,10,16,0.94)", backdropFilter: "blur(16px)",
      border: "1px solid rgba(78,205,196,0.25)", borderRadius: 10,
      padding: "12px 14px 14px",
      boxShadow: "0 8px 30px rgba(0,0,0,0.5)",
      fontFamily: "Roboto, -apple-system, system-ui, sans-serif",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between",
        alignItems: "center", marginBottom: 10 }}>
        <span style={{ fontSize: 9, letterSpacing: 2, color: "#4ecdc4",
          fontWeight: 700, fontFamily: "monospace" }}>STREAMLINE TUNER</span>
        <button onClick={props.onClose} style={{
          background: "none", border: "none", color: "#7d8a9a", cursor: "pointer",
          fontSize: 16, lineHeight: 1, padding: 0, width: 20, height: 20,
        }}>×</button>
      </div>

      {/* Trail fade — how long tails persist (exponential!) */}
      <div style={rowStyle}>
        <div style={labelRowStyle}>
          <span style={nameStyle}>Trail fade</span>
          <span style={valStyle}>{props.fadeOpacity.toFixed(3)}</span>
        </div>
        <input type="range" min={0.85} max={0.999} step={0.001} style={rangeStyle}
          value={props.fadeOpacity}
          onChange={(e) => props.setFadeOpacity(parseFloat(e.target.value))} />
        <div style={hintStyle}>Higher = longer tails. Exponential — small moves near 1.0 are dramatic.</div>
      </div>

      {/* Speed factor — how far particles advect per frame */}
      <div style={rowStyle}>
        <div style={labelRowStyle}>
          <span style={nameStyle}>Speed factor</span>
          <span style={valStyle}>{props.speedFactor.toFixed(3)}</span>
        </div>
        <input type="range" min={0.02} max={0.6} step={0.002} style={rangeStyle}
          value={props.speedFactor}
          onChange={(e) => props.setSpeedFactor(parseFloat(e.target.value))} />
        <div style={hintStyle}>How far particles travel each frame. Too low = static specks.</div>
      </div>

      {/* Drop rate — base respawn probability */}
      <div style={rowStyle}>
        <div style={labelRowStyle}>
          <span style={nameStyle}>Drop rate</span>
          <span style={valStyle}>{props.dropRate.toFixed(4)}</span>
        </div>
        <input type="range" min={0} max={0.02} step={0.0005} style={rangeStyle}
          value={props.dropRate}
          onChange={(e) => props.setDropRate(parseFloat(e.target.value))} />
        <div style={hintStyle}>How often particles respawn at random. Higher = more broken-up streaks.</div>
      </div>

      {/* Drop rate bump — extra respawn in fast wind */}
      <div style={rowStyle}>
        <div style={labelRowStyle}>
          <span style={nameStyle}>Drop rate bump</span>
          <span style={valStyle}>{props.dropRateBump.toFixed(4)}</span>
        </div>
        <input type="range" min={0} max={0.05} step={0.0005} style={rangeStyle}
          value={props.dropRateBump}
          onChange={(e) => props.setDropRateBump(parseFloat(e.target.value))} />
        <div style={hintStyle}>Extra respawn chance scaled by wind speed.</div>
      </div>

      {/* Point size — pixel thickness (commits on release; rebuilds layer) */}
      <div style={rowStyle}>
        <div style={labelRowStyle}>
          <span style={nameStyle}>Point size</span>
          <span style={valStyle}>{pointSizeDraft.toFixed(1)} px</span>
        </div>
        <input type="range" min={1} max={10} step={0.5} style={rangeStyle}
          value={pointSizeDraft}
          onChange={(e) => setPointSizeDraft(parseFloat(e.target.value))}
          onMouseUp={(e) => props.setPointSize(parseFloat((e.target as HTMLInputElement).value))}
          onTouchEnd={(e) => props.setPointSize(parseFloat((e.target as HTMLInputElement).value))} />
        <div style={hintStyle}>Line thickness. Commits on release (rebuilds the layer).</div>
      </div>

      {/* Particle count — THE density knob. Side length of the square particle
          texture; actual count = value². Commits on release (rebuilds layer). */}
      <div style={{ ...rowStyle, marginBottom: 0 }}>
        <div style={labelRowStyle}>
          <span style={nameStyle}>Particle count</span>
          <span style={valStyle}>
            {particleDraft} ({((particleDraft * particleDraft) / 1000).toFixed(0)}k)
          </span>
        </div>
        <input type="range" min={100} max={900} step={20} style={rangeStyle}
          value={particleDraft}
          onChange={(e) => setParticleDraft(parseInt(e.target.value))}
          onMouseUp={(e) => props.setParticleStateSize(parseInt((e.target as HTMLInputElement).value))}
          onTouchEnd={(e) => props.setParticleStateSize(parseInt((e.target as HTMLInputElement).value))} />
        <div style={hintStyle}>Density. count = value². Higher = more streamlines but heavier on the GPU. Commits on release.</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Main component
// ═══════════════════════════════════════════════════════════════════════════

export default function StormChanceMap() {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  // Holds the dynamically-imported `maplibre-gl` module after the map is
  // initialised. Needed by code paths outside the init effect — for example
  // the alert click handler — that need access to maplibregl.Popup or other
  // exports without re-importing the (large) module.
  const maplibreglRef = useRef<any>(null);
  // Mirror of mapFlavor in a ref. The warnings click handler is registered
  // once (when the layer is first added) and lives across many renders;
  // reading mapFlavor through a ref means new clicks always pick up the
  // current theme without us having to re-register the handler.
  const mapFlavorRef = useRef<string>("dark");
  // Tracks the currently-open NWS alert popup so a fresh click can close
  // the previous one. MapLibre allows multiple popups to coexist by default
  // — that quickly turns into clutter when users compare alerts. Cleared on
  // the popup's `close` event so the ref doesn't go stale.
  const activePopupRef = useRef<any>(null);

  // Long-lived refs
  const poolRef = useRef<WeatherCogPool | null>(null);
  // Current resolution TIER for full-extent COG decodes. We always decode the
  // whole radar extent (no viewport windowing), so the raster is fully present
  // at every pan/zoom and panning never re-decodes. Only the resolution varies
  // with zoom, in coarse tiers; crossing a tier boundary triggers a re-decode
  // at the new resolution. null = not computed yet (first load).
  // Initialized to the single fixed tier (not null) so the very first frames
  // decoded on open key under the same token as every later decode — avoids a
  // brief "full" vs "t0m1" key split that would cache the same image twice.
  const tierRef = useRef<ResolutionTier | null>(FIXED_TIER);
  // Per-sub-layer raster instances. Keyed by sub-layer id (e.g. "radar_mrms").
  // For simple (non-composite) layers this map has a single entry. For
  // composite layers it has one entry per element of the composite[] list.
  const weatherLayersRef = useRef<Map<string, WeatherRasterLayer>>(new Map());
  // Early-park bookkeeping for the current layer-init run. parkedLenRef holds
  // the length of the shortest sub-layer we've parked against so far this run
  // (Infinity = not yet parked). As sub-layer keyframe lists arrive (in any
  // order), we re-park only when a STRICTLY shorter list shows up — so the
  // final parked index matches the shortest sub-layer, exactly as the old
  // post-preload park did, but set as soon as keyframes land instead of after
  // the whole preload resolves. Reset to Infinity at the top of each run.
  const parkedLenRef = useRef<number>(Infinity);
  const streamlinesRef = useRef<WeatherWindStreamlines | null>(null);
  const [showStreamlines, setShowStreamlines] = useState(true);
  // These were tuned interactively via the now-hidden STREAMLINES sliders in
  // the sidebar. Tweak them here if you ever want to re-tune. The setters
  // are still wired up and the slider markup is just commented out, so it's
  // a one-line revert (see LayerSidebar) if you want the controls back.
  //
  // speedFactor is the dominant knob for "do these look like streamlines?".
  // It scales how far each particle advects per frame (offset = velocity *
  // 0.0001 * speedFactor, in wind-UV space). Too low and particles barely
  // move, so each one just fades in place and the field reads as static
  // specks rather than flowing lines — that's what a value like 0.046 gave.
  // 0.25 makes particles travel far enough each frame to lay down a visible
  // streak. Raise for faster/longer streaks, lower if they look too sparse.
  //
  // fadeOpacity sets how long the trail (the streak behind each particle)
  // persists: each frame the trail is redrawn at this opacity, so higher =
  // longer tails. 0.992 gives very long, near-streaky tails for visibility at
  // close zoom. Caveat: as this approaches 1.0 the trail fades so slowly it
  // can smear/ghost on a still camera — 0.992 is about as high as is safe
  // before that. (The trail buffer is cleared on camera moves, so smear only
  // shows when the view is held still for many seconds.)
  //
  // dropRate / dropRateBump control how often particles respawn at a fresh
  // random spot. Keep them low so streaks stay continuous instead of
  // constantly breaking and re-seeding.
  const [slFadeOpacity, setSlFadeOpacity] = useState(0.97);
  const [slSpeedFactor, setSlSpeedFactor] = useState(0.022);
  const [slDropRate, setSlDropRate] = useState(0.006);
  const [slDropRateBump, setSlDropRateBump] = useState(0.01);
  const [slPointSize, setSlPointSize] = useState(2.0);
  // Particle density. This is the side length of the square particle-state
  // texture, so the actual particle count is the square of it (e.g. 160 →
  // ~26k particles). Changing it rebuilds the streamlines layer (the particle
  // textures are allocated at construction), so the tuner commits it on slider
  // release rather than every drag tick. Keep the two construction sites below
  // reading this value (not a literal) so the slider drives both.
  const [slParticleStateSize, setSlParticleStateSize] = useState(160);
  // TEMPORARY (streamline tuner): set ENABLE_STREAMLINE_TUNER to true to bring
  // back the on-screen "Tune streamlines" pill + slider panel for live tuning.
  // Left in place (disabled) so it's a one-line revert. Remove the pill/panel
  // render block, the <StreamlineTuner/> component, and this state to delete
  // entirely.
  const ENABLE_STREAMLINE_TUNER = false;
  const [showStreamlineTuner, setShowStreamlineTuner] = useState(false);
  // Per-sub-layer frame caches and metadata. Each sub-layer maintains an
  // independent LRU (capped at its own maxCacheSize) and its own in-flight
  // decode set so misses on one radar don't affect the other.
  const frameCachesRef = useRef<Map<string, Map<string, WeatherFrame>>>(new Map());
  const maxCacheSizesRef = useRef<Map<string, number>>(new Map());
  const pendingJitRef = useRef<Map<string, Set<string>>>(new Map());
  const rafRef = useRef<number | null>(null);
  const playStartRef = useRef<number>(0);       // performance.now() when play began
  const playStartIdxRef = useRef<number>(0);    // fractional idx when play began
  const loopEndRef = useRef<number>(0);         // current loop wrap point (grows as nowcast loads)

  const WEATHER_LAYER_ID = "weather-raster-layer";

  const [mapFlavor, setMapFlavor] = usePersistedState<string>("mapFlavor", () => {
    // Day → "Light" (grayscale), night → "Dark" (black). Basemap is limited
    // to these two; the UI labels grayscale as "Light" and black as "Dark".
    // This time-of-day guess is only the FIRST-RUN default — once the user
    // picks a theme their choice is restored on every later visit instead.
    try { const h = new Date().getHours(); return (h >= 6 && h < 20) ? "grayscale" : "black"; }
    catch { return "black"; }
  }, isString);
  // Keep the ref in sync so click handlers registered in long-lived effects
  // can read the current theme without being re-registered on every change.
  useEffect(() => { mapFlavorRef.current = mapFlavor; }, [mapFlavor]);
  const [mapLoaded, setMapLoaded] = useState(false);
  // Current map zoom, mirrored into state so the streamline params can react to
  // it. Streamlines are simulated in viewport space, so without compensation
  // the field is the SAME density and trail length at every zoom — which reads
  // as balanced when zoomed out (~z4) but far too cluttered/intense when zoomed
  // in (z6+). We ease trail length and drop rate by zoom (see the streamline
  // params effect) so the close-zoom view keeps the lighter zoom-4 aesthetic.
  const [mapZoom, setMapZoom] = useState(4);
  const [activeLayer, setActiveLayer] = usePersistedState<string | null>("activeLayer", "radar", isStringOrNull);
  // When true, the radar precip raster is hidden (opacity forced to 0) while
  // radar stays the active layer — so overlays (lightning/warnings/alerts),
  // the timeline and the legend all keep working. Driven by the "Hide Precip"
  // control in the Radar menu. Only meaningful while activeLayer === "radar".
  const [hidePrecip, setHidePrecip] = useState(false);
  const [opacity, setOpacity] = usePersistedState("opacity", 70, isNumberInRange(0, 100));
  // Protomaps label-size multiplier (1 = default). Driven by the settings
  // "Label size" slider. Applied LIVE via setLayoutProperty (no setStyle), so
  // dragging the slider resizes labels instantly without tearing down the
  // custom weather layers.
  const [labelSize, setLabelSize] = usePersistedState("labelSize", 1, isNumberInRange(0.1, 4));
  // Basemap feature toggles (settings panel). All on by default. Applied LIVE
  // via setLayoutProperty("visibility", ...) and baked into buildDarkStyle so
  // they persist across a basemap swap.
  const [featureToggles, setFeatureToggles] = usePersistedState<FeatureToggles>(
    "featureToggles",
    { roads: true, highways: true, landuse: true, buildings: true },
    isBoolRecord(["roads", "highways", "landuse", "buildings"]),
  );
  const setFeatureToggle = (key: keyof FeatureToggles, value: boolean) =>
    setFeatureToggles((prev) => ({ ...prev, [key]: value }));
  // Bumped after a setStyle() basemap swap finishes loading. Custom layers
  // (weather raster, streamlines, coastline) and the warning/lightning style
  // layers are destroyed by setStyle, so effects that add them key on this
  // epoch to re-add themselves once the new style is live.
  const [styleEpoch, setStyleEpoch] = useState(0);
  // Playback speed multiplier. Applied on top of each layer's config
  // secondsPerFrame. Range is [2, 7]; default 4 sits in the middle.
  const [speedMultiplier, setSpeedMultiplier] = usePersistedState("speedMultiplier", 4.0, isNumberInRange(2, 7));
  const [keyframes, setKeyframes] = useState<Record<string, any[]>>({});
  // currentIdx is fractional (e.g. 3.4 means "40% between frame 3 and frame 4")
  const [currentIdx, setCurrentIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);
  // Frames that permanently failed to decode during preload (after one retry
  // with a freshly-minted token). Kept separate from loadedCount: the progress
  // UI counts these toward COMPLETION so the bar can finish and report "N
  // unavailable" instead of freezing at a fixed percentage forever.
  const [failedCount, setFailedCount] = useState(0);
  // Monotonic per-sub-layer registry of frames decoded AT LEAST ONCE this
  // session (keys are composed frame cache keys, so tier-scoped). The frame
  // CACHE is bounded by count and byte budget and therefore EVICTS — which
  // means "how many frames are in the cache" can PLATEAU below the loop
  // length even while every decode succeeds, and any progress number derived
  // from cache contents freezes at a fixed percentage forever. That is
  // indistinguishable, from the UI, from a network stall. The progress bar
  // numerator now counts THIS registry (decode progress), while playback and
  // the timeline underlay keep reading the real cache (residency). Entries
  // are tiny strings; unbounded growth over a session is negligible.
  const decodedOnceRef = useRef<Map<string, Set<string>>>(new Map());
  const byteBudgetWarnedRef = useRef<Set<string>>(new Set());
  // Mirrors for the byte-budget evictor (a []-deps callback that can't read
  // state directly): the current playhead index, and per-sub-layer maps from
  // keyframe key -> timeline index. Both are kept fresh by tiny effects below.
  const playheadIdxRef = useRef(0);
  const kfIndexRef = useRef<Map<string, Map<string, number>>>(new Map());
  // Monotonic counter per sub-layer marking when it was last the active layer.
  // Drives least-recently-used ordering when the global budget must release
  // whole inactive caches.
  const subLayerTouchOrderRef = useRef<Map<string, number>>(new Map());
  const touchSeqRef = useRef(0);
  // Bumped whenever the decode window changes (pan/zoom past the safe zone).
  // A dedicated effect watches this and re-warms the whole frame cache at the
  // new window — without it, only the JIT path refills frames, so the rest of
  // the timeline stays empty until playback sweeps the playhead across it.
  const [decodeWindowVersion, setDecodeWindowVersion] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Lightning & warnings
  const [lightningEnabled, setLightningEnabled] = usePersistedState("lightningEnabled", false, isBool);
  const [lightningData, setLightningData] = useState<Record<number, any>>({});
  const prevLightningTsRef = useRef<Set<number>>(new Set());
  const lightningFlashRef = useRef<number | null>(null);
  // Mirror of currentMapTime so the lightning render effect can read the
  // latest scrubber clock without depending on it (would otherwise tear
  // down all per-timestamp layers on every play tick).
  const currentMapTimeRef = useRef<number | null>(null);
  const [warningsEnabled, setWarningsEnabled] = usePersistedState("warningsEnabled", false, isBool);
  const [warningsData, setWarningsData] = useState<any>(null);
  // "All Weather Alerts" overlay. Distinct from "Severe Warnings": warnings
  // renders only the severe subset enumerated in WARNING_COLORS, whereas this
  // shows EVERY active NWS alert returned by the endpoint, colored by the
  // alert's `severity` property rather than by a hand-picked per-event palette.
  const [alertsEnabled, setAlertsEnabled] = usePersistedState("alertsEnabled", false, isBool);
  const [alertsData, setAlertsData] = useState<any>(null);
  // Canada alerts are now MERGED into the US alerts layer (one source, one
  // toggle). They're still fetched separately — from the Esri Canada / MSC
  // ArcGIS feature service (same protocol as the US NWS ArcGIS source: standard
  // [lng,lat] GeoJSON, permissive CORS) — and the two feature sets are combined
  // in the alerts render effect. This replaced the raw api.weather.gc.ca OGC
  // feed, whose features fetched but never reached the map source. canadaAlerts
  // Data holds the fetched Canada features; the shared alertsEnabled controls it.
  const [canadaAlertsData, setCanadaAlertsData] = useState<any>(null);
  // NHC tropical cyclone overlay: forecast cone, forecast track + points, and
  // past (best) track + points, all from the NHC "tropical weather summary"
  // ArcGIS service (every active storm, every basin, one fetch). See
  // stormOverlays.ts for endpoints, layer IDs, and styling.
  const [tropicalEnabled, setTropicalEnabled] = usePersistedState("tropicalEnabled", false, isBool);
  const [tropicalData, setTropicalData] = useState<TropicalData | null>(null);
  // SPC Day 1 convective outlook overlay. The four products (categorical /
  // tornado / wind / hail) cover the same geography, so this is an EXCLUSIVE
  // pick — null means off, otherwise exactly one product is shown.
  const [spcOutlook, setSpcOutlook] = useState<SpcProduct | null>(null);
  const [spcData, setSpcData] = useState<any>(null);
  // One-time click/cursor handler registration guard (tropical only — the SPC
  // overlay is legend-driven and has no click handlers). MapLibre keeps
  // delegated per-layer listeners on the map object (they survive setStyle
  // and layer re-adds), so re-binding on every styleEpoch would stack
  // duplicate handlers.
  const tropicalHandlersBoundRef = useRef(false);

  // Small-viewport flag for the attribution pill's compact sizing (matches the
  // < 400px breakpoint the legend/timeline pills use via useIsSmall).
  const isSmall = useIsSmall();

  // Which alert data sources ("nws" / "eccc") are actually rendered in the
  // current viewport. Drives the region-keyed attribution credit: NOAA over the
  // US, Environment and Climate Change Canada over Canada (TEXT only — the MSC
  // Open Data licence forbids the ECCC logo), both when the view straddles the
  // border, and nothing over open ocean. Reads the `_source` tag the alert
  // normalizers stamp on every feature; queries the merged "alerts-fill" layer,
  // so it adds no fetch. dataVersion re-evaluates the set when either feed
  // merges in without a camera move.
  const visibleAlertSources = useVisibleAlertSources(mapRef.current, {
    layerId: "alerts-fill",
    enabled: alertsEnabled,
    dataVersion: [alertsData, canadaAlertsData, mapLoaded],
  });

  // Tropical cyclone + SPC sources for the same attribution pill. Each hook
  // queries its own overlay layer; the merged set feeds a single pill so the
  // user sees one compact credit line regardless of which overlays are on.
  const visibleTropicalSources = useVisibleAlertSources(mapRef.current, {
    layerId: "tropical-obs-points",
    enabled: tropicalEnabled,
    dataVersion: [tropicalData, mapLoaded],
  });
  const visibleSpcSources = useVisibleAlertSources(mapRef.current, {
    layerId: "spc-outlook-fill",
    enabled: !!spcOutlook,
    dataVersion: [spcData, mapLoaded],
  });

  // Merge all three source sets into one for the attribution pill.
  const mergedSources = useMemo(() => {
    const merged = new Set<string>();
    for (const s of visibleAlertSources) merged.add(s);
    for (const s of visibleTropicalSources) merged.add(s);
    for (const s of visibleSpcSources) merged.add(s);
    return merged;
  }, [visibleAlertSources, visibleTropicalSources, visibleSpcSources]);

  // ── Keep the ⓘ attribution button clear of the bottom pills ───────────────
  // MapLibre's compact attribution control lives at the bottom-right of the
  // map. The timeline/legend pill is centred and, on narrow viewports, spans
  // nearly the full width — so as the window shrinks the two collide and the
  // pill (a later sibling in the DOM) paints over the ⓘ, hiding a control we
  // are contractually required to keep reachable.
  //
  // Two things fix that, and both are needed:
  //   1. z-index — `.maplibregl-ctrl-bottom-right` is raised above the pill in
  //      the stylesheet at the bottom of this file, so the ⓘ is never buried
  //      even mid-transition.
  //   2. This effect — once the two boxes actually intersect, the ⓘ climbs the
  //      right edge and comes to rest ON the pill's top edge, tucked into its
  //      top-right corner. It does NOT stay put and simply layer over the box:
  //      that would leave it covering the legend's SNOW/Heavy label and the
  //      time readout underneath it. Riding the rim keeps both the button and
  //      everything inside the pill fully legible.
  //
  // MEASUREMENT NOTE — the whole correctness of this hinges on it: the button's
  // RESTING geometry is derived from layout (its untransformed offset box
  // inside MapLibre's corner container), never from its own live
  // getBoundingClientRect.
  //
  // The obvious version — read the current rect and add back whatever lift is
  // applied — is subtly broken, because `transform` is transitioned. A
  // re-measure landing inside that 180ms window reads a HALF-MOVED rect and
  // adds back the FULL target lift, so the "resting" position comes out too
  // low, the overlap test fails, and the lift resets to zero — dropping the
  // button back onto the timeline. Map rendering fires mutations more or less
  // continuously, so in practice that clobbered the lift within a frame or two
  // of every attempt, and the button only looked right after some later event
  // (toggling the menu) happened to re-measure once everything had settled.
  //
  // offsetWidth/offsetHeight are layout values and ignore transforms entirely,
  // so the geometry below is stable no matter when it is sampled — mid-
  // transition, mid-render, or at rest. The result is idempotent: running it
  // twice in a row produces the same answer.
  useEffect(() => {
    if (!mapLoaded) return;
    // The button comes to REST on the pill's top edge rather than floating in
    // clear space above it — it should read as sitting on the rim of the box,
    // so this is a hairline separation, not a margin. Raise it if you want the
    // button to detach from the pill instead.
    const REST_GAP = 2;
    const OBSTACLES = ["[data-timeline-pill]", "[data-attrib-pill]"];
    let lift = 0;                  // px currently applied vertically
    let shift = 0;                 // px currently applied horizontally
    let raf = 0;
    let ctrlEl: HTMLElement | null = null;
    let pillEl: HTMLElement | null = null;

    const apply = () => {
      const host = mapContainer.current;
      const corner = host?.querySelector(
        ".maplibregl-ctrl-bottom-right"
      ) as HTMLElement | null;
      if (!host || !corner) return;

      // WHAT MOVES vs WHAT IS MEASURED. We transform the attribution control
      // ITSELF, not MapLibre's bottom-right corner container that holds it.
      // That split is deliberate: the corner is then never transformed, so its
      // getBoundingClientRect is trustworthy at any moment, and it serves as a
      // fixed origin for locating the control inside it.
      //
      // Do NOT try to derive the control's box by subtracting the corner's from
      // it — that was the previous bug. MapLibre gives the corner's children
      // `margin: 0 10px 10px 0`, but the COMPACT attribution overrides that
      // with `margin: 10px` on all four sides, so the inset differs top vs
      // bottom and the guessed height came out 10px wrong. A 10px error is
      // enough to drop the button inside the pill's top edge and clip it.
      //
      // offsetLeft/offsetTop are measured against the offsetParent (the corner,
      // which is positioned) and are pure layout values — they ignore any
      // transform on the element, so this is exact whatever margins MapLibre
      // decides to use, and stable mid-transition.
      const ctrl = (corner.querySelector(".maplibregl-ctrl") ??
        corner) as HTMLElement;
      const iw = ctrl.offsetWidth, ih = ctrl.offsetHeight;
      if (iw === 0 || ih === 0) return;

      const cornerRect = corner.getBoundingClientRect();
      const sameEl = ctrl === corner;
      const natLeft = cornerRect.left + (sameEl ? 0 : ctrl.offsetLeft);
      const natTop = cornerRect.top + (sameEl ? 0 : ctrl.offsetTop);
      const natRight = natLeft + iw;
      const natBottom = natTop + ih;

      // Lift enough to clear every obstacle the button would otherwise sit on.
      // This iterates because clearing one obstacle can move the button INTO
      // another: hopping above the timeline pill lands it in the band where the
      // data-credit pill lives, and on a narrow screen that credit is wide
      // enough to reach the right edge. Each pass re-tests from the position
      // the previous pass would produce, so the button settles above the whole
      // stack rather than between two of its layers. Two passes is the real
      // ceiling with two obstacles; the loop cap is just a guard.
      let want = 0;
      for (let pass = 0; pass < 4; pass++) {
        const top = natTop - want;
        const bottom = natBottom - want;
        let next = want;
        for (const sel of OBSTACLES) {
          const el = document.querySelector(sel) as HTMLElement | null;
          if (!el) continue;
          const p = el.getBoundingClientRect();
          if (p.width === 0 || p.height === 0) continue;
          const overlapsX = natLeft < p.right && natRight > p.left;
          const overlapsY = top < p.bottom && bottom > p.top;
          if (overlapsX && overlapsY) {
            // Solve for the lift that lands the button's bottom edge on this
            // obstacle's top edge.
            next = Math.max(next, Math.ceil(natBottom - p.top + REST_GAP));
          }
        }
        if (next === want) break;
        want = next;
      }

      // Tuck the button against the pill's right edge while it's perched there.
      // MapLibre's control margin (10px) and the pill's side margin (12-20px,
      // tier-dependent) don't match, so without this the button floats a few px
      // outside the corner it's supposed to be sitting on. Only applied while
      // lifted — at rest the control keeps its normal bottom-right placement.
      let wantShift = 0;
      const pill = document.querySelector(
        "[data-timeline-pill]"
      ) as HTMLElement | null;
      if (want > 0 && pill) {
        const pr = pill.getBoundingClientRect();
        if (pr.width > 0) wantShift = Math.round(pr.right - natRight);
      }

      if (want !== lift || wantShift !== shift) {
        lift = want;
        shift = wantShift;
        ctrl.style.transform =
          lift || shift ? `translate(${shift}px, ${-lift}px)` : "";
      }

      // (Re)bind size observers to whatever the anchors resolve to now. The
      // timeline pill unmounts when no layer is active and remounts with a
      // different height when one is picked, so identity can change. Observing
      // the pill is what catches it growing as frames arrive — it mounts short
      // ("Loading…", no legend yet) and gets taller once the legend renders,
      // which moves the very edge the button is resting on.
      if (ro && (ctrl !== ctrlEl || pill !== pillEl)) {
        ro.disconnect();
        ctrlEl = ctrl;
        pillEl = pill;
        ro.observe(ctrl);
        if (pill) ro.observe(pill);
      }
    };

    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(apply);
    };
    const ro = "ResizeObserver" in window ? new ResizeObserver(schedule) : null;

    schedule();
    // Late-layout safety net. Inside the Flutter WebView the first frames can
    // land before web fonts settle and before the initial keyframes arrive, so
    // the pill's final height isn't known yet. These are cheap idempotent
    // re-checks, not a poll — they stop after the last one.
    const timers = [120, 400, 1200, 3000].map((ms) =>
      window.setTimeout(schedule, ms)
    );
    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", schedule);
    mapRef.current?.on("resize", schedule);
    // childList only: catches the pills mounting/unmounting without firing on
    // every attribute tick from the map canvas (or on our own style write).
    const mo =
      "MutationObserver" in window ? new MutationObserver(schedule) : null;
    mo?.observe(document.body, { childList: true, subtree: true });
    // Expanding the ⓘ into its full "© OpenStreetMap" text changes its size,
    // which needs a re-measure too.
    const ctrlClick = () => schedule();
    document
      .querySelector(".maplibregl-ctrl-attrib")
      ?.addEventListener("click", ctrlClick);

    return () => {
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      mapRef.current?.off("resize", schedule);
      mo?.disconnect();
      ro?.disconnect();
      document
        .querySelector(".maplibregl-ctrl-attrib")
        ?.removeEventListener("click", ctrlClick);
      const resting = mapContainer.current?.querySelector(
        ".maplibregl-ctrl-bottom-right .maplibregl-ctrl"
      ) as HTMLElement | null;
      if (resting) resting.style.transform = "";
    };
  }, [mapLoaded]);

  // Lightning, Severe Warnings, and All Weather Alerts are radar-only overlays.
  // Whenever the active data layer is anything other than radar (satellite,
  // sky, air, wind), force all three off. Flipping these booleans to false
  // makes the existing per-overlay effects tear down their map layers, sources,
  // popups, and polling intervals, and the menu buttons (driven by parent
  // state) follow suit. Re-selecting radar leaves them off until the user opts
  // back in — we don't restore prior overlay state.
  //
  // NOTE: the Storm Outlook (SPC) and Tropical Systems (NHC / Digital Typhoon)
  // overlays are deliberately NOT gated here — they live in their own menu
  // sections and are meaningful over any base layer (e.g. typhoon tracks over
  // satellite imagery), so they persist across layer switches.
  useEffect(() => {
    if (activeLayer !== "radar") {
      setLightningEnabled(false);
      setWarningsEnabled(false);
      setAlertsEnabled(false);
    }
  }, [activeLayer]);

  // ─── Main-menu combination rules (newest pick wins) ────────────────────────
  // • Storm Outlook (SPC) always displays ALONE — no base layer (not even
  //   wind) and no tropical overlay alongside it. Turning it on clears both;
  //   picking anything else while it's on turns it off and shows the new pick.
  // • Tropical Systems may pair ONLY with the Wind base layer (or show alone).
  //   Enabling tropical over any other base layer clears that layer; picking a
  //   non-wind base layer while tropical is on turns tropical off.
  // • Radar and its companion overlays (lightning / warnings / alerts / hide
  //   precip) are deliberately untouched — their radar-only gating above is
  //   the existing behavior and stays exactly as it was.
  // These wrappers are what StormChanceUI receives, so every user selection
  // path (expanded menu, collapsed rail, mobile pill) flows through them; the
  // raw setters remain available internally for non-user state plumbing.
  const selectActiveLayer = useCallback((layer: string | null) => {
    setActiveLayer(layer);
    if (layer) {
      setSpcOutlook(null);                       // SPC never combines
      if (layer !== "wind") setTropicalEnabled(false); // tropical pairs with wind only
    }
  }, []);
  const selectSpcOutlook = useCallback((v: SpcProduct | null) => {
    setSpcOutlook(v);
    if (v) {
      setActiveLayer(null);
      setTropicalEnabled(false);
    }
  }, []);
  const selectTropical = useCallback((v: boolean) => {
    setTropicalEnabled(v);
    if (v) {
      setSpcOutlook(null);
      setActiveLayer((prev) => (prev && prev !== "wind" ? null : prev));
    }
  }, []);

  // ─── Derived: sub-layer list + scrubber sizing ───────────────────────────
  // For composite layers, the scrubber length is the length of the SHORTEST
  // sub-layer keyframe list — both sub-layers advance together by index and
  // the longer one is clamped at its last frame. (Typical case: radar_mrms
  // is capped at 20 and radar_eumetnet may be slightly different; whichever
  // is shorter bounds the scrubber.) For single layers this is just that
  // layer's keyframe count.
  //
  // Memoized so that effects depending on subLayerKeys don't re-run every
  // render from a fresh array identity.
  const subLayerKeys = useMemo(() => resolveSubLayers(activeLayer), [activeLayer]);

  // Bumped whenever the page comes back after being hidden/frozen/offline for
  // a meaningful stretch — see useWakeEpoch. Every poller below lists this in
  // its deps so returning to the tab re-runs the fetch immediately instead of
  // waiting out the remainder of a setInterval that wasn't running.
  const wakeEpoch = useWakeEpoch();

  const scrubberLength = useMemo(
    () => subLayerKeys.length === 0
      ? 0
      : Math.min(...subLayerKeys.map((k) => keyframes[k]?.length ?? 0)),
    [subLayerKeys, keyframes],
  );

  // The "active" sub-layer for progress/playback gating. A radar composite has
  // two independent sub-layers (radar_mrms / US and radar_eumetnet / Europe);
  // only one is meaningfully visible depending on where the user is looking,
  // and the other's frames are off-screen/empty. Rather than detect geography,
  // we pick whichever sub-layer currently has the MOST frames cached at the
  // current tier — that's the one actually being watched and decoded. This
  // makes the progress bar and the playback start-clamp track the radar the
  // user sees, for BOTH US and EU users. Falls back to the first sub-layer
  // before anything is loaded.
  const activeSubLayerKey = useCallback((): string | null => {
    if (subLayerKeys.length === 0) return null;
    if (subLayerKeys.length === 1) return subLayerKeys[0];
    const suffix = `::${tierToken(tierRef.current)}`;
    let bestKey = subLayerKeys[0];
    let bestCount = -1;
    for (const subKey of subLayerKeys) {
      const cache = frameCachesRef.current.get(subKey);
      let c = 0;
      if (cache) for (const key of cache.keys()) if (key.endsWith(suffix)) c++;
      if (c > bestCount) { bestCount = c; bestKey = subKey; }
    }
    return bestKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subLayerKeys]);

  // keyframes only — consistent with loadedAtWindow (the numerator). Using the
  // active (most-loaded) sub-layer means the bar reflects whichever radar the
  // user is actually viewing (MRMS in the US, EUMETNET in Europe), instead of
  // summing both and reading far below 100% when only one is on screen.
  const totalExpectedFrames = useMemo(() => {
    const key = activeSubLayerKey();
    return key ? (keyframes[key]?.length ?? 0) : 0;
  }, [activeSubLayerKey, keyframes, loadedCount]);

  // How many frames are actually cached AT THE CURRENT TIER, counted against
  // the PRIMARY sub-layer only (the first one — radar_mrms for the radar
  // composite), matching oldestLoadedIdx and the visible animation. A
  // composite's secondary layer (radar_eumetnet / Europe) has its own much
  // longer, time-misaligned keyframe list and may load partially or not at all
  // for a US-centered view — counting both made the bar read ~20% (MRMS fully
  // loaded + EU barely loaded, over the combined total) even though the radar
  // on screen was completely loaded. The cache is tier-keyed, so we match the
  // current tier's token suffix. Recomputed when a decode lands (loadedCount)
  // or the tier changes (decodeWindowVersion).
  const loadedAtWindow = useMemo(() => {
    const tier = tierRef.current;
    const key = activeSubLayerKey();
    if (!key) return 0;
    // DECODE PROGRESS, not cache residency: a frame counts once decoded at
    // the current tier, even if the byte-budget LRU has since evicted it.
    // Counting cache contents here made the bar plateau at a fixed percentage
    // forever whenever the loop outgrew the byte budget — every decode
    // succeeding, zero errors, bar frozen. Iterating the CURRENT keyframe
    // list (instead of registry size) keeps the numerator honest when
    // manifests advance and old keys linger in the registry.
    const cache = frameCachesRef.current.get(key);
    const seen = decodedOnceRef.current.get(key);
    const kfs = keyframes[key] ?? [];
    let c = 0;
    for (const kf of kfs) {
      const ck = frameCacheKey(kf.key, tier);
      if (cache?.has(ck) || seen?.has(ck)) c++;
    }
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubLayerKey, keyframes, loadedCount, decodeWindowVersion]);

  // The contiguous block of loaded frames AROUND the parked "now" frame, at
  // the current tier. Returns [low, high] inclusive indices.
  //
  // Why "around now" and not "walk down from newest": the preload decodes the
  // parked now-frame first, then the recent past, then the nowcast, then older
  // history. So early in loading the loaded set is a block straddling the
  // forecast boundary — e.g. [recentPastStart .. now] with the nowcast still
  // filling in above. The newest frame (end of nowcast) is often NOT loaded yet.
  // The old logic walked DOWN from `scrubberLength-1` and stopped at the first
  // gap; with nowcast loading after the past, that newest frame was unloaded,
  // so it bailed immediately and collapsed the playable range. Instead we anchor
  // at the parked frame (always loaded first) and expand outward in BOTH
  // directions to the first gap on each side. Both bounds grow as frames land:
  // `low` moves earlier as history backfills, `high` moves later as nowcast
  // fills in — which is exactly the "loop start and end both grow" behaviour.
  //
  // Judged against the PRIMARY sub-layer only (radar_mrms for the radar
  // composite) — see the long note that previously lived here: requiring every
  // sub-layer to have frame i made almost nothing count as loaded.
  const loadedBlockAroundNow = useCallback((): [number, number] => {
    if (subLayerKeys.length === 0 || scrubberLength === 0) return [0, 0];
    const tier = tierRef.current;
    const key = activeSubLayerKey();
    if (!key) return [0, 0];
    const kfs = keyframes[key];
    if (!kfs || kfs.length === 0) return [0, 0];
    const cache = frameCachesRef.current.get(key);
    const isLoadedAt = (idx: number): boolean => {
      if (idx < 0 || idx >= kfs.length || idx >= scrubberLength) return false;
      return !!cache?.has(frameCacheKey(kfs[idx].key, tier));
    };
    const newest = scrubberLength - 1;
    // Anchor at the parked "now" frame (last non-forecast, clamped to range).
    let anchor = computeParkIdx(kfs);
    if (anchor > newest) anchor = newest;
    if (anchor < 0) anchor = 0;
    // If the anchor itself isn't loaded yet (very first ticks), there's no
    // playable block — report a degenerate single-frame range at the anchor.
    if (!isLoadedAt(anchor)) return [anchor, anchor];
    // Expand down to the first gap.
    let low = anchor;
    while (low - 1 >= 0 && isLoadedAt(low - 1)) low--;
    // Expand up to the first gap (never past the newest frame).
    let high = anchor;
    while (high + 1 <= newest && isLoadedAt(high + 1)) high++;
    return [low, high];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subLayerKeys, activeSubLayerKey, keyframes, scrubberLength]);

  // Low bound of the loaded block (the scrubber's left edge / playback start).
  // Kept as `oldestLoadedIdx` so existing callers (scrubber underlay, play
  // loop) don't change shape.
  const oldestLoadedIdx = useCallback((): number => {
    return loadedBlockAroundNow()[0];
  }, [loadedBlockAroundNow]);

  // High bound of the loaded block (the playback wrap point). Grows forward
  // as nowcast frames decode, so the loop extends through the future as it
  // fills in rather than wrapping at an undecoded frame.
  const newestLoadedIdx = useCallback((): number => {
    return loadedBlockAroundNow()[1];
  }, [loadedBlockAroundNow]);

  // Mirror the block function into a ref so the play-loop RAF closure can read
  // the current loaded bounds at wrap time WITHOUT being in its dependency
  // array — otherwise the loop would tear down and restart (visible stutter)
  // every time a frame finishes decoding and changes the callback's identity.
  // Reading BOTH bounds from this single call guarantees a consistent [lo, hi]
  // (calling two separate accessors mid-decode could observe different cache
  // states and disagree).
  const loadedBlockAroundNowRef = useRef(loadedBlockAroundNow);
  loadedBlockAroundNowRef.current = loadedBlockAroundNow;

  // Reactive value of the oldest loaded index for UI (the scrubber's left
  // bound). Recomputes as decodes land (loadedCount) or the tier changes.
  const oldestLoaded = useMemo(
    () => oldestLoadedIdx(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [oldestLoadedIdx, loadedCount, decodeWindowVersion],
  );

  // Unix timestamp (seconds) at the current scrubber position, derived by
  // interpolating between keyframes[shortestKey][floor(currentIdx)] and
  // [floor+1]. Returns null when no radar/animation layer is active so
  // overlays (lightning) can fall back to "show everything" behaviour. Uses
  // the shortest sub-layer's keyframes — same one TimeScrubber displays —
  // so what the user sees in the time label is what overlays sync against.
  const currentMapTime = useMemo<number | null>(() => {
    if (subLayerKeys.length === 0) return null;
    const shortestKey = subLayerKeys.reduce((best, k) => {
      const len = keyframes[k]?.length ?? 0;
      const bestLen = keyframes[best]?.length ?? Infinity;
      return len > 0 && len < bestLen ? k : best;
    }, subLayerKeys[0]);
    const kfs = keyframes[shortestKey];
    if (!kfs || kfs.length === 0) return null;
    const idx = Math.max(0, Math.min(currentIdx, kfs.length - 1));
    const lo = Math.floor(idx);
    const hi = Math.min(lo + 1, kfs.length - 1);
    const t0 = keyframeUnixTime(kfs[lo]?.filename);
    const t1 = keyframeUnixTime(kfs[hi]?.filename);
    if (t0 == null) return null;
    if (t1 == null || lo === hi) return t0;
    const f = idx - lo;
    return Math.round(t0 + (t1 - t0) * f);
  }, [subLayerKeys, keyframes, currentIdx]);

  // ─── Init worker pool ─────────────────────────────────────────────────────
  useEffect(() => {
    try {
      // Pool size is memory, not just parallelism: each worker can hold a
      // whole-file ArrayBuffer plus its decoded raster at the same instant
      // (the range path's fallback fetches the entire COG — that is the very
      // allocation pattern the original iOS crash was traced to). Three
      // workers can therefore have three full COGs in flight simultaneously.
      // iOS gets 2, trading a little preload throughput for a materially
      // lower peak. Desktop/Android keep the default heuristic.
      poolRef.current = new WeatherCogPool(
        () => new WeatherCogWorker(),
        IS_IOS_FAMILY ? 2 : undefined,
      );
    } catch (err) {
      // A failed worker construction (e.g. the ?worker module fails to load
      // under the new ES worker format) would otherwise throw silently out of
      // this effect. Log it loudly so it's visible instead of a frozen screen.
      console.error("[pool init] FAILED to construct worker pool:", err);
    }
    return () => { poolRef.current?.destroy(); poolRef.current = null; };
  }, []);

  // ─── Init map ────────────────────────────────────────────────────────────
  useEffect(() => {
    let mounted = true;
    (async () => {
      const maplibregl = await import("maplibre-gl");
      const pmtiles = await import("pmtiles");
      if (!mounted) return;
      maplibreglRef.current = maplibregl;

      const protocol = new pmtiles.Protocol();
      maplibregl.default.addProtocol("pmtiles", protocol.tile);

      // Camera constraints to keep weather data continuous and on-screen.
      //
      // Why no `maxBounds` here:
      //   We initially tried `maxBounds: [[-180, -85], [180, 85]]` to lock
      //   panning to the world rectangle. That hits MapLibre issue #6148 —
      //   when the longitude span equals (or exceeds) 360°, the projection
      //   matrix collapses to singular and the next _calcMatrices call
      //   crashes with "Cannot read properties of null (reading '0')".
      //   Workarounds like `[-179.999, 179.999]` avoid the crash but leave
      //   a visible stutter at the edges.
      //
      // Cleaner approach: disable world repeating entirely with
      // `renderWorldCopies: false`. The basemap then renders exactly one
      // world copy, matching the wind/raster data extent, and we don't need
      // a longitude bound at all. minZoom (computed from container width)
      // still prevents zooming so far out that the world becomes smaller
      // than the viewport — at that point MapLibre would just letterbox
      // with empty space, which we don't want either.
      // Read optional lat/lng passed in from the Flutter app via URL query params.
      // When present they are used as the initial camera position so the map
      // opens centred on the user's current weather location without needing
      // the browser's own geolocation permission (which WebView blocks).
      const urlParams = new URLSearchParams(window.location.search);
      const paramLat = parseFloat(urlParams.get('lat') ?? '');
      const paramLng = parseFloat(urlParams.get('lng') ?? '');
      const hasUrlLocation = !isNaN(paramLat) && !isNaN(paramLng);

      const map = new maplibregl.default.Map({
        container: mapContainer.current!,
        style: buildDarkStyle(mapFlavor, labelSize, featureToggles),
        center: hasUrlLocation ? [paramLng, paramLat] : [-95.7, 37.1],
        zoom: hasUrlLocation ? 8 : 4, maxZoom: 14,
        minZoom: computeMinZoom(mapContainer.current!),
        renderWorldCopies: false,
        // Latitude bound stops vertical pan past the Web Mercator poles
        // (which would reveal empty space above/below the world). Longitude
        // span is kept under 360° to dodge MapLibre issue #6148; with
        // renderWorldCopies:false this never feels restrictive in practice
        // because the user already can't drag the world off-screen.
        maxBounds: [[-179.9, -85], [179.9, 85]],
        maxPitch: 60,
        // compact:true renders the attribution as its small ⓘ pill at ALL
        // screen sizes (default is responsive — expanded on wide viewports).
        // The collapseAttribution() call in the load handler forces that pill
        // to start CLOSED (MapLibre otherwise ships it open on first load).
        attributionControl: { compact: true },
        // We WANT tilt via Shift + left-drag, so dragRotate/pitchWithRotate
        // stay enabled (MapLibre couples pitch and bearing into this one
        // handler — there's no "pitch-only drag" option). The bearing the
        // gesture introduces is continuously cancelled by the "rotate"
        // listener below, so in practice the map only ever pitches and stays
        // pinned to north.
        dragRotate: true,
        pitchWithRotate: true,
      });
      // North lock. Any path that tries to change bearing — the Shift+drag
      // pitch gesture, two-finger touch twist — gets snapped back to 0 on the
      // next frame. We only call setBearing when it actually drifted, to avoid
      // fighting MapLibre's own transform each frame.
      const lockBearingNorth = () => {
        if (map.getBearing() !== 0) map.setBearing(0);
      };
      map.on("rotate", lockBearingNorth);
      // Disable the rotate component of the two-finger touch gesture (keeps
      // pinch-zoom and touch-drag-to-pitch). Belt-and-suspenders alongside
      // the bearing lock above.
      map.touchZoomRotate.disableRotation();
      // Navigation controls are provided by StormChanceUI (zoom, compass, geolocation).

      // Recompute the zoom floor on resize so a window-resize never lets the
      // user zoom out further than the world fits horizontally. MapLibre
      // clamps the current zoom up to the new minZoom automatically.
      const updateMinZoom = () => {
        if (!mapContainer.current) return;
        map.setMinZoom(computeMinZoom(mapContainer.current));
      };
      window.addEventListener("resize", updateMinZoom);
      // Stash on map so we can remove it during teardown without an extra ref.
      (map as any).__updateMinZoom = updateMinZoom;

      map.on("load", () => {
        if (!mounted) return;
        mapRef.current = map;
        (window as any).__stormMap = map; // exposed for Flutter WebView JS injection
        setMapLoaded(true);

        // Collapse the attribution pill on startup. MapLibre's compact
        // attribution control is a <details> that ships OPEN — on first layout
        // it adds both `maplibregl-compact` and `maplibregl-compact-show` and
        // sets the `open` attribute, so the full "© OpenStreetMap · MapLibre"
        // text is visible until the user clicks it. We force the collapsed
        // state by stripping `maplibregl-compact-show` and the `open` attr. The
        // user can still tap the ⓘ to expand. Done on the next frame too because
        // MapLibre's own resize/layout (which sets the open state) can run just
        // after `load`; collapsing too early would get overwritten.
        const collapseAttribution = () => {
          const el = mapContainer.current?.querySelector(
            ".maplibregl-ctrl-attrib.maplibregl-compact"
          );
          if (el) {
            el.classList.remove("maplibregl-compact-show");
            el.removeAttribute("open");
          }
        };
        collapseAttribution();
        requestAnimationFrame(collapseAttribution);

        // Initialize the resolution tier from the current zoom. We always
        // decode the full extent; the tier only sets the resolution.
        tierRef.current = computeResolutionTier(map);

        // Re-evaluate the tier after every map move/zoom. We always decode the
        // FULL extent, so pans never matter and small zooms within a tier
        // never re-decode. Only crossing a tier boundary (a large zoom change)
        // swaps resolution — at which point we kick a re-decode at the new
        // tier. Frames at the old tier stay cached (tier-keyed), so zooming
        // back is instant.
        //
        // moveend fires after both pans and zooms; we just compare tiers and
        // bail when unchanged, so pan-only moveends are essentially free.
        const handleViewportChange = () => {
          const newTier = computeResolutionTier(map);
          if (sameTier(newTier, tierRef.current)) return;
          tierRef.current = newTier;
          // Cancel in-flight decodes from the OLD tier so they don't occupy
          // pool workers the new tier needs. Their results would still cache
          // under the old tier's key and stay reusable if we zoom back, but
          // freeing the workers now makes the new tier appear faster.
          const pool = poolRef.current;
          if (pool) pool.bumpGeneration();
          // We deliberately do NOT clear frameCachesRef — the cache is tier-
          // keyed, so each tier's full-extent frames persist and returning to
          // a previously-visited tier is instant. The LRU bounds memory. The
          // progress bar reads `loadedAtWindow` (frames cached at the CURRENT
          // tier), which is 0 for a fresh tier and full again on return.
          pendingJitRef.current.forEach((s) => s.clear());
          // Trigger the rewarm effect (preload the timeline at the new tier)
          // and recompute loadedAtWindow / re-run the display effect.
          setDecodeWindowVersion((v) => v + 1);
        };
        map.on("moveend", handleViewportChange);

        // Mirror the map zoom into React state so the streamline params can
        // ease with zoom (lighter/shorter at close zoom). Seed it now and
        // update on every zoom frame. Cheap — just a setState with a number;
        // the param effect downstream is the only consumer.
        setMapZoom(map.getZoom());
        map.on("zoom", () => setMapZoom(map.getZoom()));

        // If the Flutter app passed in coords via URL params, place the marker
        // immediately at that location (no geolocation permission needed).
        // Otherwise fall back to the browser's geolocation API, which works
        // in desktop browsers but is typically blocked inside WebView.
        if (hasUrlLocation) {
          const markerEl = document.createElement("div");
          markerEl.innerHTML = `
            <div style="position:relative;width:24px;height:24px">
              <div style="position:absolute;inset:0;border-radius:50%;background:rgba(94,181,223,0.25);animation:locPulse 2s ease-in-out infinite"></div>
              <div style="position:absolute;top:6px;left:6px;width:12px;height:12px;border-radius:50%;background:#5EB5DF;border:2px solid #fff;box-shadow:0 0 8px rgba(94,181,223,0.6)"></div>
            </div>
          `;
          const MarkerCtor = maplibregl.default?.Marker ?? maplibregl.Marker;
          if (MarkerCtor) {
            new MarkerCtor({ element: markerEl, anchor: "center" })
              .setLngLat([paramLng, paramLat])
              .addTo(map);
          }
        } else if (navigator.geolocation) {
          // Fallback for desktop browsers where geolocation is available
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              const { longitude, latitude } = pos.coords;
              map.flyTo({ center: [longitude, latitude], zoom: 8, duration: 1500 });

              // Add a pulsing location marker
              const markerEl = document.createElement("div");
              markerEl.innerHTML = `
                <div style="position:relative;width:24px;height:24px">
                  <div style="position:absolute;inset:0;border-radius:50%;background:rgba(94,181,223,0.25);animation:locPulse 2s ease-in-out infinite"></div>
                  <div style="position:absolute;top:6px;left:6px;width:12px;height:12px;border-radius:50%;background:#5EB5DF;border:2px solid #fff;box-shadow:0 0 8px rgba(94,181,223,0.6)"></div>
                </div>
              `;
              const MarkerCtor = maplibregl.default?.Marker ?? maplibregl.Marker;
              if (MarkerCtor) {
                new MarkerCtor({ element: markerEl, anchor: "center" })
                  .setLngLat([longitude, latitude])
                  .addTo(map);
              }
            },
            () => { /* denied or failed — no action needed */ },
            { enableHighAccuracy: true, timeout: 10000 }
          );
        }
      });
      map.on("error", (e: any) => {
        if (e.error?.message?.includes("source")) return;
        console.warn("Map error:", e.error?.message ?? e);
      });
    })().catch((err) => { console.error("Map init error:", err); setError(err.message); });

    return () => {
      mounted = false;
      weatherLayersRef.current.forEach((layer) => layer.dispose());
      weatherLayersRef.current.clear();
      if (mapRef.current) {
        const upd = (mapRef.current as any).__updateMinZoom;
        if (upd) window.removeEventListener("resize", upd);
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // ─── Mount / swap weather layer when activeLayer changes ─────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    // Cancel in-flight decodes from the previous layer so they don't pile
    // up. We DON'T clear frame caches here — when the user switches to a
    // layer they recently viewed, those cached frames are still valid and
    // skipping re-decode makes the timeline feel instant. The cache is
    // already bounded per-sub-layer by maxCacheSize (LRU), so memory stays
    // in check without an aggressive wipe.
    //
    // Stale-frame note: keyframe lists may have advanced while a layer was
    // hidden (new TIFs ingested). The preload path below will fetch the
    // *new* manifest and request the newest keys; any cached frame whose
    // key isn't in the new list becomes harmlessly idle (LRU evicts it
    // eventually). Frames whose keys ARE in the new list are reused.
    poolRef.current?.bumpGeneration();
    pendingJitRef.current.clear();
    setLoadedCount(0);
    setFailedCount(0);

    // Dispose any existing weather layer instances (could be >1 for composite)
    weatherLayersRef.current.forEach((layer) => layer.dispose());
    weatherLayersRef.current.clear();

    // Streamlines: only for wind. Construction is deferred until AFTER the
    // raster sub-layer(s) are added below, because MapLibre's draw order
    // matches add order — and we want the wind raster painted underneath the
    // streamlines (raster is the colored speed field with low values made
    // transparent via the wspal-derived palette; streamlines are the moving
    // particles drawn on top).
    if (streamlinesRef.current) {
      streamlinesRef.current.dispose();
      streamlinesRef.current = null;
    }

    if (!activeLayer) {
      // No weather layer → drop the explicit coastline; Protomaps' implicit
      // coastline (water-over-earth edge) is plenty when nothing's covering it.
      if (map.getLayer && map.getLayer(COASTLINE_LAYER_ID)) {
        map.removeLayer(COASTLINE_LAYER_ID);
      }
      setKeyframes({}); setCurrentIdx(0);
      return;
    }

    const subKeys = resolveSubLayers(activeLayer);
    if (subKeys.length === 0) return;

    // Spin up one WeatherRasterLayer per sub-layer. Each owns its own
    // canvas/GL context, frame cache, and palette — they are completely
    // independent except that they share the scrubber index.
    // Resolve the beforeId once for this layer-swap so all weather layers
    // (raster sub-layers + streamlines) slot in at the same place: above
    // water/land, below all roads/boundaries/labels. They preserve relative
    // order among themselves because each addLayer with a beforeId inserts
    // *immediately before* that ID, so the last-added ends up on top.
    const weatherBeforeId = pickWeatherBeforeId(map);

    subKeys.forEach((subKey) => {
      const subCfg = WEATHER_LAYERS[subKey];
      if (!subCfg) {
        console.warn(`Composite references unknown sub-layer: ${subKey}`);
        return;
      }
      // Seed the cache size from config, but never shrink a size the preload
      // already raised to fit the full loop (the two effects can run in either
      // order; the larger, loop-aware value must win or the past will evict
      // and skip during playback).
      const configuredCacheSize = subCfg.maxCacheSize ?? 12;
      const existing = maxCacheSizesRef.current.get(subKey) ?? 0;
      maxCacheSizesRef.current.set(subKey, Math.max(configuredCacheSize, existing));
      // Only initialize the cache if this sub-layer doesn't already have
      // one. Re-activating a sub-layer reuses its existing cache so cached
      // frames render instantly without re-decode.
      if (!frameCachesRef.current.has(subKey)) {
        frameCachesRef.current.set(subKey, new Map());
      }
      pendingJitRef.current.set(subKey, new Set());

      const layer = new WeatherRasterLayer({
        id: `${WEATHER_LAYER_ID}-${subKey}`, config: subCfg, opacity: opacity / 100,
      });
      // addLayer accepts a CustomLayerInterface. MapLibre will call onAdd
      // with the GL context and start invoking render() each frame.
      map.addLayer(layer as any, weatherBeforeId);
      weatherLayersRef.current.set(subKey, layer);
    });

    // Re-assert the severe-alerts layer separation against the radar we just
    // added. The alerts/warnings render effects and this radar layer-add effect
    // run independently and in either order, so toggling radar off→on (with
    // alerts already on) can re-add the raster ABOVE the fills that were meant
    // to sit under it. Whenever a radar sub-layer exists, move each alert/
    // warning FILL back beneath it and lift each OUTLINE back to the top, so
    // "filled boxes below radar, outlines above radar" holds after every toggle
    // and style reload. moveLayer is a no-op-safe reorder; guarded on layer
    // existence so it does nothing when a pairing isn't currently mounted.
    {
      const radarUnderId =
        [`${WEATHER_LAYER_ID}-radar_mrms`, `${WEATHER_LAYER_ID}-radar_eumetnet`]
          .find((id) => map.getLayer(id));
      if (radarUnderId) {
        for (const fillId of ["alerts-fill", "warnings-fill"]) {
          if (map.getLayer(fillId)) map.moveLayer(fillId, radarUnderId);
        }
        // Lift outlines to the very top (moveLayer with no beforeId appends
        // above everything) so they stay above radar and above the fills.
        for (const lineId of ["alerts-outline", "warnings-outline"]) {
          if (map.getLayer(lineId)) map.moveLayer(lineId);
        }
      }
    }

    // Now add the streamlines on top of the raster (added last = drawn last
    // among the weather layers, so on top of them) but still below labels.
    if (activeLayer === "wind" && showStreamlines) {
      streamlinesRef.current = new WeatherWindStreamlines({
        // Particle density (side length; count = this²). Driven by the tuner's
        // Particle count slider via slParticleStateSize. Keep in sync with the
        // point-size rebuild site below.
        particleStateSize: slParticleStateSize,
        opacity: 1.0,
        fadeOpacity: slFadeOpacity,
        speedFactor: slSpeedFactor,
        dropRate: slDropRate,
        dropRateBump: slDropRateBump,
        pointSize: slPointSize,
        // 10% darker on the dark ("black") basemap; full brightness on light.
        colorBrightness: mapFlavor === "black" ? 0.9 : 1.0,
        windRange: [-70, 70],
      });
      // Add as a MapLibre custom layer so the draw shader receives the map's
      // own projection matrix (with tilt/rotation baked in). Without this,
      // particles drift off the basemap when the map is pitched.
      map.addLayer(streamlinesRef.current as any, weatherBeforeId);
    }

    // Coastline outline. Protomaps' `boundaries` layer specifically EXCLUDES
    // coastline features (its expression is "natural != coastline AND maritime
    // != yes") because coastlines are conventionally rendered implicitly via
    // the edge of the water polygon over the earth polygon. With our weather
    // raster painted between earth and water-edge, that implicit coastline
    // gets covered. So we explicitly stroke the water polygons here as a
    // line layer, added LAST so it sits above streamlines and weather but
    // still below labels (same beforeId).
    //
    // This also strokes inland water (lakes, large rivers) which is a nice
    // bonus — it makes the Great Lakes etc. legible against the wind field.
    addWeatherCoastline(map, weatherBeforeId);

    setLoading(true);
    let cancelled = false;
    // Fresh run: forget any park from a previous layer's load so the early-park
    // below re-evaluates against this layer's sub-layer lengths.
    parkedLenRef.current = Infinity;

    // Fetch keyframes for each sub-layer in parallel. We update the keyframes
    // map as each one returns so the first-arriving radar can start rendering
    // without waiting for its sibling.
    Promise.all(
      subKeys.map(async (subKey) => {
        const subCfg = WEATHER_LAYERS[subKey];
        const baseDownsample = subCfg.downsample ?? 1;
        const configuredCacheSize = subCfg.maxCacheSize ?? 12;
        const kfs = await fetchKeyframes(subCfg);
        if (cancelled) return;

        setKeyframes((prev) => ({ ...prev, [subKey]: kfs }));

        // ── Early park: set the scrubber to "now" the moment THIS sub-layer's
        // keyframes arrive, instead of waiting for the whole preload Promise.all
        // to resolve. The display effect re-runs on currentIdx and on every
        // decode (loadedCount), and applyFrameForIdx kicks a priority-1 JIT
        // load for the parked frame — so painting the first frame no longer
        // waits behind the entire timeline preload. We park against whichever
        // sub-layer has the shortest list seen so far (re-parking only on a
        // strictly shorter one), matching the final park's "shortest sub-layer"
        // rule. Sub-layer lists arrive in any order, so this may run once or
        // twice; the guard keeps it from thrashing on the longer list.
        if (kfs.length > 0 && kfs.length < parkedLenRef.current) {
          parkedLenRef.current = kfs.length;
          setCurrentIdx(computeParkIdx(kfs));
        }

        const pool = poolRef.current;
        if (!pool || kfs.length === 0) return;

        // Size the LRU to hold the WHOLE loop plus a little headroom. The
        // nowcast frame count varies day to day and isn't capped, so a fixed
        // cache size can be smaller than the loop — in which case the oldest
        // frames evict mid-playback and the past skips. Deriving the size from
        // the actual loop length guarantees every displayed frame stays
        // resident. (At downsample 3, ~3MB/frame, even a 48-frame loop is only
        // ~144MB — mobile-safe.) We take the max with the configured value so
        // an explicitly large config is still honored.
        const effectiveCacheSize = Math.max(configuredCacheSize, kfs.length + 4);
        maxCacheSizesRef.current.set(subKey, effectiveCacheSize);

        // ── Two-phase preload ordering ─────────────────────────────────────
        // Goal: make the loop PLAYABLE fast, then fill in history in the
        // background. We don't need all ~28 frames before the user can watch —
        // we need the recent past and the forecast first. So:
        //   Phase 1 (high priority): the last ~30 min of realtime PLUS all
        //     nowcast/forecast frames. These jump the pool queue (priority 1)
        //     so the animation around "now" is ready in ~1s.
        //   Phase 2 (normal priority): the older realtime history, newest-
        //     first, so the past fills in from recent → oldest while the user
        //     is already watching.
        // Decode the ENTIRE loop — do NOT truncate to maxCacheSize. Truncating
        // dropped the oldest frames entirely, so playing through the past hit
        // never-decoded frames and hard-skipped at any speed. We instead size
        // the cache to hold the whole loop (see maxCacheSizesRef set below),
        // so every displayed frame is present and playback stays smooth.
        const preloadBudget = kfs.length;

        // Boundary = index of the first forecast frame (or end if none). Frames
        // at idx >= boundary are the future (nowcast); frames below are past.
        let boundaryIdx = kfs.length;
        for (let i = 0; i < kfs.length; i++) {
          if (kfs[i].isForecast) { boundaryIdx = i; break; }
        }
        // "Last 30 min" of realtime at 5-min spacing ≈ 6 frames.
        const RECENT_PAST_FRAMES = 6;
        const recentPastStart = Math.max(0, boundaryIdx - RECENT_PAST_FRAMES);

        // ── Three priority bands for preload ordering ──────────────────────
        // We want the loop to become useful in the order a forecaster reads it:
        //   1) the previous ~30 min of OBSERVED radar (recent past) — highest,
        //      this is what the user stares at first when the layer opens
        //   2) the NOWCAST / forecast frames — so they can play "now → future"
        //   3) the REST of the past (older history) — fills in last, newest-
        //      first, while the user is already watching the recent loop
        // Higher priority value = dispatched sooner in the pool queue.
        //
        // recentPast: [recentPastStart, boundaryIdx), newest-first
        const recentPast = [...Array(kfs.length).keys()]
          .filter((i) => i >= recentPastStart && i < boundaryIdx)
          .sort((a, b) => b - a);
        // nowcast: [boundaryIdx, end), oldest-first so it plays now → future
        const nowcast = [...Array(kfs.length).keys()]
          .filter((i) => i >= boundaryIdx)
          .sort((a, b) => a - b);
        // olderPast: [0, recentPastStart), newest-first (recent history before
        // ancient history, so scrubbing backwards fills in nearest-first)
        const olderPast = [...Array(kfs.length).keys()]
          .filter((i) => i < recentPastStart)
          .sort((a, b) => b - a);

        // Map each index to its band priority. Recent past 3, nowcast 2,
        // older past 1. (The parked frame is decoded+awaited separately below,
        // ahead of all of these, so it doesn't need its own priority tier.)
        const PRIORITY_RECENT_PAST = 3;
        const PRIORITY_NOWCAST = 2;
        const PRIORITY_OLDER_PAST = 1;
        const priorityByIdx = new Map<number, number>();
        for (const i of recentPast) priorityByIdx.set(i, PRIORITY_RECENT_PAST);
        for (const i of nowcast) priorityByIdx.set(i, PRIORITY_NOWCAST);
        for (const i of olderPast) priorityByIdx.set(i, PRIORITY_OLDER_PAST);

        // Full decode order, respecting the per-sub-layer cache budget.
        const ordered = [...recentPast, ...nowcast, ...olderPast].slice(0, preloadBudget);
        // The single parked "now" frame: the newest recent-past frame (i.e. the
        // last observed frame before the forecast boundary) when there is a
        // past, else the earliest frame. computeParkIdx already encodes this,
        // so reuse it instead of re-deriving.
        const parkFrameIdx = computeParkIdx(kfs);

        // Token strategy: pass the SUPPLIER (getCogToken), not a resolved
        // string. The pool resolves it at DISPATCH time per job. Two reasons:
        // (1) enqueueing stays fully synchronous in one tick, which the pool's
        // priority queue ordering depends on (no per-frame await here); and
        // (2) a token resolved once up front goes STALE for jobs deep in the
        // queue — the token TTL is 5 minutes and a full composite preload
        // (2 sub-layers x ~28-40 frames through 3 workers) can approach or
        // exceed that on slow devices/networks. Every 403 from a stale token
        // was a frame that never entered the cache, freezing the progress bar
        // at a fixed percentage. getCogToken caches and self-refreshes when
        // <30s of validity remain, so per-dispatch resolution is ~free.

        // Decode one frame: full-extent, fixed resolution, tier-keyed cache.
        // `priority` orders the band (recent past > nowcast > older past) in
        // the pool queue.
        const decodeFrame = (idx: number) => {
          const kf = kfs[idx];
          const tier = tierRef.current;
          const ck = frameCacheKey(kf.key, tier);
          const cache = frameCachesRef.current.get(subKey);
          if (cache?.has(ck)) {
            setLoadedCount((c) => c + 1);
            return Promise.resolve();
          }
          const priority = priorityByIdx.get(idx) ?? 0;
          const attempt = () =>
            pool.decode(kf.key, COG_API, tierDownsample(tier, baseDownsample), null, priority, getCogToken);
          return attempt()
            .then((frame: DecodedFrame) => {
              if (cancelled) return;
              insertIntoCache(subKey, ck, frame);
              setLoadedCount((c) => c + 1);
            })
            .catch((err) => {
              if (err.message === "cancelled") return;
              // One retry, re-enqueued at the same priority with a token
              // minted at ITS dispatch. This absorbs the transient failure
              // classes seen in the wild: a hung range request (the worker's
              // raced fallback usually rescues it, but the 30s watchdog can
              // still fire first on a slow connection), a burst 429/5xx from
              // the edge, and an expired token (403). Without a retry, one
              // transient failure permanently froze the progress bar, because
              // the bar counts CACHED frames — a failed frame never advanced
              // it, no matter what loadedCount said.
              console.warn(`Decode failed for ${kf.key} (${err.message}); retrying once`);
              return attempt()
                .then((frame: DecodedFrame) => {
                  if (cancelled) return;
                  insertIntoCache(subKey, ck, frame);
                  setLoadedCount((c) => c + 1);
                })
                .catch((err2) => {
                  if (err2.message === "cancelled") return;
                  console.warn(`Decode failed for ${kf.key} after retry:`, err2.message);
                  setLoadedCount((c) => c + 1);
                  setFailedCount((c) => c + 1);
                });
            });
        };

        // ── Parked frame first, ALONE ──────────────────────────────────────
        // The root cause of slow first paint: this is a COMPOSITE layer (MRMS +
        // EUMETNET), so two sub-layers each enqueue ~28 frames into ONE 3-worker
        // pool — 56 frames competing for 3 slots. Even with priority, the single
        // frame the user actually needs first (the parked "now" frame) ends up
        // behind a flood of sibling frames, so first paint waited ~7.7s (≈3
        // decode rounds) instead of ~1 decode (~2.4s).
        //
        // Fix: decode JUST the parked frame first and await it. Because each
        // decode is a fixed ~2.4s full-raster read (we are deliberately NOT
        // changing that — see the decode-once/full-extent cache design), the
        // only lever for faster first paint is making sure the parked frame is
        // the FIRST thing the pool touches, not the 4th. The display effect
        // (driven by loadedCount) paints it the moment it lands. THEN we enqueue
        // the rest of the loop to fill in around it.
        if (parkFrameIdx >= 0) {
          await decodeFrame(parkFrameIdx);
        }
        if (cancelled) return;
        // Enqueue everything else (skip the already-decoded parked frame).
        ordered.filter((idx) => idx !== parkFrameIdx).forEach(decodeFrame);
      }),
    ).then(() => {
      if (cancelled) return;
      setLoading(false);
      // Authoritative re-park once ALL sub-layers have loaded and their final
      // lengths are known. The early-park above already set the scrubber to
      // "now" against the shortest list seen so far for fast first paint; this
      // reconciles in case lists arrived out of order and the true shortest
      // came in later. Reuses computeParkIdx so it matches the early-park
      // exactly — if the index is already correct this is a no-op. Read current
      // keyframes via the setter to avoid a stale closure.
      setKeyframes((cur) => {
        const shortestKey = subKeys.reduce((best, k) => {
          const len = cur[k]?.length ?? 0;
          const bestLen = cur[best]?.length ?? Infinity;
          return len > 0 && len < bestLen ? k : best;
        }, subKeys[0]);
        const kfs = cur[shortestKey] ?? [];
        setCurrentIdx(computeParkIdx(kfs));
        return cur;
      });
    }).catch((err) => {
      // A rejection anywhere in the per-sub-layer chain (keyframe manifest
      // fetch, token mint, an unexpected throw) previously skipped
      // setLoading(false) entirely, leaving the loading UI up forever with
      // no error surfaced — indistinguishable from a preload stall.
      console.error("Layer preload failed:", err);
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
    // styleEpoch: after a basemap setStyle() the custom weather layers are gone,
    // so re-run this to rebuild them (cached frames make the re-decode fast).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLayer, mapLoaded, styleEpoch]);

  // ─── Cache helpers: per-sub-layer LRU with count AND byte-budget eviction ───
  // Each sub-layer has its own cache (frameCachesRef map → key → WeatherFrame)
  // and its own maxCacheSize. This keeps the radar_mrms cache from evicting
  // radar_eumetnet frames just because the combined total went over 12.
  //
  // WHY THE BYTE BUDGET EXISTS: sizing the cache by frame COUNT alone let
  // total resident bytes float with frame dimensions and loop length, and on
  // iOS the WebKit web-content process is hard-killed at ~1.5 GB resident
  // (confirmed via a JetsamEvent showing WebContent at exactly 1536 MB ~30 s
  // into radar preload). The budget caps the bytes a sub-layer's frames can
  // pin regardless of how many frames the loop has or how large each frame
  // decodes; over-budget frames evict LRU-first and simply re-decode on
  // demand if scrubbed back to. A small entry-count floor keeps the
  // currently-displayed pair and its temporal neighbors resident even if a
  // single frame is enormous.
  // Device-aware: the 96 MB figure was sized for iOS's ~1.5 GB jetsam limit
  // (2 radar sub-layers x 96 MB of raw frames + their GPU textures + the rest
  // of the app leaves comfortable headroom there). Applying the SAME budget on
  // desktop was needlessly strict — a loop of large frames overflowed it,
  // eviction ran during preload, and playback lost its resident window (the
  // "bar full but animation frozen" failure). Desktop browsers routinely give
  // a tab multiple GB; 256 MB per sub-layer keeps even large loops fully
  // resident there while iOS keeps its proven-safe cap.
  const PER_SUB_CACHE_BYTE_BUDGET = (IS_IOS_FAMILY ? 96 : 256) * 1024 * 1024;
  // WHY A GLOBAL BUDGET IS ALSO NEEDED: the per-sub-layer budget above bounds
  // ONE sub-layer, but frameCachesRef holds a SEPARATE cache per sub-layer and
  // deliberately never drops them on layer switch (tier/layer re-visits stay
  // instant). There are 13 real sub-layers; a user who browses radar (2 subs)
  // -> wind -> temperature -> dewpoint -> satellite... accumulates one budget
  // PER SUB-LAYER VISITED, with nothing capping the sum. At 96 MB each that
  // crosses a gigabyte of raw frame data after six or seven layers, on top of
  // GPU textures, decoded worker buffers and MapLibre's own tiles — which is
  // exactly the ~1.5 GB WebKit jetsam ceiling. Desktop and Android survive it
  // (multi-GB tabs, and Android Chrome reclaims rather than hard-kills); iOS
  // does not, which is why the crash reads as iOS-only. The per-sub-layer cap
  // alone can never prevent this because it has no visibility into the total.
  //
  // The global cap is the real ceiling; the per-sub-layer cap remains as the
  // shape-keeper (no single layer may hog the whole allowance).
  const TOTAL_CACHE_BYTE_BUDGET = (IS_IOS_FAMILY ? 220 : 700) * 1024 * 1024;
  const CACHE_MIN_ENTRIES = 4; // never byte-evict below this many frames
  const frameBytes = (f: WeatherFrame) => f.rawData.byteLength;
  const cacheBytes = (cache: Map<string, WeatherFrame>) => {
    let n = 0;
    cache.forEach((f) => { n += frameBytes(f); });
    return n;
  };
  // Sub-layers backing what is on screen right now. Their caches are the last
  // thing the global sweep touches.
  const activeSubKeysRef = useRef<string[]>([]);
  const globalEvictWarnedRef = useRef(false);

  /** Enforce TOTAL_CACHE_BYTE_BUDGET across every sub-layer cache.
   *
   * Eviction order is by usefulness, cheapest-to-lose first:
   *   1. Whole caches of INACTIVE sub-layers (layers not currently displayed),
   *      oldest-touched first. Dropping these is invisible — revisiting the
   *      layer re-preloads exactly as a first visit does.
   *   2. Only if still over budget, frames from ACTIVE sub-layers, farthest
   *      from the playhead first (same policy as the per-sub-layer evictor),
   *      never below CACHE_MIN_ENTRIES so the displayed pair survives.
   * Returns bytes freed. */
  const enforceGlobalBudget = useCallback(() => {
    const caches = frameCachesRef.current;
    let total = 0;
    caches.forEach((c) => { total += cacheBytes(c); });
    if (total <= TOTAL_CACHE_BYTE_BUDGET) return 0;

    const before = total;
    const active = new Set(activeSubKeysRef.current);
    const order = subLayerTouchOrderRef.current;
    // Inactive sub-layers, least-recently-active first.
    const inactive = [...caches.keys()]
      .filter((k) => !active.has(k))
      .sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));

    for (const subKey of inactive) {
      if (total <= TOTAL_CACHE_BYTE_BUDGET) break;
      const c = caches.get(subKey);
      if (!c || c.size === 0) continue;
      total -= cacheBytes(c);
      caches.delete(subKey);
      decodedOnceRef.current.delete(subKey); // progress for a dropped layer resets too
      byteBudgetWarnedRef.current.delete(subKey);
    }

    if (total > TOTAL_CACHE_BYTE_BUDGET) {
      const playhead = playheadIdxRef.current;
      for (const subKey of activeSubKeysRef.current) {
        const c = caches.get(subKey);
        if (!c) continue;
        const idxMap = kfIndexRef.current.get(subKey);
        const byDistance = [...c.keys()]
          .map((k) => {
            const idx = idxMap?.get(k.split("::")[0]);
            return { k, dist: idx === undefined ? Infinity : Math.abs(idx - playhead) };
          })
          .sort((a, b) => b.dist - a.dist);
        for (const { k } of byDistance) {
          if (total <= TOTAL_CACHE_BYTE_BUDGET || c.size <= CACHE_MIN_ENTRIES) break;
          const f = c.get(k);
          if (!f) continue;
          c.delete(k);
          total -= frameBytes(f);
        }
      }
    }

    if (!globalEvictWarnedRef.current) {
      globalEvictWarnedRef.current = true;
      const mb = (n: number) => Math.round(n / (1024 * 1024));
      console.warn(
        `[frameCache] GLOBAL budget enforced: ${mb(before)}MB -> ${mb(total)}MB ` +
        `(cap ${mb(TOTAL_CACHE_BYTE_BUDGET)}MB, ${IS_IOS_FAMILY ? "iOS" : "desktop"} profile). ` +
        `Inactive layer caches are released first; revisiting them re-preloads.`
      );
    }
    return before - total;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const insertIntoCache = useCallback((subKey: string, key: string, frame: WeatherFrame) => {
    let cache = frameCachesRef.current.get(subKey);
    if (!cache) {
      cache = new Map();
      frameCachesRef.current.set(subKey, cache);
    }
    // Key includes the decode window the frame was produced at (the caller
    // passes an already-composed key — see ensureFrameLoaded). If already
    // present, delete so re-insert moves it to the newest end.
    if (cache.has(key)) cache.delete(key);
    cache.set(key, frame);
    // Progress registry: this frame has been decoded, whatever the LRU or
    // byte budget later decide about keeping it resident.
    let seen = decodedOnceRef.current.get(subKey);
    if (!seen) { seen = new Set(); decodedOnceRef.current.set(subKey, seen); }
    seen.add(key);
    // Evict oldest entries until under the entry-count cap…
    const max = maxCacheSizesRef.current.get(subKey) ?? 12;
    while (cache.size > max) {
      const firstKey = cache.keys().next().value;
      if (firstKey === undefined) break;
      cache.delete(firstKey);
    }
    // …then until under the byte budget. Summing on every insert is cheap
    // (≤ ~40 entries) and self-healing — no stateful byte counter to desync.
    let totalBytes = 0;
    for (const f of cache.values()) totalBytes += frameBytes(f);
    // Evict by DISTANCE FROM THE PLAYHEAD, farthest first — not insertion
    // order. Insertion-order (LRU) eviction interacted catastrophically with
    // the preload: the highest-priority frames (the "now" loop) are decoded
    // FIRST, so when the loop outgrew the budget they were also evicted
    // FIRST, and preload ended with only distant history resident. Playback
    // expands outward from the parked frame through RESIDENT frames only
    // (loadedBlockAroundNow), so it found a 1-frame block and the animation
    // froze even though every decode had succeeded and the bar read 100%.
    // Distance eviction guarantees the resident set is always the window
    // AROUND the playhead — exactly the frames playback needs next. Frames
    // whose key isn't in the current manifest (stale tiers/manifests) sort as
    // infinitely far and go first.
    let byteEvictions = 0;
    if (totalBytes > PER_SUB_CACHE_BYTE_BUDGET && cache.size > CACHE_MIN_ENTRIES) {
      const playhead = playheadIdxRef.current;
      const idxMap = kfIndexRef.current.get(subKey);
      const byDistance = [...cache.keys()]
        .map((k) => {
          const kfKey = k.split("::")[0];
          const idx = idxMap?.get(kfKey);
          return { k, dist: idx === undefined ? Infinity : Math.abs(idx - playhead) };
        })
        .sort((a, b) => b.dist - a.dist);
      for (const { k } of byDistance) {
        if (totalBytes <= PER_SUB_CACHE_BYTE_BUDGET || cache.size <= CACHE_MIN_ENTRIES) break;
        const evicted = cache.get(k);
        if (evicted === undefined) continue;
        cache.delete(k);
        totalBytes -= frameBytes(evicted);
        byteEvictions++;
      }
    }
    // Global ceiling across ALL sub-layer caches — the per-sub-layer loop
    // above cannot see the total, and it is the total that iOS kills for.
    enforceGlobalBudget();

    // One-shot diagnostic per sub-layer: if the byte budget is evicting, the
    // loop does NOT fit in memory and cache-derived counts will plateau at
    // ~floor(budget / frameBytes) frames. Logged once with the numbers needed
    // to verify, so a "progress frozen at N% with zero network errors" report
    // is attributable in a single console glance.
    if (byteEvictions > 0 && !byteBudgetWarnedRef.current.has(subKey)) {
      byteBudgetWarnedRef.current.add(subKey);
      const mb = (n: number) => (n / (1024 * 1024)).toFixed(1);
      console.warn(
        `[frameCache] byte budget active for "${subKey}": frame=${mb(frameBytes(frame))}MB, ` +
        `budget=${mb(PER_SUB_CACHE_BYTE_BUDGET)}MB -> ~${Math.max(CACHE_MIN_ENTRIES, Math.floor(PER_SUB_CACHE_BYTE_BUDGET / Math.max(1, frameBytes(frame))))} ` +
        `frames resident, kept CENTERED ON THE PLAYHEAD; frames beyond that window re-decode on demand when scrubbed to`
      );
    }
  }, []);

  // Mark an entry as recently used (moves to newest end of LRU).
  const touchInCache = useCallback((subKey: string, key: string) => {
    const cache = frameCachesRef.current.get(subKey);
    if (!cache) return;
    const v = cache.get(key);
    if (v !== undefined) {
      cache.delete(key);
      cache.set(key, v);
    }
  }, []);

  const ensureFrameLoaded = useCallback((subKey: string, kfKey: string, priority = 0) => {
    const tier = tierRef.current;
    const ck = frameCacheKey(kfKey, tier);
    const cache = frameCachesRef.current.get(subKey);
    if (cache?.has(ck)) return;
    let pending = pendingJitRef.current.get(subKey);
    if (!pending) {
      pending = new Set();
      pendingJitRef.current.set(subKey, pending);
    }
    // Dedup on the composed key so the same frame at two different tiers can
    // be in flight simultaneously without colliding.
    if (pending.has(ck)) return;
    const pool = poolRef.current;
    if (!pool) return;
    const cfg = WEATHER_LAYERS[subKey];
    if (!cfg) return;
    const baseDownsample = cfg.downsample ?? 1;
    pending.add(ck);
    // Full-extent decode (bbox = null) at this tier's resolution.
    getCogToken()
      .then((token) =>
        pool.decode(kfKey, COG_API, tierDownsample(tier, baseDownsample), null, priority, token)
      )
      .then((frame) => {
        insertIntoCache(subKey, ck, frame);
        setLoadedCount((c) => c + 1);
      })
      .catch((err) => {
        if (err.message === "cancelled") return;
        console.warn(`JIT decode failed for ${kfKey}:`, err.message);
      })
      .finally(() => {
        pending?.delete(ck);
      });
  }, [insertIntoCache]);

  // ─── Drive frame display from currentIdx ─────────────────────────────────
  // currentIdx is fractional (e.g. 3.4) — we pick frame floor(idx) and
  // frame floor(idx)+1 as frame0/frame1, and pass the fractional part as time.
  //
  // For composite layers this runs once per sub-layer: each sub-layer
  // independently picks its own frame0/frame1 from its own keyframe list at
  // the same scrubber index. The longer sub-layer is clamped at its last
  // frame once the shorter one has reached its tail (set by scrubberLength
  // above).
  const applyFrameForIdx = useCallback((idx: number) => {
    if (subLayerKeys.length === 0) return;

    subLayerKeys.forEach((subKey) => {
      const layer = weatherLayersRef.current.get(subKey);
      if (!layer) return;
      const kfs = keyframes[subKey];
      if (!kfs || kfs.length === 0) return;

      const clamped = Math.max(0, Math.min(kfs.length - 1, idx));
      const i0 = Math.floor(clamped);
      const i1 = Math.min(kfs.length - 1, i0 + 1);
      const t = clamped - i0;

      const k0 = kfs[i0].key;
      const k1 = kfs[i1].key;
      const tier = tierRef.current;
      const ck0 = frameCacheKey(k0, tier);
      const ck1 = frameCacheKey(k1, tier);
      const cache = frameCachesRef.current.get(subKey);
      const f0 = cache?.get(ck0);
      const f1 = cache?.get(ck1);

      // Touch any hits so they move to the newest end of the LRU
      if (f0) touchInCache(subKey, ck0);
      if (f1 && k1 !== k0) touchInCache(subKey, ck1);

      // Kick off JIT loads for misses (and a small lookahead for smooth
      // scrubbing). The two frames being displayed right now (k0/k1) get
      // priority 1 so they jump ahead of any preload/rewarm backlog in the
      // pool queue — this is what keeps the visible frame from waiting 2-3s
      // behind a full timeline re-decode after a zoom. The lookahead frame
      // stays at normal priority.
      if (!f0) ensureFrameLoaded(subKey, k0, 1);
      if (!f1) ensureFrameLoaded(subKey, k1, 1);
      const i2 = Math.min(kfs.length - 1, i0 + 2);
      if (i2 !== i1) ensureFrameLoaded(subKey, kfs[i2].key);

      // Wait for at least one frame to be decoded before showing anything
      if (!f0 && !f1) return;

      const frame0 = f0 ?? f1!;
      const frame1 = f1 ?? f0!;

      // Multi-frame temporal smoothing: when the layer config asks for it,
      // also fetch and feed in the surrounding frames (N-1 and N+2) so the
      // shader can blend across a wider temporal window. This produces the
      // motion-blur effect that masks the visible "snap" between widely-
      // spaced MRMS frames. Other layers fall through to plain setFrames.
      const cfg = WEATHER_LAYERS[subKey];
      if (cfg?.temporalBlurWidth && cfg.temporalBlurWidth > 1.0
          && (layer as any).setTemporalFrames) {
        const iPrev = Math.max(0, i0 - 1);
        const iNext = Math.min(kfs.length - 1, i0 + 2);
        const ckPrev = frameCacheKey(kfs[iPrev].key, tier);
        const ckNext = frameCacheKey(kfs[iNext].key, tier);
        const fPrev = iPrev !== i0 ? cache?.get(ckPrev) : null;
        const fNext = iNext !== i1 ? cache?.get(ckNext) : null;
        // Touch prev/next so they stay hot in the LRU during playback;
        // also kick JIT loads if missing so they're available next tick.
        if (fPrev) touchInCache(subKey, ckPrev);
        else if (iPrev !== i0) ensureFrameLoaded(subKey, kfs[iPrev].key);
        if (fNext) touchInCache(subKey, ckNext);
        else if (iNext !== i1) ensureFrameLoaded(subKey, kfs[iNext].key);
        (layer as any).setTemporalFrames(fPrev ?? null, frame0, frame1, fNext ?? null);
      } else {
        layer.setFrames(frame0, frame1);
      }
      layer.setTime(t);

      // Streamlines: only wind drives this, and wind is never a composite
      // sub-layer, so it's safe to feed this unconditionally here.
      if (subKey === "wind") {
        streamlinesRef.current?.setWindFrame(frame0);
      }
    });
  }, [subLayerKeys, keyframes, touchInCache, ensureFrameLoaded]);

  useEffect(() => { applyFrameForIdx(currentIdx); }, [currentIdx, applyFrameForIdx, loadedCount, decodeWindowVersion]);

  useEffect(() => { playheadIdxRef.current = currentIdx; }, [currentIdx]);
  // Track which sub-layers are on screen, stamp them as most-recently-used,
  // and sweep immediately on switch. Sweeping HERE (not only on insert) is
  // what actually frees memory when a user browses layers: the layer just left
  // becomes inactive at this moment, and on iOS we want its frames gone before
  // the incoming layer starts allocating its own, not after both are resident.
  useEffect(() => {
    activeSubKeysRef.current = subLayerKeys;
    for (const k of subLayerKeys) {
      subLayerTouchOrderRef.current.set(k, ++touchSeqRef.current);
    }
    enforceGlobalBudget();
  }, [subLayerKeys, enforceGlobalBudget]);
  useEffect(() => {
    const m = new Map<string, Map<string, number>>();
    for (const [subKey, kfs] of Object.entries(keyframes)) {
      const idx = new Map<string, number>();
      (kfs ?? []).forEach((kf: any, i: number) => idx.set(kf.key, i));
      m.set(subKey, idx);
    }
    kfIndexRef.current = m;
  }, [keyframes]);

  // Field debugging: window.__scDebug reflects live preload state so a stuck
  // report can be diagnosed by pasting one line in the console. Cheap (runs
  // only when a decode lands or fails) and inert for normal users.
  useEffect(() => {
    const caches: Record<string, { frames: number; mb: number; decodedOnce: number }> = {};
    frameCachesRef.current.forEach((cache, subKey) => {
      let bytes = 0;
      cache.forEach((f) => { bytes += f.rawData.byteLength; });
      caches[subKey] = {
        frames: cache.size,
        mb: Math.round(bytes / (1024 * 1024)),
        decodedOnce: decodedOnceRef.current.get(subKey)?.size ?? 0,
      };
    });
    let totalMb = 0;
    for (const v of Object.values(caches)) totalMb += v.mb;
    (window as any).__scDebug = {
      profile: IS_IOS_FAMILY ? "ios" : "desktop",
      totalCacheMb: totalMb,
      totalCapMb: Math.round(TOTAL_CACHE_BYTE_BUDGET / (1024 * 1024)),
      perSubCapMb: Math.round(PER_SUB_CACHE_BYTE_BUDGET / (1024 * 1024)),
      activeSubLayers: activeSubKeysRef.current,
      loadedCount, failedCount, loadedAtWindow, totalExpectedFrames, caches,
    };
  }, [loadedCount, failedCount, loadedAtWindow, totalExpectedFrames]);

  // ─── Re-warm cache after a resolution-tier change ────────────────────────
  // When a zoom crosses a tier boundary, handleViewportChange bumps
  // decodeWindowVersion. The initial preload lives in the layer-init effect
  // (keyed on activeLayer) and does NOT re-run here, so without this effect
  // only the JIT path refills frames at the new tier — leaving the rest of the
  // timeline blank until playback sweeps across it. This mirrors that preload:
  // newest/nearest-first, capped per sub-layer, full-extent at the new tier.
  // Skipped on the initial mount (version 0) since layer-init covers it.
  useEffect(() => {
    if (decodeWindowVersion === 0) return;
    const pool = poolRef.current;
    if (!pool || subLayerKeys.length === 0) return;

    subLayerKeys.forEach((subKey) => {
      const subCfg = WEATHER_LAYERS[subKey];
      if (!subCfg) return;
      const kfs = keyframes[subKey];
      if (!kfs || kfs.length === 0) return;

      // Center the preload on the parked playhead rather than the newest
      // frame, so the frames nearest what the user is looking at come back
      // first after a tier change. Load the whole loop (no truncation) — same
      // reasoning as the initial preload: a truncated set leaves gaps that
      // skip during playback.
      const centerIdx = Math.max(0, Math.min(kfs.length - 1, Math.round(currentIdx)));
      const order = [...Array(kfs.length).keys()]
        .sort((a, b) => Math.abs(a - centerIdx) - Math.abs(b - centerIdx));

      order.forEach((idx) => {
        const kf = kfs[idx];
        const cache = frameCachesRef.current.get(subKey);
        // Tier-aware hit check: a frame cached at a DIFFERENT tier doesn't
        // count — we need it at the current tier. ensureFrameLoaded repeats
        // this check authoritatively; this just avoids the call overhead.
        if (cache?.has(frameCacheKey(kf.key, tierRef.current))) return;
        // ensureFrameLoaded handles pending-dedup and cache insertion, and
        // bumps loadedCount on success so the display effect re-runs.
        ensureFrameLoaded(subKey, kf.key);
      });
    });
    // currentIdx intentionally omitted: fire on tier change, not every tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decodeWindowVersion, subLayerKeys, keyframes, ensureFrameLoaded]);

  // ─── Keyframe auto-refresh (the "left it open all day" fix) ───────────────
  //
  // THE BUG: the layer-init effect above is keyed on [activeLayer, mapLoaded,
  // styleEpoch]. It fetches /manifest exactly once per layer selection and then
  // never again. Leave the map open on a phone and the keyframe list is frozen
  // at whatever was current when you picked the layer — the radar loop keeps
  // animating, but it animates the same twenty stale frames forever. Only a
  // reload (or a layer switch, which re-runs the init effect) pulls new data.
  //
  // THE FIX: poll the manifest on a cadence derived from the product's own
  // update rate, and swap the keyframe list in place. Deliberately NOT done by
  // bumping styleEpoch / re-running the init effect, because that tears down
  // and reallocates the WebGL layers, resets loadedCount, and puts the loading
  // UI back up — a visible blink every minute on an idle map. Here we only
  // touch the data:
  //
  //   1. Re-fetch keyframes for the active sub-layers.
  //   2. Bail out completely if nothing changed (the common case — no state
  //      update, no re-render, no re-decode).
  //   3. If frames DID land, swap the list, remap the playhead so the user
  //      doesn't get yanked, and warm the new frames.
  //
  // Nothing ever blanks: applyFrameForIdx holds the previously-decoded frame
  // on screen until at least one frame for the new index is in cache
  // ("if (!f0 && !f1) return"), so the new frame fades in when it's ready
  // rather than flashing empty.
  const keyframesRef = useRef(keyframes);
  useEffect(() => { keyframesRef.current = keyframes; }, [keyframes]);
  const currentIdxRef = useRef(currentIdx);
  useEffect(() => { currentIdxRef.current = currentIdx; }, [currentIdx]);

  useEffect(() => {
    if (!mapLoaded || !activeLayer || subLayerKeys.length === 0) return;

    const intervalMs = refreshIntervalForSubLayers(subLayerKeys);
    let cancelled = false;
    let inflight = false;

    const check = async () => {
      if (cancelled || inflight) return;
      // Don't poll a hidden tab. The wakeEpoch dep re-runs this whole effect
      // the moment the user comes back, which fires an immediate check —
      // that's the path that actually matters on a phone.
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;

      inflight = true;
      try {
        const fresh: Record<string, any[]> = {};
        await Promise.all(
          subLayerKeys.map(async (subKey) => {
            const cfg = WEATHER_LAYERS[subKey];
            if (!cfg) return;
            fresh[subKey] = await fetchKeyframes(cfg);
          }),
        );
        if (cancelled) return;

        const prev = keyframesRef.current;

        // ── Change detection ────────────────────────────────────────────────
        // An empty result means the fetch failed (fetchKeyframesForPrefix
        // swallows errors and returns []). Treat that as "no news" rather than
        // "everything disappeared" — otherwise one flaky request wipes a
        // working loop off the screen.
        const changed = subLayerKeys.some((k) => {
          const a = prev[k] ?? [];
          const b = fresh[k] ?? [];
          if (b.length === 0) return false;
          if (a.length !== b.length) return true;
          return a[a.length - 1]?.key !== b[b.length - 1]?.key;
        });
        if (!changed) return;

        // Drop any sub-layer that came back empty so a partial failure can't
        // clear a list that's still good.
        for (const k of Object.keys(fresh)) {
          if (!fresh[k] || fresh[k].length === 0) delete fresh[k];
        }
        if (Object.keys(fresh).length === 0) return;

        // ── Playhead remap ──────────────────────────────────────────────────
        // Index math runs against the SHORTEST sub-layer, matching the rule
        // the init effect and scrubberLength already use.
        const primary = subLayerKeys.reduce((best, k) => {
          const len = (fresh[k] ?? prev[k] ?? []).length;
          const bestLen = (fresh[best] ?? prev[best] ?? []).length || Infinity;
          return len > 0 && len < bestLen ? k : best;
        }, subLayerKeys[0]);

        const oldKfs = prev[primary] ?? [];
        const newKfs = fresh[primary] ?? oldKfs;
        const oldIdx = Math.round(currentIdxRef.current);

        let nextIdx: number;
        if (oldKfs.length === 0 || oldIdx === computeParkIdx(oldKfs)) {
          // The user was parked at "now" (the default state for a map sitting
          // untouched). Advance them to the NEW "now" — this is the whole
          // point of the refresh.
          nextIdx = computeParkIdx(newKfs);
        } else {
          // The user has scrubbed somewhere specific, or is watching a loop
          // play. Keep them on the same FRAME, not the same index — the
          // realtime list is capped at 20, so a new frame arriving shifts
          // every index down by one and a naive index carry-over would jump
          // them forward in time.
          const oldKey = oldKfs[oldIdx]?.key;
          const byKey = oldKey ? newKfs.findIndex((f: any) => f.key === oldKey) : -1;
          if (byKey >= 0) {
            nextIdx = byKey;
          } else {
            // Their frame aged off the head of the list. Fall back to
            // correcting by however many frames were dropped from the front.
            const shift = oldKfs.findIndex((f: any) => f.key === newKfs[0]?.key);
            nextIdx = shift > 0 ? oldIdx - shift : oldIdx;
          }
          nextIdx = Math.max(0, Math.min(newKfs.length - 1, nextIdx));
        }

        // ── Cache budget ────────────────────────────────────────────────────
        // Same sizing rule as the init preload: never let the LRU be smaller
        // than the loop, or the oldest frames evict mid-playback and the past
        // skips. Only ever grows, never shrinks a size the preload raised.
        for (const subKey of subLayerKeys) {
          const kfs = fresh[subKey];
          if (!kfs?.length) continue;
          const configured = WEATHER_LAYERS[subKey]?.maxCacheSize ?? 12;
          const existing = maxCacheSizesRef.current.get(subKey) ?? 0;
          maxCacheSizesRef.current.set(
            subKey, Math.max(existing, configured, kfs.length + 4),
          );
        }

        setKeyframes((prevKf) => ({ ...prevKf, ...fresh }));
        setCurrentIdx(nextIdx);

        // ── Warm the arrivals ───────────────────────────────────────────────
        // Newest-first at elevated priority so the frame the user is about to
        // be parked on decodes ahead of any backlog. ensureFrameLoaded dedupes
        // against pending jobs and the tier-keyed cache, so re-requesting a
        // frame we already hold is free. (The display effect also kicks a
        // priority-1 JIT load for the parked frame itself — this just gets the
        // rest of the new tail in behind it.)
        for (const subKey of subLayerKeys) {
          const kfs = fresh[subKey];
          if (!kfs?.length) continue;
          const prevKeys = new Set((prev[subKey] ?? []).map((f: any) => f.key));
          for (let i = kfs.length - 1; i >= 0; i--) {
            if (!prevKeys.has(kfs[i].key)) ensureFrameLoaded(subKey, kfs[i].key, 2);
          }
        }
      } catch (err) {
        // A failed refresh is a no-op by design: the existing loop keeps
        // playing on the frames already in cache and we try again next tick.
        console.warn("Keyframe auto-refresh failed (keeping current frames):", err);
      } finally {
        inflight = false;
      }
    };

    // wakeEpoch changing re-runs this effect, so THIS call is the
    // "user came back to the tab" refresh. On first mount it fires right
    // after the init effect's own fetch and finds nothing new (manifest is
    // still inside its 5s TTL), so it costs nothing.
    check();

    const timer = window.setInterval(check, intervalMs);
    return () => { cancelled = true; clearInterval(timer); };
  }, [mapLoaded, activeLayer, subLayerKeys, wakeEpoch, ensureFrameLoaded]);


  // ─── Opacity ──────────────────────────────────────────────────────────────
  useEffect(() => {
    // Both the wind raster (colored speed field with calm air made
    // transparent via the wspal palette) AND the streamlines on top get the
    // user's opacity. Previously this branch zero'd the raster so only the
    // particles were visible, but now the raster acts as the speed-magnitude
    // backdrop the streamlines fly over.
    // "Hide Precip": when active, force the radar raster fully transparent so
    // only the overlays (lightning/warnings/alerts) remain. activeLayer stays
    // "radar", so the legend, timeline and overlays are unaffected. Other
    // layers ignore hidePrecip entirely.
    const precipHidden = hidePrecip && activeLayer === "radar";
    const effOpacity = precipHidden ? 0 : opacity / 100;
    weatherLayersRef.current.forEach((layer) => layer.setOpacity(effOpacity));
    streamlinesRef.current?.setOpacity(opacity / 100);
  }, [opacity, activeLayer, showStreamlines, hidePrecip]);

  // Tell every active sub-layer whether the playhead is being driven by the
  // playback loop. The shader uses this to raise the noise-floor alpha cut
  // during playback so light-precip pixels don't pulse as they cross the
  // LUT ramp between frames. Re-broadcast when layers come/go too.
  useEffect(() => {
    weatherLayersRef.current.forEach((layer) => layer.setAnimating(playing));
  }, [playing, activeLayer]);

  // ─── METAR observation pills ──────────────────────────────────────────────
  // Real surface observations (actual station thermometer readings) rendered
  // as rounded pills — temperature over station name — whenever a layer from
  // the Air temperature family is active. Data comes from our Worker's
  // /metars proxy (edge-cached AWC METAR feed); see weatherMetarPills.ts.

  // One-time wiring + style-swap resilience. attachMetarPills is idempotent:
  // re-calling after a styleEpoch bump restores the source/layer/pill image
  // that setStyle wiped, without duplicating the moveend listener or timer.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    attachMetarPills(map, COG_API, getCogToken);
  }, [mapLoaded, styleEpoch]);

  // Enable/disable + value mode with the active layer. Observations exist for
  // temperature and dewpoint directly; "apparent temp" pills compute the real
  // NWS wind chill / heat index from the obs (temp + dewpoint + wind).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    const tempFamily = ["temperature", "apparent_temperature", "dewpoint"];
    if (activeLayer && tempFamily.includes(activeLayer)) {
      setMetarPillsEnabled(map, true);
      setMetarPillsMode(map, activeLayer as any);
    } else {
      setMetarPillsEnabled(map, false);
    }
  }, [activeLayer, mapLoaded]);

  // Observations are shown at "now" ONLY: the playhead must sit on the newest
  // non-forecast keyframe (the present). Past and forecast frames rely on the
  // contour colors alone — a cheap visibility flip either way, no teardown.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const kfs = keyframes[subLayerKeys[0]];
    if (!kfs || kfs.length === 0) return;
    const idx = Math.max(0, Math.min(kfs.length - 1, Math.round(currentIdx)));
    let nowIdx = -1;
    for (let i = kfs.length - 1; i >= 0; i--) {
      if (!kfs[i].isForecast) { nowIdx = i; break; }
    }
    setMetarPillsVisible(map, nowIdx !== -1 && idx === nowIdx);
  }, [currentIdx, keyframes, subLayerKeys]);

  // Full teardown on unmount (aborts in-flight fetches, clears the refresh
  // timer, removes the moveend listener and the map layer/source).
  useEffect(() => {
    return () => { detachMetarPills(mapRef.current); };
  }, []);

  // ─── Streamline params (live update, no rebuild) ─────────────────────────
  // The slider values define the look at the REFERENCE zoom (~z4, clean and
  // legible). The simulation runs in viewport space, so WITHOUT compensation it
  // packs the full particle count, full screen-speed, and full screen-length
  // trails into the view at EVERY zoom — which is exactly why close zoom showed
  // "more particles, faster, longer tails." We can't fix that with dropRate
  // (it only controls respawn frequency, not how many particles are on screen),
  // so we drive three real levers by zoom:
  //   • draw fraction DOWN → genuinely fewer particles drawn (shader culls by
  //                          index; no rebuild)
  //   • speed scale  DOWN  → genuinely slower advection per frame
  //   • fadeOpacity  DOWN  → shorter trails
  // At/below the reference zoom everything passes through at full strength, so
  // the zoomed-out view is untouched. The tuner sliders shift the curve.
  useEffect(() => {
    const sl = streamlinesRef.current;
    if (!sl) return;

    // 0 at/below reference zoom → 1 at/above full zoom.
    // Pulled FULL_ZOOM down from 10 to 8 so the compensation is fully applied
    // by the time the user reaches the zoom levels they complained about (8+),
    // instead of only being two-thirds applied there. Clamped to 1 above z8,
    // so anything past 8 stays at the most-thinned/slowest setting.
    const REF_ZOOM = 4;
    const FULL_ZOOM = 8;
    const t = Math.max(0, Math.min(1, (mapZoom - REF_ZOOM) / (FULL_ZOOM - REF_ZOOM)));

    // Density: draw all particles when zoomed out, down to ~12% when zoomed in.
    // (Was 25% — still looked crowded at close zoom, so we cull more.)
    const DRAW_FRACTION_AT_FULL = 0.12;
    const drawFraction = 1 + (DRAW_FRACTION_AT_FULL - 1) * t;

    // Speed: full step when zoomed out, down to ~20% when zoomed in.
    // (Was 35% — still read as "too fast" at close zoom.)
    const SPEED_SCALE_AT_FULL = 0.20;
    const speedScale = 1 + (SPEED_SCALE_AT_FULL - 1) * t;

    // Trail length: ease fadeOpacity down (exponential, so a small drop is a
    // big visual change).
    const FADE_AT_FULL = 0.90;
    const fade = slFadeOpacity + (FADE_AT_FULL - slFadeOpacity) * t;

    // Thickness: at close zoom there are far fewer particles on screen, so the
    // base point size reads as too thin/sparse. Scale point size UP with zoom
    // so the remaining streamlines stay visually substantial. ~1.8x at full.
    const POINT_SIZE_MULT_AT_FULL = 1.8;
    const pointSize = slPointSize * (1 + (POINT_SIZE_MULT_AT_FULL - 1) * t);

    // Theme-driven brightness: render the streamlines 10% darker on the dark
    // ("black") basemap than on the light ("grayscale") basemap. The base
    // streamline gray (208,208,208) is the light-map color at brightness 1.0;
    // 0.9 yields ~187,187,187 on dark. Applied to RGB only (alpha untouched),
    // and it carries into the tails automatically since tails are faded copies
    // of the drawn heads.
    const DARK_MAP_BRIGHTNESS = 0.9;
    const brightness = mapFlavor === "black" ? DARK_MAP_BRIGHTNESS : 1.0;

    sl.setFadeOpacity(fade);
    sl.setSpeedFactor(slSpeedFactor);
    sl.setSpeedScale(speedScale);
    sl.setDrawFraction(drawFraction);
    sl.setPointSize(pointSize);
    sl.setColorBrightness(brightness);
    sl.setDropRate(slDropRate);
    sl.setDropRateBump(slDropRateBump);
  }, [slFadeOpacity, slSpeedFactor, slDropRate, slDropRateBump, slPointSize, mapZoom, mapFlavor]);

  // ─── Point size / particle count change (requires layer rebuild) ─────────
  // Both point size and particle-state size are baked in at construction (the
  // particle textures are allocated then), so changing either requires
  // disposing and recreating the streamlines layer rather than a live setter.
  useEffect(() => {
    if (!streamlinesRef.current || activeLayer !== "wind") return;
    const map = mapRef.current;
    if (!map) return;
    streamlinesRef.current.dispose();
    streamlinesRef.current = new WeatherWindStreamlines({
      // Keep in sync with the first site above (count = size²).
      particleStateSize: slParticleStateSize,
      opacity: opacity / 100,
      fadeOpacity: slFadeOpacity,
      speedFactor: slSpeedFactor,
      dropRate: slDropRate,
      dropRateBump: slDropRateBump,
      pointSize: slPointSize,
      // Match the first site: 10% darker on the dark basemap.
      colorBrightness: mapFlavor === "black" ? 0.9 : 1.0,
      windRange: [-70, 70],
    });
    map.addLayer(streamlinesRef.current as any, pickWeatherBeforeId(map));
    // Re-add coastline so it sits above the freshly-inserted streamlines
    // (otherwise streamlines would draw on top of coastlines).
    addWeatherCoastline(map, pickWeatherBeforeId(map));
    // The new instance starts with NO wind texture. The display effect only
    // feeds frames when currentIdx/loadedCount change, so if playback is
    // paused after a rebuild it would never receive one and would render
    // nothing (the "streamlines break when adjusting point size" bug). Push
    // the current frame in immediately so the rebuilt layer is populated.
    applyFrameForIdx(currentIdx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slPointSize, slParticleStateSize]);

  // ─── Playback: advance fractional idx every RAF ──────────────────────────
  // Each keyframe transition takes `secondsPerFrame` seconds of real time.
  // Fast-changing layers (like MRMS radar) use a larger value to visually mask
  // inter-frame motion. Default is 1.5s which feels natural for most layers.
  //
  // For composite layers we take the MAX of all sub-layers' secondsPerFrame
  // so the slower-paced sub-layer sets the pace — otherwise MRMS would tear
  // at playback speeds tuned for the EU radar's calmer motion.
  //
  // Realtime vs nowcast pacing: realtime radar frames are 5-min apart while
  // EU/MRMS nowcasts are still 15-min apart. To keep wall-clock-per-tick
  // proportional to the data interval (i.e. one second of playback covers
  // roughly the same minutes of weather regardless of which side of the
  // boundary you're on), the realtime portion plays 2x faster per frame
  // than the forecast portion. Boundary detected via the `isForecast` flag
  // on each keyframe (set in fetchKeyframes).
  useEffect(() => {
    if (!playing || scrubberLength === 0) return;

    const baseSecs = subLayerKeys.length === 0
      ? 1.5
      : Math.max(...subLayerKeys.map((k) => WEATHER_LAYERS[k]?.secondsPerFrame ?? 1.5));
    // Non-radar layers play at half speed (each frame visible for 2x as
    // long). Radar stays at full speed because radar frames are dense
    // (5-min realtime / 15-min nowcast) and look natural at normal pace —
    // forecast layers like temperature/wind tend to be every few hours
    // each, so playing them slower gives the eye time to register the
    // change between frames.
    const isRadar = subLayerKeys.some((k) => k.startsWith("radar"));
    const layerSpeedMultiplier = isRadar ? 1.0 : 0.5;
    // User-adjustable speed: higher multiplier = faster playback.
    const SECONDS_PER_FRAME = baseSecs / Math.max(0.1, speedMultiplier * layerSpeedMultiplier);
    // Realtime (past) portion plays 2× faster than the nowcast/forecast
    // portion, so the denser 5-min radar history sweeps through quickly
    // while the sparser 15-min forecast plays at normal pace.
    const REALTIME_BOOST = 2.0;
    const SECS_PER_FRAME_REALTIME = SECONDS_PER_FRAME / REALTIME_BOOST;
    const SECS_PER_FRAME_FORECAST = SECONDS_PER_FRAME;

    // Find the boundary index (first forecast frame) in the shortest
    // sub-layer — same one the scrubber clock uses. We index off this rather
    // than the longest list so the boundary lines up with what the user sees.
    const shortestKey = subLayerKeys.reduce((best, k) => {
      const len = keyframes[k]?.length ?? 0;
      const bestLen = keyframes[best]?.length ?? Infinity;
      return len > 0 && len < bestLen ? k : best;
    }, subLayerKeys[0]);
    const kfs = keyframes[shortestKey] ?? [];
    // Index of the first forecast frame, or kfs.length if there are none.
    // Frames at idx < boundary are realtime (faster); idx >= boundary are
    // forecast (slower). Defaults to "everything is realtime" if no flag
    // present (e.g. non-radar layers), which preserves prior behaviour.
    let boundaryIdx = kfs.length;
    for (let i = 0; i < kfs.length; i++) {
      if (kfs[i].isForecast) { boundaryIdx = i; break; }
    }

    // Loop bounds derived strictly from the contiguous loaded block.
    // The old fallback paths returned ranges like [0, nowIdx] when the
    // block was degenerate — those included unloaded frames. Now we only
    // ever return what loadedBlockAroundNow reports. If the block is too
    // small to animate (≤1 frame), the tick holds position and retries
    // next frame — the block will grow as decodes land.
    const safeLoopBounds = (): [number, number] => {
      return loadedBlockAroundNowRef.current();
    };

    playStartRef.current = performance.now();
    // Resume from the current scrubber position if it falls within the
    // playable loaded range; otherwise fall back to the loop start. This
    // lets pause→play resume where the user left off instead of always
    // jumping to the beginning of the timeline.
    const [loopStart, loopEnd0] = safeLoopBounds();
    loopEndRef.current = loopEnd0;
    const resumeIdx = (loopEnd0 > loopStart && currentIdx >= loopStart && currentIdx < loopEnd0)
      ? currentIdx
      : loopStart;
    playStartIdxRef.current = resumeIdx;
    setCurrentIdx(resumeIdx);

    const tick = () => {
      const elapsed = (performance.now() - playStartRef.current) / 1000;
      // Convert elapsed wall-clock seconds into idx advance, accounting for
      // the speed change at boundaryIdx. We start from playStartIdxRef and
      // walk forward: any time spent in the realtime portion (idx < boundary)
      // advances idx at the faster rate; any time spent in the forecast
      // portion advances at the slower rate.
      const startIdx = playStartIdxRef.current;
      let next: number;
      if (startIdx >= boundaryIdx) {
        // Started in (or past) the forecast region — entire elapsed at slow rate
        next = startIdx + elapsed / SECS_PER_FRAME_FORECAST;
      } else {
        // Started in realtime. How long does it take to cross the boundary?
        const idxToBoundary = boundaryIdx - startIdx;
        const secsToBoundary = idxToBoundary * SECS_PER_FRAME_REALTIME;
        if (elapsed <= secsToBoundary) {
          // Still in realtime range
          next = startIdx + elapsed / SECS_PER_FRAME_REALTIME;
        } else {
          // Crossed into forecast — fast-portion fully consumed, remainder at slow rate
          const remainingSecs = elapsed - secsToBoundary;
          next = boundaryIdx + remainingSecs / SECS_PER_FRAME_FORECAST;
        }
      }
      // Re-read the loaded block each tick so the loop window grows
      // dynamically as frames decode — older history extends the start,
      // nowcast extends the end. Because safeLoopBounds returns ONLY the
      // contiguous loaded block, the playhead never sweeps into undecoded
      // territory.
      const [wrapStart, wrapEnd] = safeLoopBounds();
      loopEndRef.current = wrapEnd;
      // If the loaded block is too small to animate (≤1 frame loaded),
      // hold position and keep checking — decodes will widen the block.
      if (wrapEnd <= wrapStart) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      if (next >= wrapEnd) {
        playStartRef.current = performance.now();
        playStartIdxRef.current = wrapStart;
        next = wrapStart;
      }
      setCurrentIdx(next);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, scrubberLength, activeLayer, speedMultiplier]);

  // ─── Basemap flavor change ────────────────────────────────────────────────
  // setStyle() REPLACES the entire MapLibre style, which destroys every custom
  // layer (the weather raster, streamlines, coastline) and the warning/lightning
  // style layers — they are NOT preserved across a style swap. So after the new
  // style finishes loading we bump `styleEpoch`; the effects that own those
  // layers depend on it and re-add themselves. We build the new style at the
  // CURRENT label size so labels keep their size through the swap.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    try {
      map.setStyle(buildDarkStyle(mapFlavor, labelSize, featureToggles));
      // Re-add custom layers only once the new style is FULLY loaded. `idle`
      // fires after the style, its sources, sprites and glyphs are ready and
      // the map has finished rendering — unlike `styledata`, which fires many
      // times mid-load (re-adding then would race missing sources). Guard with
      // isStyleLoaded() in case the listener fires for an unrelated idle.
      const onReady = () => {
        if (!map.isStyleLoaded || map.isStyleLoaded()) {
          setStyleEpoch((e) => e + 1);
        } else {
          map.once("idle", onReady);
        }
      };
      map.once("idle", onReady);
    } catch (e) { console.error("Style error:", e); }
    // labelSize intentionally omitted — it's applied live below, not via setStyle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapFlavor]);

  // ─── Label size change (live, no setStyle) ────────────────────────────────
  // Resize Protomaps place labels in place via setLayoutProperty. This avoids
  // a full style rebuild, so dragging the slider is instant and never tears
  // down the weather layers (the bug that made the slider appear to do nothing
  // when it went through setStyle).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    try { applyLabelScaleLive(map, labelSize); }
    catch (e) { console.error("Label size error:", e); }
    // Re-apply after a basemap swap too: the new style resets label sizes to
    // whatever buildDarkStyle baked in, and styleEpoch tracks that swap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelSize, styleEpoch, mapLoaded]);

  // ─── Feature toggles change (live, no setStyle) ───────────────────────────
  // Show/hide roads, highways, landuse and buildings in place via
  // setLayoutProperty("visibility", ...). Like label size, this avoids a style
  // rebuild so toggling is instant and the weather layers are untouched. Also
  // re-applied on styleEpoch so the toggles stick across a basemap swap (the
  // baked-in visibility already covers it, but re-applying is cheap and makes
  // the two paths agree).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    try { applyFeatureVisibility(map, featureToggles); }
    catch (e) { console.error("Feature visibility error:", e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureToggles, styleEpoch, mapLoaded]);
  useEffect(() => {
    if (!lightningEnabled) { setLightningData({}); return; }
    const fetchLightning = () => {
      getCogToken()
        .then((token) => fetch(`${FLASK_API}/lightning?t=${encodeURIComponent(token)}`))
        .then((res) => { if (!res.ok) throw new Error(`Lightning API ${res.status}`); return res.json(); })
        .then((data) => setLightningData(data))
        .catch((err) => console.error("Lightning fetch failed:", err));
    };
    fetchLightning();
    const i = setInterval(fetchLightning, 60000);
    return () => clearInterval(i);
  }, [lightningEnabled, wakeEpoch]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    if (lightningFlashRef.current) { cancelAnimationFrame(lightningFlashRef.current); lightningFlashRef.current = null; }

    const removeAllLightning = () => {
      const style = map.getStyle();
      if (style?.layers) {
        style.layers.forEach((l: any) => { if (l.id.startsWith(LIGHTNING_LAYER_PREFIX)) map.removeLayer(l.id); });
      }
      const sources = style?.sources ? Object.keys(style.sources) : [];
      sources.forEach((s) => { if (s.startsWith(LIGHTNING_LAYER_PREFIX)) map.removeSource(s); });
    };

    if (!lightningEnabled || Object.keys(lightningData).length === 0) {
      removeAllLightning();
      prevLightningTsRef.current = new Set();
      return;
    }

    const timestamps = Object.keys(lightningData).map(Number).sort((a, b) => a - b);
    const count = timestamps.length;
    const oldestTs = timestamps[0];
    const newestTs = timestamps[count - 1];
    const prevSet = prevLightningTsRef.current;
    const isFirstLoad = prevSet.size === 0;
    const newTimestamps = new Set<number>();
    if (!isFirstLoad) {
      timestamps.forEach((ts) => { if (!prevSet.has(ts)) newTimestamps.add(ts); });
    }
    prevLightningTsRef.current = new Set(timestamps);

    const ensureIcon = async () => {
      if (map.hasImage(LIGHTNING_ICON_ID)) return;
      const size = 32;
      const c = document.createElement("canvas");
      c.width = size; c.height = size;
      const ctx = c.getContext("2d")!;
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.moveTo(18, 1); ctx.lineTo(8, 15); ctx.lineTo(14, 15); ctx.lineTo(12, 31);
      ctx.lineTo(24, 14); ctx.lineTo(17, 14); ctx.lineTo(18, 1);
      ctx.closePath(); ctx.fill();
      const imgData = ctx.getImageData(0, 0, size, size);
      map.addImage(LIGHTNING_ICON_ID, imgData, { sdf: true });
    };

    (async () => {
      await ensureIcon();
      removeAllLightning();
      // Snapshot the current map clock for the initial paint. The separate
      // sync effect below will re-shade these layers as the scrubber moves.
      const mapTime = currentMapTimeRef.current;
      const newLayerInfo: { lyrId: string; shade: { color: string; opacity: number } }[] = [];

      for (let i = 0; i < count; i++) {
        const ts = timestamps[i];
        const geojson = lightningData[ts];
        if (!geojson?.features?.length) continue;

        const srcId = LIGHTNING_LAYER_PREFIX + ts;
        const lyrId = LIGHTNING_LAYER_PREFIX + ts;
        const shade = pickLightningShade(ts, i, count, oldestTs, newestTs, mapTime);
        const isNew = newTimestamps.has(ts);

        map.addSource(srcId, { type: "geojson", data: geojson });
        map.addLayer({
          id: lyrId, type: "symbol", source: srcId,
          layout: {
            "icon-image": LIGHTNING_ICON_ID,
            "icon-size": ["interpolate", ["linear"], ["zoom"], 2, 0.35, 5, 0.55, 8, 0.8, 12, 1.0],
            "icon-allow-overlap": true, "icon-ignore-placement": true,
          },
          paint: {
            // shade==null means the strike is in the future (hidden).
            "icon-color": isNew ? "#FFFFFF" : (shade?.color ?? "#FFFFFF"),
            "icon-opacity": isNew ? 0 : (shade?.opacity ?? 0),
          },
        });
        // Only the freshly-arrived timestamps run through the flash animation.
        // The flash falls back to the most recent shade — when synced to a
        // radar that's parked at "now", that's still LIGHTNING_SHADES[last].
        if (isNew) newLayerInfo.push({ lyrId, shade: shade ?? LIGHTNING_SHADES[LIGHTNING_SHADES.length - 1] });
      }

      if (newLayerInfo.length > 0) {
        const flashStart = performance.now();
        const flashDuration = 1500;
        const animate = (now: number) => {
          const elapsed = now - flashStart;
          const t = Math.min(1, elapsed / flashDuration);
          let flashOpacity: number;
          if (t < 0.12) flashOpacity = t / 0.12;
          else if (t < 0.2) flashOpacity = 1.0 - ((t - 0.12) / 0.08) * 0.6;
          else if (t < 0.32) flashOpacity = 0.4 + ((t - 0.2) / 0.12) * 0.6;
          else if (t < 0.4) flashOpacity = 1.0 - ((t - 0.32) / 0.08) * 0.5;
          else if (t < 0.52) flashOpacity = 0.5 + ((t - 0.4) / 0.12) * 0.5;
          else {
            const settleT = (t - 0.52) / 0.48;
            flashOpacity = 1.0 - settleT * 0.08;
          }
          const iconColor = t < 0.52 ? "#FFFFFF"
            : `rgb(255,${Math.round(255 - ((t - 0.52) / 0.48) * 14)},${Math.round(255 - ((t - 0.52) / 0.48) * 137)})`;
          const sizeMul = t < 0.52
            ? 1.0 + Math.sin(t / 0.52 * Math.PI * 3) * 0.3
            : 1.0 + (1 - (t - 0.52) / 0.48) * 0.1;

          newLayerInfo.forEach(({ lyrId }) => {
            if (map.getLayer(lyrId)) {
              map.setPaintProperty(lyrId, "icon-opacity", flashOpacity);
              map.setPaintProperty(lyrId, "icon-color", iconColor);
              map.setLayoutProperty(lyrId, "icon-size", [
                "interpolate", ["linear"], ["zoom"],
                2, 0.35 * sizeMul, 5, 0.55 * sizeMul, 8, 0.8 * sizeMul, 12, 1.0 * sizeMul,
              ]);
            }
          });
          if (t < 1) lightningFlashRef.current = requestAnimationFrame(animate);
          else {
            lightningFlashRef.current = null;
            newLayerInfo.forEach(({ lyrId, shade }) => {
              if (map.getLayer(lyrId)) {
                map.setPaintProperty(lyrId, "icon-opacity", shade.opacity);
                map.setPaintProperty(lyrId, "icon-color", shade.color);
                map.setLayoutProperty(lyrId, "icon-size", [
                  "interpolate", ["linear"], ["zoom"], 2, 0.35, 5, 0.55, 8, 0.8, 12, 1.0,
                ]);
              }
            });
          }
        };
        lightningFlashRef.current = requestAnimationFrame(animate);
      }
    })().catch((e) => console.error("[Lightning] render error:", e));

    return () => {
      if (lightningFlashRef.current) { cancelAnimationFrame(lightningFlashRef.current); lightningFlashRef.current = null; }
      removeAllLightning();
    };
    // styleEpoch: setStyle() removes these layers; re-run to re-add them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightningData, lightningEnabled, mapLoaded, styleEpoch]);

  // ─── Lightning ↔ scrubber sync ───────────────────────────────────────────
  // Keep a ref of the live map clock so the render effect (above) can pick
  // it up at layer-creation time without depending on currentMapTime — that
  // dependency would tear down every per-timestamp layer on every play tick.
  // Then re-shade existing layers in place: timestamps in the future hide,
  // visible ones get a shade based on age relative to the playback clock.
  // Skip while the flash animation is running so we don't fight it for the
  // newly-arrived layers.
  useEffect(() => {
    currentMapTimeRef.current = currentMapTime;
    const map = mapRef.current;
    if (!map || !mapLoaded || !lightningEnabled) return;
    if (lightningFlashRef.current != null) return;
    const tsList = Object.keys(lightningData).map(Number).sort((a, b) => a - b);
    if (tsList.length === 0) return;
    const oldestTs = tsList[0];
    const newestTs = tsList[tsList.length - 1];
    for (let i = 0; i < tsList.length; i++) {
      const ts = tsList[i];
      const lyrId = LIGHTNING_LAYER_PREFIX + ts;
      if (!map.getLayer(lyrId)) continue;
      const shade = pickLightningShade(ts, i, tsList.length, oldestTs, newestTs, currentMapTime);
      if (!shade) {
        // Future strike — hide.
        map.setPaintProperty(lyrId, "icon-opacity", 0);
      } else {
        map.setPaintProperty(lyrId, "icon-opacity", shade.opacity);
        map.setPaintProperty(lyrId, "icon-color", shade.color);
      }
    }
  }, [currentMapTime, lightningData, lightningEnabled, mapLoaded]);

  // ─────────────────────────────────────────────────────────────────────────
  // Shared NWS alert popup builder. Used by BOTH the "Severe Warnings" and the
  // "All Weather Alerts" overlays so a clicked polygon shows the identical rich
  // card regardless of which layer it came from. Reads only the clicked
  // feature's properties plus the map/refs already in scope; opens a themed
  // MapLibre popup with severity/urgency/certainty badges, an issued→expires
  // timeline, and collapsible area/description/instruction sections.
  const openNwsAlertPopup = (map: any, e: any) => {
        if (!e.features?.length) return;
        const props = e.features[0].properties;
        const accent = NWS_PRODTYPE_COLORS[props.event]
          ?? WARNING_COLORS[props.event]
          ?? ALERT_SEVERITY_COLORS[props.severity]
          ?? "#ffff00";
        // Basemap is limited to dark + grey (both dark-toned), so the alert
        // popup always uses the dark chrome. (Kept as a flag in case a light
        // basemap returns later.)
        const isLight = false;
        const themeClass = isLight ? "nws-light" : "nws-dark";

        // Theme tokens. These mirror StormChance's THEMES.dark / THEMES.light
        // so the popup body reads as a natural extension of the rest of the
        // UI when toggled. Kept inline (rather than CSS classes for every
        // little span) because the inline-style HTML string approach is
        // simpler than wiring up a stylesheet for each text element.
        const tok = isLight ? {
          textPrimary: "#1a2533", textSecondary: "#3a4a5c",
          textMuted: "#506070", textDim: "#708090",
          cardBg: "rgba(0,0,0,0.025)", cardBorder: "rgba(0,0,0,0.06)",
          impactBg: "rgba(46,139,190,0.05)", impactBorder: "rgba(46,139,190,0.18)",
          impactAccent: "#2E8BBE",
          todoBg: "rgba(30,168,114,0.05)", todoBorder: "rgba(30,168,114,0.2)",
          todoAccent: "#1ea872",
          accent: "#2E8BBE", accentSoft: "rgba(46,139,190,0.1)",
          accentBorder: "rgba(46,139,190,0.3)",
          badgeBg: "rgba(0,0,0,0.04)",
          issued: "#d83644", expires: "#1ea872",
          severeBg: "rgba(216,54,68,0.12)",
        } : {
          textPrimary: "#e8ecf2", textSecondary: "#c8d0dc",
          textMuted: "#7d8a9a", textDim: "#5e6d7e",
          cardBg: "rgba(255,255,255,0.025)", cardBorder: "rgba(255,255,255,0.05)",
          impactBg: "rgba(94,181,223,0.05)", impactBorder: "rgba(94,181,223,0.15)",
          impactAccent: "#5EB5DF",
          todoBg: "rgba(94,232,158,0.05)", todoBorder: "rgba(94,232,158,0.18)",
          todoAccent: "#5EE89E",
          accent: "#5EB5DF", accentSoft: "rgba(94,181,223,0.12)",
          accentBorder: "rgba(94,181,223,0.25)",
          badgeBg: "rgba(255,255,255,0.05)",
          issued: "#ff5e6c", expires: "#5EE89E",
          severeBg: "rgba(255,94,108,0.18)",
        };

        // ── Helpers ──────────────────────────────────────────────────────

        // NWS ISO timestamp → { time, date } pair for two-line display.
        // Wall-clock is preserved (no tz reprojection) so users see local
        // time for the affected region.
        const fmtTime = (iso: string | null | undefined) => {
          if (!iso) return { time: "—", date: "" };
          const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
          if (!m) return { time: iso, date: "" };
          const [, y, mo, d, hh, mm] = m;
          const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
          const monthName = months[parseInt(mo) - 1] ?? mo;
          const h24 = parseInt(hh);
          const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
          const ampm = h24 < 12 ? "AM" : "PM";
          return { time: `${h12}:${mm} ${ampm}`, date: `${monthName} ${parseInt(d)}, ${y}` };
        };

        // HTML escape — defends against `<`, `>`, etc. in NWS text
        // (e.g. "gusts >45 mph") that would otherwise break the layout.
        const esc = (s: string | null | undefined) =>
          (s ?? "").replace(/[&<>"']/g, c => ({
            "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
          }[c]!));

        // Only allow http(s) hrefs. esc() blocks attribute breakout but not a
        // "javascript:" scheme, so a hostile or garbled feed URL could otherwise
        // run on click. Returns a safe href, or "" to omit the link entirely.
        const safeHref = (u: string | null | undefined): string => {
          if (!u) return "";
          try {
            const p = new URL(u, window.location.origin);
            return p.protocol === "https:" || p.protocol === "http:" ? p.href : "";
          } catch {
            return "";
          }
        };

        // NWS warning descriptions follow a semi-structured format: a free-text
        // narrative ("At 835PM CDT, a severe thunderstorm was located…") followed
        // by labeled segments — HAZARD..., SOURCE..., IMPACT... — each introduced
        // by an ALL-CAPS keyword and ellipsis. This pulls out the text for a given
        // set of labels so we can route HAZARD/IMPACT into the Impact box and keep
        // the narrative in "What's Happening".
        //
        // Returns { matched: text of the requested labels (joined), rest: the
        // description with those labeled segments removed }. Matching is done on
        // the raw (un-escaped) string; callers escape the pieces afterward.
        const extractSegments = (raw: string, labels: string[]) => {
          const text = (raw ?? "").trim();
          if (!text) return { matched: "", rest: "" };

          // Find every "LABEL..." occurrence and its span up to the next label
          // (or end of string). We scan for known NWS segment keywords so a
          // stray capitalized word in the narrative doesn't get treated as a
          // segment boundary.
          const KEYWORDS = ["HAZARD", "SOURCE", "IMPACT", "PRECAUTIONARY/PREPAREDNESS ACTIONS"];
          const boundary = new RegExp(`(?:^|\\n|\\s)(${KEYWORDS.join("|")})\\.\\.\\.`, "g");

          const segs: { label: string; start: number; bodyStart: number }[] = [];
          let m: RegExpExecArray | null;
          while ((m = boundary.exec(text)) !== null) {
            segs.push({ label: m[1], start: m.index, bodyStart: m.index + m[0].length });
          }

          if (segs.length === 0) return { matched: "", rest: text };

          const wanted = new Set(labels.map(l => l.toUpperCase()));
          const matchedParts: string[] = [];
          // Track character ranges of matched segments so we can build `rest`.
          const cutRanges: [number, number][] = [];

          for (let i = 0; i < segs.length; i++) {
            const seg = segs[i];
            const end = i + 1 < segs.length ? segs[i + 1].start : text.length;
            if (wanted.has(seg.label)) {
              const body = text.slice(seg.bodyStart, end).trim();
              // Re-prefix with the label so the box reads "HAZARD...60 mph…".
              matchedParts.push(`${seg.label}...${body}`);
              cutRanges.push([seg.start, end]);
            }
          }

          // Build `rest` by removing matched ranges from the original text.
          let rest = text;
          for (const [s, e] of cutRanges.sort((a, b) => b[0] - a[0])) {
            rest = rest.slice(0, s) + rest.slice(e);
          }
          // Collapse the blank lines left behind by removed segments.
          rest = rest.replace(/\n{3,}/g, "\n\n").trim();

          return { matched: matchedParts.join("\n\n"), rest };
        };

        // Severity pill styling. Severe/extreme get the issued-color treatment;
        // moderate gets orange; everything else stays neutral. Urgency and
        // certainty render as plain neutral pills (no value-based coloring).
        const sevColors = (s: string) => {
          const v = (s ?? "").toLowerCase();
          if (v === "extreme" || v === "severe") return { bg: tok.severeBg, fg: tok.issued };
          if (v === "moderate") return { bg: "rgba(255,170,51,0.15)", fg: "#d97706" };
          return { bg: tok.badgeBg, fg: tok.textMuted };
        };

        // Render body text inside a fixed-max-height scrollable box. Short text
        // sits flush at the top; long text scrolls within the box. This replaces
        // the old "More…/Less" toggle — the full text is always present, the box
        // just caps its height. The .nws-alert-scroll class supplies the themed
        // scrollbar; the inline max-height lets each caller size its own box.
        // `color` defaults to the primary text token but Impact passes secondary.
        const scrollBody = (text: string, maxHeight = 150, color: string): string => {
          if (!text) return "";
          return `<div class="nws-alert-scroll" style="max-height:${maxHeight}px;font-size:13px;line-height:1.5;white-space:pre-wrap;color:${color}">${text}</div>`;
        };

        // ── HTML bits ────────────────────────────────────────────────────

        const sectionLabel = (label: string) => `
          <div style="font-size:10px;color:${tok.textDim};text-transform:uppercase;letter-spacing:1.2px;font-weight:600;margin-bottom:6px">${label}</div>`;

        // Metric column: an uppercase label above a text-value pill. Severity is
        // color-coded via sevColors; urgency and certainty render as neutral
        // pills. The value text is capitalized for display.
        const badge = (label: string, value: string, bg = tok.badgeBg, fg = tok.textSecondary) => `
          <div style="display:flex;flex-direction:column;align-items:center;gap:6px;flex:1;min-width:0">
            <span style="font-size:9px;color:${tok.textMuted};text-transform:uppercase;letter-spacing:1.2px;font-weight:700">${label}</span>
            <span style="font-size:12px;color:${fg};background:${bg};padding:4px 12px;border-radius:6px;font-weight:600;text-transform:capitalize;white-space:nowrap">${esc(value) || "—"}</span>
          </div>`;

        const timeCard = (label: string, t: { time: string; date: string }, color: string) => `
          <div style="flex:1;background:${tok.cardBg};border:1px solid ${tok.cardBorder};border-radius:8px;padding:10px 12px;text-align:center">
            <div style="font-size:9px;color:${tok.textDim};text-transform:uppercase;letter-spacing:1.2px;font-weight:600;margin-bottom:6px">${label}</div>
            <div style="font-size:22px;color:${color};font-weight:700;line-height:1.1;margin-bottom:3px">${t.time}</div>
            <div style="font-size:11px;color:${tok.textMuted}">${t.date}</div>
          </div>`;

        const issued = fmtTime(props.sent ?? props.effective);
        const expires = fmtTime(props.expires ?? props.ends);
        const sev = sevColors(props.severity);

        // Split the NWS description into the three display regions. NWS CAP
        // summaries are semi-structured: a narrative, then labeled segments
        // (HAZARD/SOURCE/IMPACT) and a PRECAUTIONARY/PREPAREDNESS ACTIONS block
        // carrying the protective-action text. Depending on the source these
        // arrive differently:
        //   • api.weather.gov  → `instruction` is a separate field; `description`
        //     holds narrative + HAZARD/SOURCE/IMPACT.
        //   • Esri Living Atlas → everything (including the preparedness actions)
        //     is concatenated into one `Summary` string; `instruction` is null.
        // So we parse all of it out of `description` and fall back to the
        // explicit `instruction` field only when the description had no
        // preparedness segment. This makes the Impact and What-to-Do boxes
        // populate identically regardless of which feed served the alert.
        const PREP_LABEL = "PRECAUTIONARY/PREPAREDNESS ACTIONS";
        const { matched: impactText, rest: afterImpact } =
          extractSegments(props.description ?? "", ["HAZARD", "IMPACT"]);
        const { matched: prepText, rest: happeningRaw } =
          extractSegments(afterImpact, [PREP_LABEL]);

        // Tidy the narrative: strip the CAP "&&" segment terminator and the
        // leading "* WHERE/WHEN..." bullet lines that some products include, so
        // What's Happening reads as clean prose.
        const happeningText = (happeningRaw || "")
          .replace(/&&/g, "")
          .trim();

        // What-to-Do text: prefer the parsed preparedness segment (strip its
        // label prefix and the && terminator), else the explicit instruction
        // field from the CAP API.
        const prepClean = (prepText || "")
          .replace(new RegExp(`^${PREP_LABEL}\\.\\.\\.`), "")
          .replace(/&&/g, "")
          .trim();
        const todoText = prepClean || (props.instruction ?? "");

        // Areas Affected is short (county names) — render flush, no scroll box.
        const areaBody = esc(props.areaDesc);
        const descBody = scrollBody(esc(happeningText), 150, tok.textPrimary);
        const impactBody = scrollBody(esc(impactText || ""), 130, tok.textSecondary);
        const todoBody = scrollBody(esc(todoText), 110, tok.textSecondary);
        const alertHref = safeHref(props.url);

        const html = `
          <div class="nws-alert-body" style="font-family:Roboto,-apple-system,system-ui,'Segoe UI',sans-serif;color:${tok.textPrimary};padding:18px 18px 16px;max-width:360px;box-sizing:border-box">
            <div style="text-align:center;margin-bottom:16px">
              <div style="font-family:Anton,'Roboto Condensed',Roboto,sans-serif;font-size:24px;font-weight:400;color:${tok.textPrimary};line-height:1.15;text-transform:uppercase;letter-spacing:0.4px;padding:0 8px">
                ${esc(props.event) || "Weather Alert"}
              </div>
              <div style="width:85%;height:3px;border-radius:2px;background:${accent};margin:8px auto 0;box-shadow:0 0 8px ${accent}99"></div>
            </div>

            <div style="display:flex;gap:12px;margin-bottom:16px">
              ${badge("Severity", props.severity, sev.bg, sev.fg)}
              ${badge("Urgency", props.urgency)}
              ${badge("Certainty", props.certainty)}
            </div>

            ${sectionLabel("Timing")}
            <div style="display:flex;align-items:stretch;gap:10px;margin-bottom:4px">
              ${timeCard("Issued", issued, tok.issued)}
              ${timeCard("Expired", expires, tok.expires)}
            </div>

            ${areaBody ? `<div style="margin-top:16px">
              ${sectionLabel("Areas Affected")}
              <div style="font-size:13px;color:${tok.textPrimary};line-height:1.5;white-space:pre-wrap">${areaBody}</div>
            </div>` : ""}

            ${descBody ? `<div style="margin-top:16px">
              ${sectionLabel("What's Happening")}
              ${descBody}
            </div>` : ""}

            ${impactBody ? `<div style="margin-top:16px;padding:10px 12px;background:${tok.impactBg};border:1px solid ${tok.impactBorder};border-left:3px solid ${tok.impactAccent};border-radius:6px">
              <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px">
                <span style="font-size:13px;color:#ffaa33">⚠</span>
                <span style="font-size:10px;color:${tok.textPrimary};text-transform:uppercase;letter-spacing:1.2px;font-weight:700">Impact</span>
              </div>
              ${impactBody}
            </div>` : ""}

            ${todoBody ? `<div style="margin-top:12px;padding:10px 12px;background:${tok.todoBg};border:1px solid ${tok.todoBorder};border-left:3px solid ${tok.todoAccent};border-radius:6px">
              <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px">
                <span style="font-size:13px;color:${tok.todoAccent}">✓</span>
                <span style="font-size:10px;color:${tok.textPrimary};text-transform:uppercase;letter-spacing:1.2px;font-weight:700">What To Do</span>
              </div>
              ${todoBody}
            </div>` : ""}

            ${alertHref ? `<div style="margin-top:16px">
              <a href="${esc(alertHref)}" target="_blank" rel="noopener noreferrer"
                 style="display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:600;color:${tok.accent};text-decoration:none;padding:8px 12px;background:${tok.accentSoft};border:1px solid ${tok.accentBorder};border-radius:6px">
                View full alert
                <span style="font-size:13px">→</span>
              </a>
            </div>` : ""}
          </div>`;

        // Close any previously-open alert popup before opening a new one.
        // Without this, multiple popups stack on the map as the user clicks
        // around. MapLibre's `remove()` triggers the popup's "close" event
        // which we use below to clear the ref.
        if (activePopupRef.current) {
          activePopupRef.current.remove();
          activePopupRef.current = null;
        }

        // Choose the popup anchor explicitly from the click's pixel position
        // within the map container. MapLibre's `anchor: "auto"` flips
        // horizontally well but has flaky vertical handling — bottom-edge
        // clicks frequently end up clipped. Picking the anchor ourselves
        // means the popup always opens *toward* the available space.
        const mapRect = map.getContainer().getBoundingClientRect();
        const clickX = e.point.x;
        const clickY = e.point.y;
        const w = mapRect.width;
        const h = mapRect.height;
        // Scale first: the anchor choice depends on how wide the card will
        // actually be once scaled, not on its authored 360px.
        const scale = nwsPopupScale(w);
        const cardWidth = NWS_POPUP_DESIGN_WIDTH * scale;
        const anchor = pickPopupAnchor(w, h, clickX, clickY, cardWidth);

        const PopupCtor = maplibreglRef.current?.default?.Popup
          ?? maplibreglRef.current?.Popup;
        if (!PopupCtor) return;
        const popup = new PopupCtor({
          maxWidth: `${Math.round(cardWidth)}px`,
          className: `nws-alert-popup ${themeClass}${scale < 1 ? " nws-popup-scaled" : ""}`,
          closeButton: true,
          anchor,
          offset: 12,
        })
          .setLngLat(e.lngLat).setHTML(html).addTo(map);

        // Hand the scale to the CSS 'zoom' rule. Set on the popup root so it
        // inherits down to .maplibregl-popup-content.
        const popupEl = popup.getElement();
        if (popupEl) {
          popupEl.style.setProperty("--nws-popup-scale", String(scale));
          popupEl.style.setProperty("--nws-popup-max-h", `${Math.round(h * NWS_POPUP_MAX_HEIGHT_FRAC)}px`);
        }

        activePopupRef.current = popup;
        // Clear the ref when the user closes the popup (X button, ESC, or
        // map click outside).
        popup.on("close", () => {
          if (activePopupRef.current === popup) activePopupRef.current = null;
        });
  };

  // ─── Generic overlay popup (hurricanes / SPC) ────────────────────────────
  // Same anchoring, theming, and single-popup bookkeeping as the NWS alert
  // popup, but takes pre-built HTML so any overlay can use it. The theme
  // tokens mirror openNwsAlertPopup's dark branch (the basemap set is all
  // dark-toned, so the popups always use the dark chrome).
  const STORM_POPUP_TOK = {
    textPrimary: "#e8ecf2", textSecondary: "#c8d0dc",
    textMuted: "#7d8a9a", textDim: "#5e6d7e",
    cardBg: "rgba(255,255,255,0.025)", cardBorder: "rgba(255,255,255,0.05)",
    accent: "#5EB5DF", accentSoft: "rgba(94,181,223,0.12)",
    accentBorder: "rgba(94,181,223,0.25)",
    issued: "#ff5e6c", expires: "#5EE89E",
  };
  const openOverlayPopup = (map: any, e: any, html: string) => {
    if (activePopupRef.current) {
      activePopupRef.current.remove();
      activePopupRef.current = null;
    }
    // Pick the popup anchor from the click's position within the container so
    // the popup always opens toward the available space (see the identical
    // logic in openNwsAlertPopup for why MapLibre's "auto" isn't used).
    const mapRect = map.getContainer().getBoundingClientRect();
    const w = mapRect.width, h = mapRect.height;
    const clickX = e.point.x, clickY = e.point.y;
    // Storm/SPC cards are authored 340px wide but share the alert popup's CSS,
    // so they take the same viewport scaling.
    const scale = nwsPopupScale(w);
    const cardWidth = 340 * scale;
    const anchor = pickPopupAnchor(w, h, clickX, clickY, cardWidth);

    const PopupCtor = maplibreglRef.current?.default?.Popup ?? maplibreglRef.current?.Popup;
    if (!PopupCtor) return;
    const popup = new PopupCtor({
      maxWidth: `${Math.round(cardWidth)}px`,
      className: `nws-alert-popup nws-dark${scale < 1 ? " nws-popup-scaled" : ""}`,
      closeButton: true, anchor, offset: 12,
    }).setLngLat(e.lngLat).setHTML(html).addTo(map);
    const popupEl = popup.getElement();
    if (popupEl) {
      popupEl.style.setProperty("--nws-popup-scale", String(scale));
      popupEl.style.setProperty("--nws-popup-max-h", `${Math.round(h * NWS_POPUP_MAX_HEIGHT_FRAC)}px`);
    }
    activePopupRef.current = popup;
    popup.on("close", () => {
      if (activePopupRef.current === popup) activePopupRef.current = null;
    });
  };

  // ═════════════════════════════════════════════════════════════════════════
  // Warnings (unchanged)
  // ═════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!warningsEnabled) { setWarningsData(null); return; }
    const fetchWarnings = () => {
      fetch(NWS_ALERTS_API, { headers: { "User-Agent": "(stormchance.com, contact@stormchance.com)" } })
        .then((res) => res.json()).then(setWarningsData)
        .catch((err) => console.error("Warnings fetch failed:", err));
    };
    fetchWarnings();
    const i = setInterval(fetchWarnings, 120000);
    return () => clearInterval(i);
  }, [warningsEnabled, wakeEpoch]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    const SRC = "warnings-source", LYR_FILL = "warnings-fill", LYR_LINE = "warnings-outline";

    if (!warningsEnabled || !warningsData) {
      if (map.getLayer(LYR_FILL)) map.removeLayer(LYR_FILL);
      if (map.getLayer(LYR_LINE)) map.removeLayer(LYR_LINE);
      if (map.getSource(SRC)) map.removeSource(SRC);
      // Close any lingering alert popup so it doesn't outlive the layer it
      // describes. The "close" event handler clears the ref for us.
      if (activePopupRef.current) activePopupRef.current.remove();
      return;
    }

    const matchExpr: any[] = ["match", ["get", "event"]];
    Object.entries(WARNING_COLORS).forEach(([k, v]) => { matchExpr.push(k, v); });
    matchExpr.push("#ffff00");

    // Restrict to ONLY the enumerated severe warnings. Derived from
    // WARNING_COLORS keys so the filter tracks the table automatically; any
    // other event type in the feed is dropped from both fill and outline (so
    // the #ffff00 default leg above is now effectively unreachable, kept only
    // as a safety fallback).
    const allowedWarningEvents = Object.keys(WARNING_COLORS);
    const warningEventFilter: any[] = ["in", ["get", "event"], ["literal", allowedWarningEvents]];

    // Click/draw priority: Tornado Warning on top, then Severe Thunderstorm
    // Warning, then Flash Flood Warning. MapLibre draws higher sort-key values
    // last (on top), and queryRenderedFeatures returns topmost-first — and the
    // click handler (openNwsAlertPopup) reads e.features[0] — so this single
    // key governs BOTH paint order and which alert the popup shows when
    // polygons overlap. Derived from WARNING_COLORS key order (first key =
    // highest priority) so the ranking tracks that table: index 0 → highest
    // sort key. Applied to fill and line alike so they never disagree.
    const warningSortKey: any[] = ["match", ["get", "event"]];
    allowedWarningEvents.forEach((ev, i) => {
      warningSortKey.push(ev, allowedWarningEvents.length - i);
    });
    warningSortKey.push(0); // anything else (unreachable past the filter) sinks

    if (map.getSource(SRC)) {
      (map.getSource(SRC) as any).setData(warningsData);
    } else {
      map.addSource(SRC, { type: "geojson", data: warningsData });

      // Layer separation: filled boxes BELOW the radar raster, box outlines
      // ABOVE it. Same anchoring approach as the alerts layer — prefer a live
      // radar sub-layer as the fill's beforeId, falling back to the weather
      // slot when radar isn't mounted yet. Outline added with no beforeId so it
      // stays on top of radar.
      const radarBeforeId =
        [`${WEATHER_LAYER_ID}-radar_mrms`, `${WEATHER_LAYER_ID}-radar_eumetnet`]
          .find((id) => map.getLayer(id)) ?? pickWeatherBeforeId(map);

      map.addLayer({ id: LYR_FILL, type: "fill", source: SRC, filter: warningEventFilter as any,
        layout: { "fill-sort-key": warningSortKey as any },
        paint: { "fill-color": matchExpr as any, "fill-opacity": 0.25 } }, radarBeforeId);
      map.addLayer({ id: LYR_LINE, type: "line", source: SRC, filter: warningEventFilter as any,
        layout: { "line-sort-key": warningSortKey as any },
        paint: { "line-color": matchExpr as any, "line-width": 2 } });

      // Open a MapLibre Popup at the click location showing alert details.
      // The popup is anchored to the map's lng/lat so it travels with the
      // alert polygon as the user pans/zooms. Theme follows mapFlavor via
      // the .nws-light / .nws-dark className (read fresh from the ref each
      // click — the basemap toggle happens after this handler is registered
      // so we can't capture it by closure).
      map.on("click", LYR_FILL, (e: any) => { openNwsAlertPopup(map, e); });
      map.on("mouseenter", LYR_FILL, () => { map.getCanvas().style.cursor = "pointer"; });
      map.on("mouseleave", LYR_FILL, () => { map.getCanvas().style.cursor = ""; });
    }
    // styleEpoch: setStyle() removes these layers; re-run to re-add them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warningsData, warningsEnabled, mapLoaded, styleEpoch]);

  // ═════════════════════════════════════════════════════════════════════════
  // All Weather Alerts (NWS + ECCC Canada)
  // ═════════════════════════════════════════════════════════════════════════
  // US alerts come from NOAA's ArcGIS watch_warn_adv feature service in a
  // single request (the active set fits one page — verified via returnCountOnly
  // and an un-paged fetch with no exceededTransferLimit flag). Canada comes
  // from the ECCC OGC API. Both are normalized to a common NWS-style property
  // shape — which also sanitizes geometry so a degenerate NOAA vertex can't
  // crash MapLibre's tiler and erase a swath of the map. Polling: 2 min.
  // ─── US (NWS) fetch — gated on alertsEnabled ────────────────────────────────
  useEffect(() => {
    if (!alertsEnabled) { setAlertsData(null); return; }
    const fetchUsAlerts = () => {
      const t0 = performance.now();
      fetch(NWS_ARCGIS_ALERTS_API)
        .then((res) => {
          if (!res.ok) throw new Error(`NWS ArcGIS alerts ${res.status}`);
          return res.json();
        })
        .then((json) => {
          // ArcGIS can return {error:{...}} with HTTP 200 on a bad query.
          if (json?.error) throw new Error(`NWS ArcGIS error ${json.error.code ?? ""}`.trim());
          return Array.isArray(json?.features) ? json.features : [];
        })
        .catch((err) => { console.error("NWS ArcGIS alerts fetch failed:", err); return [] as any[]; })
        .then((nwsRaw: any[]) => {
          const tNet = performance.now();
          const normalized = (nwsRaw ?? []).map(normalizeArcgisAlert);
          const drawable = normalized.filter((f: any) => f?.geometry);
          const tDone = performance.now();
          // Split timing: network = request→JSON-parsed (server TTFB + download
          // + JSON.parse); client = normalize/sanitize. If network dominates the
          // Esri host/transfer is the cost (consider a caching proxy or coarser
          // generalization); if client dominates, it's the normalize/tiling work.
          console.info(
            `US alerts: ${nwsRaw?.length ?? 0} fetched → ${drawable.length} drawable ` +
            `[network ${(tNet - t0).toFixed(0)}ms, client ${(tDone - tNet).toFixed(0)}ms]`
          );
          setAlertsData({ type: "FeatureCollection", features: drawable });
        });
    };
    fetchUsAlerts();
    const i = setInterval(fetchUsAlerts, 120000);
    return () => clearInterval(i);
  }, [alertsEnabled, wakeEpoch]);

  // ─── Canada fetch — now gated on the SHARED alertsEnabled toggle ───────────
  // US and Canada render into one merged layer controlled by a single toggle, so
  // this fetch keys off alertsEnabled (not a separate canadaAlertsEnabled). Still
  // a separate fetch/effect because the endpoints and paging differ; the results
  // are merged downstream in the alerts render effect. Sourced from the Esri/MSC
  // ArcGIS feature service.
  useEffect(() => {
    if (!alertsEnabled) { setCanadaAlertsData(null); return; }
    const fetchCanadaAlerts = () => {
      fetch(ECCC_ALERTS_API)
        .then((res) => {
          if (!res.ok) throw new Error(`Canada alerts ${res.status}`);
          return res.json();
        })
        .then((json: any) => {
          // ArcGIS can return {error:{...}} with HTTP 200 on a bad query.
          if (json?.error) throw new Error(`Canada ArcGIS error ${json.error.code ?? ""}`.trim());
          return json;
        })
        .catch((err) => { console.error("Canada alerts fetch failed:", err); return null; })
        .then((eccc: any) => {
          const raw = eccc?.features ?? [];
          const normalized = raw.map(normalizeEcccFeature);
          const withGeom = normalized.filter((f: any) => f?.geometry);
          const droppedToNull = normalized.length - withGeom.length;
          console.info(
            `Canada alerts: ${raw.length} fetched → ${normalized.length} normalized → ${withGeom.length} drawable` +
            (droppedToNull > 0 ? ` (⚠ ${droppedToNull} dropped — geometry came back null in normalize)` : "")
          );
          setCanadaAlertsData({ type: "FeatureCollection", features: withGeom });
        });
    };
    fetchCanadaAlerts();
    const i = setInterval(fetchCanadaAlerts, 120000);
    return () => clearInterval(i);
  }, [alertsEnabled, wakeEpoch]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    const SRC = "alerts-source", LYR_FILL = "alerts-fill", LYR_LINE = "alerts-outline";

    // Render as soon as EITHER feed has arrived — don't block the whole layer on
    // the US fetch when Canada (or vice versa) is already in hand. The effect
    // re-runs when either data set changes, so the first feed paints immediately
    // and the second merges in on its own arrival. Tear down only when alerts are
    // toggled off, or when neither feed has any data yet.
    const haveData = alertsData != null || canadaAlertsData != null;
    if (!alertsEnabled || !haveData) {
      if (map.getLayer(LYR_FILL)) map.removeLayer(LYR_FILL);
      if (map.getLayer(LYR_LINE)) map.removeLayer(LYR_LINE);
      if (map.getSource(SRC)) map.removeSource(SRC);
      if (activePopupRef.current) activePopupRef.current.remove();
      return;
    }

    // Color by exact event type using NOAA's own hazard palette (keyed on the
    // `event` property, which the normalizers set from prod_type / alert name).
    // The ArcGIS source gives a clean event name per feature, so we get
    // weather.gov-matching shading instead of the old five-bucket ramp.
    // Fallback chain: prod_type color → severity color → Unknown grey. We bake
    // the severity fallback in as a NESTED match so a prod_type we haven't
    // enumerated still lands on a sensible severity-based color rather than the
    // single default.
    const severityMatch: any[] = ["match", ["get", "severity"]];
    Object.entries(ALERT_SEVERITY_COLORS).forEach(([k, v]) => { severityMatch.push(k, v); });
    severityMatch.push(ALERT_SEVERITY_COLORS.Unknown); // default leg

    const matchExpr: any[] = ["match", ["get", "event"]];
    Object.entries(NWS_PRODTYPE_COLORS).forEach(([k, v]) => { matchExpr.push(k, v); });
    matchExpr.push(severityMatch); // default leg → severity-based color

    // Restrict the layer to ONLY the enumerated event types. Without this the
    // layer paints every active alert in the feed (hundreds of product types,
    // including narrative "...Statement"/"...Discussion"/outlook products),
    // falling back to the severity ramp for anything unlisted. We derive the
    // allow-list straight from NWS_PRODTYPE_COLORS keys so the filter and the
    // palette can't drift — editing that table is the single source of truth
    // for which alerts show. Features whose `event` isn't in the set are
    // dropped from both the fill and the outline.
    const allowedAlertEvents = Object.keys(NWS_PRODTYPE_COLORS);
    const alertEventFilter: any[] = ["any",
      ["in", ["get", "event"], ["literal", allowedAlertEvents]],
      ["==", ["get", "_source"], "eccc"],
    ];

    // Geometry is already attached by both sources (US NWS ArcGIS + Canada
    // Esri/MSC ArcGIS), so we render directly — no async zone backfill. US and
    // Canada are fetched separately (different endpoints/paging) but MERGED here
    // into one FeatureCollection feeding a single source/layer, so they share
    // paint, popup, and one toggle. Each normalizer already produced the same
    // NWS-style property shape, so the color match expression works for both.
    const usFeatures = alertsData?.features ?? [];
    const caFeatures = canadaAlertsData?.features ?? [];
    const data = { type: "FeatureCollection", features: [...usFeatures, ...caFeatures] };
    const total = data.features.length;
    const withGeom = data.features.filter((f: any) => f?.geometry).length;
    console.info(`Alerts render: ${total} total (${usFeatures.length} US + ${caFeatures.length} CA), ${withGeom} with geometry`);

    if (map.getSource(SRC)) {
      (map.getSource(SRC) as any).setData(data);
    } else {
      // Tiling options: tolerance:0 disables geojson-vt's low-zoom geometry
      // simplification (which silently drops detailed polygons when the whole
      // continent is packed into a few coarse tiles). We DELIBERATELY leave
      // buffer and maxzoom at their defaults (128 / 18): the earlier buffer:0 +
      // maxzoom:5 combination clipped large polygons at tile seams and overzoomed
      // coarse tiles, which is what made big Canadian zones vanish until you
      // zoomed into a single tile. Defaults proved correct on the Canada-only
      // layer, so the merged source uses them too.
      map.addSource(SRC, { type: "geojson", data, tolerance: 0 });

      // Layer separation: the filled boxes go BELOW the radar raster and the
      // box outlines go ABOVE it. Anchor the fill before whichever radar
      // sub-layer currently exists (MRMS/EUMETNET); if radar isn't mounted yet
      // (the radar layer-add effect and this one run independently and in
      // either order), fall back to the normal weather slot so the fill still
      // sits beneath labels/roads. The outline is added with no beforeId, so it
      // lands on top of the stack — above radar.
      const radarBeforeId =
        [`${WEATHER_LAYER_ID}-radar_mrms`, `${WEATHER_LAYER_ID}-radar_eumetnet`]
          .find((id) => map.getLayer(id)) ?? pickWeatherBeforeId(map);

      map.addLayer({ id: LYR_FILL, type: "fill", source: SRC, filter: alertEventFilter as any,
        paint: { "fill-color": matchExpr as any, "fill-opacity": 0.22 } }, radarBeforeId);
      map.addLayer({ id: LYR_LINE, type: "line", source: SRC, filter: alertEventFilter as any,
        paint: { "line-color": matchExpr as any, "line-width": 1.5 } });

      // Reuse the exact popup builder the warnings layer uses, so an alert
      // shows the same rich card (severity/urgency badges, issued→expires
      // timeline, and — for this source — a "View full alert" link to the CAP
      // product, since the ArcGIS feed carries no inline narrative text).
      map.on("click", LYR_FILL, (e: any) => {
        if (!e.features?.length) return;
        openNwsAlertPopup(map, e);
      });
      map.on("mouseenter", LYR_FILL, () => { map.getCanvas().style.cursor = "pointer"; });
      map.on("mouseleave", LYR_FILL, () => { map.getCanvas().style.cursor = ""; });
    }
    // ─── Verify tiling isn't dropping features ───────────────────────────
    // With tolerance:0 and default buffer/maxzoom, the rendered count should
    // track the in-source count even when zoomed out. A large shortfall means
    // tiling is still dropping geometry. (Rendered counts only what's in the
    // current viewport, so compare at a fixed zoom.)
    setTimeout(() => {
      try {
        const parsed = (map.querySourceFeatures(SRC) as any[]).length;
        const drawn = (map.queryRenderedFeatures({ layers: [LYR_FILL] }) as any[]).length;
        console.info(`Alerts tiling check: ${parsed} parsed, ${drawn} rendered (zoom ${map.getZoom().toFixed(1)})`);
      } catch { /* layer not ready yet */ }
    }, 1500);
    // styleEpoch: setStyle() removes these layers; re-run to re-add them.
    // canadaAlertsData: merged into the same source, so re-render on its arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertsData, canadaAlertsData, alertsEnabled, mapLoaded, styleEpoch]);

  // ═════════════════════════════════════════════════════════════════════════
  // Tropical Systems (unified Esri Living Atlas — NHC + JTWC, all basins)
  // ═════════════════════════════════════════════════════════════════════════
  // Fetch. The Esri service updates ~every 15 minutes; 10-minute poll.
  // Direct fetch to services9.arcgis.com — no worker proxy needed.
  useEffect(() => {
    if (!tropicalEnabled) { setTropicalData(null); return; }
    let cancelled = false;
    const run = () => {
      fetchTropicalData()
        .then((d) => { if (!cancelled) setTropicalData(d); })
        .catch((err) => console.error("Tropical fetch failed:", err));
    };
    run();
    const i = setInterval(run, 600000);
    return () => { cancelled = true; clearInterval(i); };
  }, [tropicalEnabled, wakeEpoch]);

  // Render. Cone fill below radar, everything else on top.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    if (!tropicalEnabled || !tropicalData) {
      removeTropicalOverlay(map);
      if (activePopupRef.current) activePopupRef.current.remove();
      return;
    }

    const radarBeforeId =
      [`${WEATHER_LAYER_ID}-radar_mrms`, `${WEATHER_LAYER_ID}-radar_eumetnet`]
        .find((id) => map.getLayer(id)) ?? pickWeatherBeforeId(map);
    upsertTropicalOverlay(map, tropicalData, radarBeforeId);

    if (!tropicalHandlersBoundRef.current) {
      tropicalHandlersBoundRef.current = true;
      const bindPoint = (layerId: string, kind: "forecast" | "observed") => {
        map.on("click", layerId, (e: any) => {
          if (!e.features?.length) return;
          openOverlayPopup(map, e, buildTropicalPopupHTML(e.features[0].properties, STORM_POPUP_TOK, kind));
        });
        map.on("mouseenter", layerId, () => { map.getCanvas().style.cursor = "pointer"; });
        map.on("mouseleave", layerId, () => { map.getCanvas().style.cursor = ""; });
      };
      bindPoint(TROPICAL_LYR.forecastIcons, "forecast");
      bindPoint(TROPICAL_LYR.observedPoints, "observed");
    }
    // styleEpoch: setStyle() removes these layers; re-run to re-add them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tropicalData, tropicalEnabled, mapLoaded, styleEpoch]);

  // ═════════════════════════════════════════════════════════════════════════
  // SPC Day 1 convective outlooks (categorical / tornado / wind / hail)
  // ═════════════════════════════════════════════════════════════════════════
  // Fetch — keyed on the selected product. Clearing spcData up front stops the
  // previous product's polygons from lingering (with the wrong palette) while
  // the new product loads. Day 1 issues at 01/06/13/16:30/20Z; 10-min poll.
  useEffect(() => {
    if (!spcOutlook) { setSpcData(null); return; }
    let cancelled = false;
    setSpcData(null);
    const run = () => {
      fetchSpcOutlook(spcOutlook)
        .then((fc) => { if (!cancelled) setSpcData(fc); })
        .catch((err) => console.error("SPC outlook fetch failed:", err));
    };
    run();
    const i = setInterval(run, 600000);
    return () => { cancelled = true; clearInterval(i); };
  }, [spcOutlook, wakeEpoch]);

  // Render. One source + fill/outline pair reused across all three products —
  // a product switch updates the data and paint expressions in place. The SPC
  // layers are intentionally non-interactive: no click popup, no pointer
  // cursor — the severity legend in the timeline pill explains the colors.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    if (!spcOutlook || !spcData) {
      removeSpcOverlay(map);
      return;
    }

    const radarBeforeId =
      [`${WEATHER_LAYER_ID}-radar_mrms`, `${WEATHER_LAYER_ID}-radar_eumetnet`]
        .find((id) => map.getLayer(id)) ?? pickWeatherBeforeId(map);
    upsertSpcOverlay(map, spcOutlook, spcData, radarBeforeId);
    // styleEpoch: setStyle() removes these layers; re-run to re-add them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spcData, spcOutlook, mapLoaded, styleEpoch]);

  // ═════════════════════════════════════════════════════════════════════════
  // Render
  // ═════════════════════════════════════════════════════════════════════════
  if (error) {
    return (
      <div style={{ width: "100%", height: "100vh", background: "#0a0c10",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "#ef4444", fontFamily: "Roboto, sans-serif", fontSize: 14, flexDirection: "column", gap: 12 }}>
        <div>Map failed to load</div>
        <div style={{ color: "#6b7280", fontSize: 12, maxWidth: 400, textAlign: "center" }}>{error}</div>
      </div>
    );
  }

  return (
    <div style={{ width: "100%", height: "100vh", background: "#0a0c10", position: "relative", overflow: "hidden",
      fontFamily: "Roboto, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      <div ref={mapContainer} style={{ width: "100%", height: "100%" }} />

      {!mapLoaded && (
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
          background: "#0a0c10", display: "flex", alignItems: "center",
          justifyContent: "center", zIndex: 100,
        }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
              <BrandCube size={64} />
            </div>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
              <BrandWordmark size={22} />
            </div>
            <div style={{ fontFamily: "Roboto, sans-serif", fontSize: 11, color: "#4a5063",
              letterSpacing: 2, textTransform: "uppercase" }}>Loading map...</div>
          </div>
        </div>
      )}

      <StormChanceUI
        activeLayer={activeLayer}
        setActiveLayer={selectActiveLayer}
        hidePrecip={hidePrecip}
        setHidePrecip={setHidePrecip}
        mapFlavor={mapFlavor}
        setMapFlavor={setMapFlavor}
        labelSize={labelSize}
        setLabelSize={setLabelSize}
        featureToggles={featureToggles}
        setFeatureToggle={setFeatureToggle}
        opacity={opacity}
        setOpacity={setOpacity}
        lightningEnabled={lightningEnabled}
        setLightningEnabled={setLightningEnabled}
        warningsEnabled={warningsEnabled}
        setWarningsEnabled={setWarningsEnabled}
        alertsEnabled={alertsEnabled}
        setAlertsEnabled={setAlertsEnabled}
        tropicalEnabled={tropicalEnabled}
        setTropicalEnabled={selectTropical}
        spcOutlook={spcOutlook}
        setSpcOutlook={selectSpcOutlook}
        keyframes={(() => {
          if (!activeLayer || scrubberLength === 0) return [];
          const shortestKey = subLayerKeys.reduce((best, k) => {
            const len = keyframes[k]?.length ?? 0;
            const bestLen = keyframes[best]?.length ?? Infinity;
            return len > 0 && len < bestLen ? k : best;
          }, subLayerKeys[0]);
          return keyframes[shortestKey] ?? [];
        })()}
        scrubberLength={scrubberLength}
        currentIdx={currentIdx}
        onScrub={(v: number) => { setPlaying(false); setCurrentIdx(v); }}
        playing={playing}
        setPlaying={setPlaying}
        loadedCount={loadedAtWindow}
        failedCount={failedCount}
        total={totalExpectedFrames}
        totalExpectedFrames={totalExpectedFrames}
        length={scrubberLength}
        oldestLoaded={oldestLoaded}
        mapRef={mapRef}
        loading={loading}
      />

      {/* Region-keyed data-source credit. Anchored 10px above the timeline pill
          via DOM measurement (data-timeline-pill), so it self-corrects as that
          pill changes height (preload bar, legend, mobile reflow). The `bottom`
          prop is a fallback only, used until the anchor is found. Shows the
          attribution for whichever alert source is drawn in view — NOAA for the
          US, Environment and Climate Change Canada for Canada (text, never the
          ECCC logo, per the MSC Open Data licence), both at the border, nothing
          over open ocean. Hidden entirely when no source is in view. */}
      <AttributionPill
        sources={mergedSources}
        anchorSelector="[data-timeline-pill]"
        gap={10}
        bottom={isSmall ? 96 : 102}
        isSmall={isSmall}
      />

      {/* TEMPORARY (streamline tuner): a pill that opens a floating slider panel
          for live-tuning the wind streamlines. Only shown on the wind layer.
          DISABLED via ENABLE_STREAMLINE_TUNER=false above — the tuned values are
          now the map defaults, so these never render. Flip the flag back to true
          to bring the pill + panel back for another round of tuning. Delete this
          whole block (and the StreamlineTuner component + the showStreamlineTuner
          state) to remove the tuner entirely. */}
      {ENABLE_STREAMLINE_TUNER && activeLayer === "wind" && showStreamlines && !showStreamlineTuner && (
        <button onClick={() => setShowStreamlineTuner(true)} style={{
          position: "absolute", top: 16, left: 16, zIndex: 60,
          background: "rgba(8,10,16,0.92)", backdropFilter: "blur(12px)",
          border: "1px solid rgba(78,205,196,0.3)", borderRadius: 999,
          padding: "7px 14px", cursor: "pointer",
          color: "#4ecdc4", fontSize: 11, fontWeight: 600,
          fontFamily: "Roboto, -apple-system, system-ui, sans-serif",
          letterSpacing: 0.5, boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <span style={{ fontSize: 13, lineHeight: 1 }}>≈</span> Tune streamlines
        </button>
      )}
      {ENABLE_STREAMLINE_TUNER && activeLayer === "wind" && showStreamlines && showStreamlineTuner && (
        <StreamlineTuner
          fadeOpacity={slFadeOpacity} setFadeOpacity={setSlFadeOpacity}
          speedFactor={slSpeedFactor} setSpeedFactor={setSlSpeedFactor}
          dropRate={slDropRate} setDropRate={setSlDropRate}
          dropRateBump={slDropRateBump} setDropRateBump={setSlDropRateBump}
          pointSize={slPointSize} setPointSize={setSlPointSize}
          particleStateSize={slParticleStateSize} setParticleStateSize={setSlParticleStateSize}
          onClose={() => setShowStreamlineTuner(false)}
        />
      )}

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 0.3; transform: scale(1); } 50% { opacity: 1; transform: scale(1.5); } }
        @keyframes locPulse { 0%, 100% { transform: scale(1); opacity: 0.25; } 50% { transform: scale(1.8); opacity: 0; } }
        .maplibregl-ctrl-attrib { font-size: 10px !important; }
        .maplibregl-ctrl-attrib a { color: #4a5063 !important; }
        .maplibregl-ctrl-top-left { display: none; }
        /* The ⓘ attribution button must stay reachable at every viewport
           size. z-index 45 puts it above the timeline/legend pill (40) and
           below the interactive control pills (60), so the pill can never
           paint over it; the effect above slides it up when the two boxes
           actually intersect, and this transition makes that move read as
           deliberate rather than a jump. */
        .maplibregl-ctrl-bottom-right { z-index: 45; }
        /* The transition lives on the control, because the control is what the
           effect above transforms — the corner container stays put so it can
           be used as a stable measuring origin. */
        .maplibregl-ctrl-bottom-right .maplibregl-ctrl {
          transition: transform 0.18s ease;
          will-change: transform;
        }
        .maplibregl-ctrl-bottom-right .maplibregl-ctrl { pointer-events: auto; }
      `}</style>
    </div>
  );
}